import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Dimensions,
  Alert,
  Modal,
  TextInput,
  FlatList,
  Animated,
  Switch,
  StatusBar,
  Platform,
  Easing,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import DateTimePicker from '@react-native-community/datetimepicker';
import { addDays, differenceInDays, isAfter, isBefore, format, parseISO, isSameDay, isSameMonth, addMonths, subMonths } from 'date-fns';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import * as Print from 'expo-print';
import * as DocumentPicker from 'expo-document-picker';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  projects: `${API_BASE_URL}/api/projects`,
  projects: `${API_BASE_URL}/api/projects`,
  companyProfile: `${API_BASE_URL}/api/company/profile`,
};

const ProjectPlanningScreen = ({ navigation, route }) => {
  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scaleAnim = useRef(new Animated.Value(0.95)).current;
  const rotateAnim = useRef(new Animated.Value(0)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;
  const headerOpacity = useRef(new Animated.Value(1)).current;
  const scrollY = useRef(new Animated.Value(0)).current;

  // State management
  const [activeView, setActiveView] = useState('gantt');
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showEditActivityModal, setShowEditActivityModal] = useState(false);
  const [showAddResourceModal, setShowAddResourceModal] = useState(false);
  const [showEditResourceModal, setShowEditResourceModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerMode, setDatePickerMode] = useState('start');
  const [showEditDatePicker, setShowEditDatePicker] = useState(false);
  const [editDatePickerMode, setEditDatePickerMode] = useState('start');
  const [editingActivity, setEditingActivity] = useState(null);
  const [expandedWBS, setExpandedWBS] = useState({});
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('wbs');
  const [timelineScale, setTimelineScale] = useState('months');
  const [showCriticalPath, setShowCriticalPath] = useState(true);
  const [autoSchedule, setAutoSchedule] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  // ── RISKS ──
  const EMPTY_RISK = { title:'', description:'', category:'Technical', likelihood:3, impact:3, status:'Open', mitigation:'', owner:'', dueDate:addDays(new Date(),30) };
  const [risks, setRisks] = useState([
    {id:1,title:'Scope Creep',description:'Client may add out-of-scope features',category:'Scope',likelihood:4,impact:3,status:'Open',mitigation:'Strict change control process',owner:'Sarah Johnson',dueDate:addDays(new Date(),60),createdAt:new Date().toISOString()},
    {id:2,title:'Key Resource Unavailability',description:'Lead developer may leave',category:'Resource',likelihood:2,impact:5,status:'Mitigated',mitigation:'Document code & cross-train team',owner:'Tom Lee',dueDate:addDays(new Date(),90),createdAt:new Date().toISOString()},
    {id:3,title:'Third-party API Delay',description:'Payment gateway integration may be delayed',category:'Technical',likelihood:3,impact:3,status:'Open',mitigation:'Identify fallback provider',owner:'Dev Team',dueDate:addDays(new Date(),30),createdAt:new Date().toISOString()},
  ]);
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [riskForm, setRiskForm] = useState({...EMPTY_RISK});
  const [riskFilter, setRiskFilter] = useState('All');
  const [showRiskDatePicker, setShowRiskDatePicker] = useState(false);

  // ── ISSUES ──
  const EMPTY_ISSUE = { title:'', description:'', priority:'Medium', status:'Open', assignee:'', dueDate:addDays(new Date(),7), category:'General', linkedActivityId:null };
  const [issues, setIssues] = useState([
    {id:1,title:'Login crashes on iOS 16',description:'App crashes on invalid credentials',priority:'Critical',status:'In Progress',assignee:'Dev Team',dueDate:addDays(new Date(),3),category:'Bug',linkedActivityId:3,createdAt:new Date().toISOString(),resolvedAt:null},
    {id:2,title:'API timeout under load',description:'Endpoints timeout at 50 concurrent users',priority:'High',status:'Open',assignee:'Backend Team',dueDate:addDays(new Date(),7),category:'Performance',linkedActivityId:3,createdAt:new Date().toISOString(),resolvedAt:null},
    {id:3,title:'Missing export button',description:'Export to Excel not implemented',priority:'Medium',status:'Open',assignee:'Sarah Johnson',dueDate:addDays(new Date(),14),category:'Feature',linkedActivityId:4,createdAt:new Date().toISOString(),resolvedAt:null},
  ]);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [issueForm, setIssueForm] = useState({...EMPTY_ISSUE});
  const [issueFilter, setIssueFilter] = useState('All');
  const [showIssueDatePicker, setShowIssueDatePicker] = useState(false);

  // ── MILESTONES ──
  const EMPTY_MS = { name:'', date:addDays(new Date(),30), status:'Pending', description:'', linkedActivityId:null };
  const [projectMilestones, setProjectMilestones] = useState([
    {id:1,name:'Requirements Sign-off',date:addDays(new Date(),10),status:'Pending',description:'All stakeholders approve requirements',linkedActivityId:1},
    {id:2,name:'Design Approved',date:addDays(new Date(),25),status:'Pending',description:'UI/UX mockups approved',linkedActivityId:2},
    {id:3,name:'Beta Release',date:addDays(new Date(),65),status:'Pending',description:'Internal beta ready',linkedActivityId:3},
    {id:4,name:'Go Live',date:addDays(new Date(),95),status:'Pending',description:'Production deployment',linkedActivityId:4},
  ]);
  const [showMsModal, setShowMsModal] = useState(false);
  const [editingMs, setEditingMs] = useState(null);
  const [msForm, setMsForm] = useState({...EMPTY_MS});
  const [showMsDatePicker, setShowMsDatePicker] = useState(false);

  // ── TIME ENTRIES ──
  const EMPTY_TIME = { activityId:null, resourceId:null, hours:'', date:new Date(), description:'', billable:true };
  const [timeEntries, setTimeEntries] = useState([]);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [editingTime, setEditingTime] = useState(null);
  const [timeForm, setTimeForm] = useState({...EMPTY_TIME});
  const [showTimeDatePicker, setShowTimeDatePicker] = useState(false);

  // ── NOTES ──
  const [activityNotes, setActivityNotes] = useState({});
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteActId, setNoteActId] = useState(null);

  // ── BASELINE ──
  const [baseline, setBaseline] = useState(null);
  const [showBaselineModal, setShowBaselineModal] = useState(false);
  const [baselineName, setBaselineName] = useState('');

  // ── BULK SELECT ──
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // ── UNDO / REDO ──
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // ── NOTIFICATIONS ──
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  // ── ANALYTICS / REPORTS sub-tabs ──
  const [analyticsTab, setAnalyticsTab] = useState('overview');
  const [reportType, setReportType] = useState('summary');
  const [reportGenerating, setReportGenerating] = useState(false);

  // ── CALENDAR ──
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedCalDate, setSelectedCalDate] = useState(null);

  // ── SETTINGS ──
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [appSettings, setAppSettings] = useState({
    autoSave: true,
    notifications: true,
  });

  // State for dependency management
  const [showDependencyModal, setShowDependencyModal] = useState(false);
  const [dependencyBeingEdited, setDependencyBeingEdited] = useState(null);
  const [newDependency, setNewDependency] = useState({
    id: null,
    type: 'FS',
    lag: 0,
  });

  // Form states
  const [newActivity, setNewActivity] = useState({
    name: '',
    duration: '',
    startDate: new Date(),
    endDate: new Date(),
    progress: 0,
    status: 'Not Started',
    isCritical: false,
    resources: [],
    cost: '',
    description: '',
    priority: 'Medium',
    predecessors: [],
  });

  const [editActivity, setEditActivity] = useState({
    name: '',
    description: '',
    duration: '',
    startDate: new Date(),
    endDate: new Date(),
    progress: 0,
    status: 'Not Started',
    isCritical: false,
    resources: [],
    cost: '',
    priority: 'Medium',
    predecessors: [],
  });

  const [newResource, setNewResource] = useState({
    name: '',
    role: '',
    rate: '',
    utilization: 0,
    skills: '',
  });

  // Separate date picker state for new/edit project modals
  const [showProjectDatePicker, setShowProjectDatePicker] = useState(false);
  const [projectDatePickerMode, setProjectDatePickerMode] = useState('start');

  // Project details for new/edit modals
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    startDate: new Date(),
    endDate: addDays(new Date(), 180),
    status: 'Planning',
    priority: 'Medium',
    budget: '',
    manager: '',
    client: '',
  });

  // Project data with enhanced structure
  const [projectData, setProjectData] = useState({
    id: 1,
    name: 'Enterprise Mobile App Development',
    description: 'Complete enterprise mobile application development project with advanced features',
    startDate: new Date(),
    endDate: addDays(new Date(), 180),
    status: 'In Progress',
    priority: 'High',
    budget: 750000,
    currency: 'GBP',
    manager: 'Sarah Johnson',
    client: 'TechCorp Industries Ltd',
    department: 'Digital Innovation',
    category: 'Software Development',
    riskLevel: 'Medium',
    activities: [
      {
        id: 1,
        wbs: '1',
        name: 'Requirements Analysis',
        description: 'Gather and document requirements',
        duration: 10,
        startDate: new Date(),
        endDate: addDays(new Date(), 10),
        progress: 50,
        status: 'In Progress',
        isCritical: false,
        priority: 'High',
        resources: [],
        cost: 10000,
        predecessors: [],
        tags: ['requirements'],
      },
      {
        id: 2,
        wbs: '2',
        name: 'Design',
        description: 'Create system architecture and UI/UX design',
        duration: 15,
        startDate: addDays(new Date(), 10),
        endDate: addDays(new Date(), 25),
        progress: 0,
        status: 'Not Started',
        isCritical: false,
        priority: 'High',
        resources: [],
        cost: 20000,
        predecessors: [{ id: 1, type: 'FS', lag: 0 }],
        tags: ['design'],
      },
      {
        id: 3,
        wbs: '3',
        name: 'Development',
        description: 'Implement features',
        duration: 30,
        startDate: addDays(new Date(), 25),
        endDate: addDays(new Date(), 55),
        progress: 0,
        status: 'Not Started',
        isCritical: false,
        priority: 'High',
        resources: [],
        cost: 50000,
        predecessors: [{ id: 2, type: 'FS', lag: 0 }],
        tags: ['dev'],
      },
      {
        id: 4,
        wbs: '4',
        name: 'Testing',
        description: 'QA and testing',
        duration: 10,
        startDate: addDays(new Date(), 55),
        endDate: addDays(new Date(), 65),
        progress: 0,
        status: 'Not Started',
        isCritical: false,
        priority: 'Medium',
        resources: [],
        cost: 15000,
        predecessors: [{ id: 3, type: 'FS', lag: 0 }],
        tags: ['qa'],
      },
    ],
    resources: [],
    milestones: [],
    risks: [],
    settings: {
      workingDays: [1, 2, 3, 4, 5],
      workingHours: { start: 9, end: 17 },
      currency: 'GBP',
      dateFormat: 'DD/MM/YYYY',
      autoSave: true,
      notifications: true,
    },
  });

  // ====================== DEFENSIVE DATA CHECKS ======================
  const flattenActivities = useCallback((activities, level = 0) => {
    let flattened = [];
    if (!activities || !Array.isArray(activities)) return flattened;
    activities.forEach(activity => {
      if (!activity || typeof activity !== 'object') return;
      flattened.push({ ...activity, level });
      if (activity.children && Array.isArray(activity.children)) {
        flattened = flattened.concat(flattenActivities(activity.children, level + 1));
      }
    });
    return flattened;
  }, []);

  const flatActivities = useMemo(() => flattenActivities(projectData.activities), [projectData.activities, flattenActivities]);

  const filteredActivities = useMemo(() => {
    let filtered = flatActivities.filter(a => a && typeof a === 'object');
    if (filterStatus !== 'All') {
      filtered = filtered.filter(activity => activity.status === filterStatus);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(activity =>
        String(activity.name ?? '').toLowerCase().includes(query) ||
        String(activity.wbs ?? '').toLowerCase().includes(query) ||
        (activity.description && String(activity.description ?? '').toLowerCase().includes(query)) ||
        (activity.tags && activity.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name': return a.name.localeCompare(b.name);
        case 'startDate': return new Date(a.startDate) - new Date(b.startDate);
        case 'endDate': return new Date(a.endDate) - new Date(b.endDate);
        case 'progress': return b.progress - a.progress;
        case 'cost': return b.cost - a.cost;
        case 'priority': {
          const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        default: return a.wbs.localeCompare(b.wbs);
      }
    });
    return filtered;
  }, [flatActivities, filterStatus, searchQuery, sortBy]);

  // Utility functions
  const formatDate = useCallback((date) => {
    if (!date) return 'N/A';
    try {
      const d = date instanceof Date ? date : parseISO(date);
      if (isNaN(d.getTime())) return 'Invalid Date';
      return format(d, 'dd/MM/yyyy');
    } catch {
      return 'Invalid Date';
    }
  }, []);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: projectData.currency || 'GBP',
    }).format(amount || 0);
  }, [projectData.currency]);

  const addWorkingDays = useCallback((startDate, days) => {
    let result = new Date(startDate);
    let added = 0;
    while (added < days) {
      result = addDays(result, 1);
      const dayOfWeek = result.getDay();
      if (projectData.settings.workingDays.includes(dayOfWeek)) {
        added++;
      }
    }
    return result;
  }, [projectData.settings.workingDays]);

  // Color helpers
  const getStatusColor = useCallback((status) => {
    const colors = {
      'Completed': '#10B981',
      'In Progress': '#3B82F6',
      'Not Started': '#6B7280',
      'On Hold': '#F59E0B',
      'Cancelled': '#EF4444',
      'Delayed': '#DC2626',
    };
    return colors[status] || '#6B7280';
  }, []);

  const getPriorityColor = useCallback((priority) => {
    const colors = { 'High': '#EF4444', 'Medium': '#F59E0B', 'Low': '#10B981' };
    return colors[priority] || '#6B7280';
  }, []);

  const getProgressColor = useCallback((progress) => {
    if (progress >= 100) return '#10B981';
    if (progress >= 75) return '#3B82F6';
    if (progress >= 50) return '#F59E0B';
    if (progress >= 25) return '#F97316';
    return '#EF4444';
  }, []);

  // ── UNDO/REDO ──
  const pushUndo = useCallback((snap) => { setUndoStack(p => [...p.slice(-19), snap]); setRedoStack([]); }, []);
  const handleUndo = useCallback(() => { if (!undoStack.length) return; const prev = undoStack[undoStack.length-1]; setRedoStack(r => [...r, JSON.parse(JSON.stringify(projectData))]); setProjectData(JSON.parse(JSON.stringify(prev))); setUndoStack(s => s.slice(0,-1)); }, [undoStack, projectData]);
  const handleRedo = useCallback(() => { if (!redoStack.length) return; const next = redoStack[redoStack.length-1]; setUndoStack(s => [...s, JSON.parse(JSON.stringify(projectData))]); setProjectData(JSON.parse(JSON.stringify(next))); setRedoStack(r => r.slice(0,-1)); }, [redoStack, projectData]);

  // ── EVM ──
  const calcEVM = useCallback(() => {
    const today = new Date(); let PV=0,EV=0,AC=0,BAC=0;
    const flatActs = (acts) => { let r=[]; if(!acts)return r; acts.forEach(a=>{r.push(a);if(a.children)r=r.concat(flatActs(a.children));}); return r; };
    flatActs(projectData.activities).filter(a=>a&&typeof a==='object').forEach(a => {
      const b = a.cost||0; BAC+=b;
      const s=new Date(a.startDate), e=new Date(a.endDate);
      if(isBefore(today,s)){} else if(isAfter(today,e)){PV+=b;} else{const tot=differenceInDays(e,s)||1;PV+=b*(differenceInDays(today,s)/tot);}
      EV+=b*(a.progress/100);
      AC+=a.actualCost||(b*(a.progress/100)*1.05);
    });
    const SPI=PV>0?EV/PV:1, CPI=AC>0?EV/AC:1, EAC=CPI>0?BAC/CPI:BAC;
    return {PV,EV,AC,BAC,SPI,CPI,EAC,ETC:EAC-AC,CV:EV-AC,SV:EV-PV,VAC:BAC-EAC,TCPI:(BAC-EV)!==0?(BAC-AC)/(BAC-EV):1,pct:BAC>0?(EV/BAC)*100:0};
  }, [projectData.activities]);

  // ── PROJECT HEALTH ──
  const calcHealth = useCallback(() => {
    const evm=calcEVM(), today=new Date();
    const fa=(acts)=>{let r=[];if(!acts)return r;acts.forEach(a=>{r.push(a);if(a.children)r=r.concat(fa(a.children));});return r;};
    const allActs=fa(projectData.activities);
    const overdue=allActs.filter(a=>a.status!=='Completed'&&isBefore(new Date(a.endDate),today)).length;
    const critI=issues.filter(i=>i.status==='Open'&&i.priority==='Critical').length;
    const highR=risks.filter(r=>r.status==='Open'&&r.likelihood*r.impact>=12).length;
    let score=100;
    if(evm.SPI<0.80)score-=25;else if(evm.SPI<0.90)score-=15;else if(evm.SPI<0.95)score-=8;
    if(evm.CPI<0.80)score-=25;else if(evm.CPI<0.90)score-=15;else if(evm.CPI<0.95)score-=8;
    score-=overdue*5; score-=critI*10; score-=highR*8;
    const h=Math.max(0,Math.min(100,Math.round(score)));
    return{score:h,label:h>=80?'Healthy':h>=60?'At Risk':h>=40?'Concerning':'Critical',color:h>=80?'#10B981':h>=60?'#F59E0B':h>=40?'#F97316':'#EF4444'};
  }, [calcEVM, projectData.activities, issues, risks]);

  // ── RISK CRUD ──
  const saveRisk = useCallback(() => {
    if(!riskForm.title.trim()){Alert.alert('Error','Title required');return;}
    if(editingRisk){setRisks(p=>p.map(r=>r.id===editingRisk.id?{...r,...riskForm}:r));}
    else{const mxId=risks.length?Math.max(...risks.map(r=>r.id)):0;setRisks(p=>[...p,{...riskForm,id:mxId+1,createdAt:new Date().toISOString()}]);}
    setShowRiskModal(false);setEditingRisk(null);setRiskForm({...EMPTY_RISK});
  }, [riskForm,editingRisk,risks]);
  const deleteRisk = useCallback((id)=>{Alert.alert('Delete Risk','Sure?',[{text:'Cancel',style:'cancel'},{text:'Delete',style:'destructive',onPress:()=>setRisks(p=>p.filter(r=>r.id!==id))}]);},[]);

  // ── ISSUE CRUD ──
  const saveIssue = useCallback(() => {
    if(!issueForm.title.trim()){Alert.alert('Error','Title required');return;}
    if(editingIssue){setIssues(p=>p.map(i=>i.id===editingIssue.id?{...i,...issueForm}:i));}
    else{const mxId=issues.length?Math.max(...issues.map(i=>i.id)):0;setIssues(p=>[...p,{...issueForm,id:mxId+1,createdAt:new Date().toISOString(),resolvedAt:null}]);}
    setShowIssueModal(false);setEditingIssue(null);setIssueForm({...EMPTY_ISSUE});
  }, [issueForm,editingIssue,issues]);
  const deleteIssue = useCallback((id)=>{Alert.alert('Delete?','Sure?',[{text:'Cancel',style:'cancel'},{text:'Delete',style:'destructive',onPress:()=>setIssues(p=>p.filter(i=>i.id!==id))}]);},[]);
  const resolveIssue = useCallback((id)=>{setIssues(p=>p.map(i=>i.id===id?{...i,status:'Resolved',resolvedAt:new Date().toISOString()}:i));},[]);

  // ── MILESTONE CRUD ──
  const saveMilestone = useCallback(() => {
    if(!msForm.name.trim()){Alert.alert('Error','Name required');return;}
    if(editingMs){setProjectMilestones(p=>p.map(m=>m.id===editingMs.id?{...m,...msForm}:m));}
    else{const mxId=projectMilestones.length?Math.max(...projectMilestones.map(m=>m.id)):0;setProjectMilestones(p=>[...p,{...msForm,id:mxId+1}]);}
    setShowMsModal(false);setEditingMs(null);setMsForm({...EMPTY_MS});
  }, [msForm,editingMs,projectMilestones]);
  const deleteMilestone = useCallback((id)=>{Alert.alert('Delete?','Sure?',[{text:'Cancel',style:'cancel'},{text:'Delete',style:'destructive',onPress:()=>setProjectMilestones(p=>p.filter(m=>m.id!==id))}]);},[]);

  // ── TIME CRUD ──
  const saveTime = useCallback(() => {
    if(!timeForm.activityId||!timeForm.hours){Alert.alert('Error','Activity & hours required');return;}
    if(editingTime){setTimeEntries(p=>p.map(t=>t.id===editingTime.id?{...t,...timeForm}:t));}
    else{const mxId=timeEntries.length?Math.max(...timeEntries.map(t=>t.id)):0;setTimeEntries(p=>[...p,{...timeForm,id:mxId+1,createdAt:new Date().toISOString()}]);}
    setShowTimeModal(false);setEditingTime(null);setTimeForm({...EMPTY_TIME});
  }, [timeForm,editingTime,timeEntries]);
  const actHours = useCallback((id)=>timeEntries.filter(t=>t.activityId===id).reduce((s,t)=>s+(parseFloat(t.hours)||0),0),[timeEntries]);

  // ── BASELINE ──
  const saveBaseline = useCallback(() => {
    if(!baselineName.trim()){Alert.alert('Error','Name required');return;}
    const fa=(acts)=>{let r=[];if(!acts)return r;acts.forEach(a=>{r.push(a);if(a.children)r=r.concat(fa(a.children));});return r;};
    setBaseline({name:baselineName,date:new Date().toISOString(),activities:fa(projectData.activities)});
    setShowBaselineModal(false);setBaselineName('');Alert.alert('Success',`Baseline "${baselineName}" saved`);
  }, [baselineName, projectData.activities]);

  // ── NOTIFICATIONS ──
  const genNotifications = useCallback(() => {
    const today=new Date(),n=[];
    const fa=(acts)=>{let r=[];if(!acts)return r;acts.forEach(a=>{r.push(a);if(a.children)r=r.concat(fa(a.children));});return r;};
    fa(projectData.activities).forEach(a=>{if(a.status!=='Completed'&&isBefore(new Date(a.endDate),today))n.push({id:`ov-${a.id}`,type:'warning',icon:'clock-alert',msg:`"${a.name}" is overdue`});});
    issues.forEach(i=>{if(i.status==='Open'&&i.priority==='Critical')n.push({id:`is-${i.id}`,type:'error',icon:'bug',msg:`Critical: ${i.title}`});});
    risks.forEach(r=>{if(r.status==='Open'&&r.likelihood*r.impact>=15)n.push({id:`ri-${r.id}`,type:'error',icon:'alert',msg:`High risk: ${r.title}`});});
    projectMilestones.forEach(m=>{const d=differenceInDays(new Date(m.date),today);if(d>=0&&d<=7&&m.status==='Pending')n.push({id:`ms-${m.id}`,type:'info',icon:'flag',msg:`Milestone "${m.name}" in ${d}d`});});
    return n;
  }, [projectData.activities, issues, risks, projectMilestones]);

  // ── HTML REPORT ──
  const genHTMLReport = useCallback(() => {
    const evm=calcEVM(),health=calcHealth();
    const fa=(acts)=>{let r=[];if(!acts)return r;acts.forEach(a=>{r.push(a);if(a.children)r=r.concat(fa(a.children));});return r;};
    const allActs=fa(projectData.activities);
    const fmtD=(d)=>{if(!d)return'N/A';try{const dt=d instanceof Date?d:parseISO(d);return format(dt,'dd/MM/yyyy');}catch{return'N/A';}};
    const fmtC=(v)=>new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format(v||0);
    return `<html><head><style>body{font-family:Arial;margin:20px;color:#111}h1{color:#1E3A8A}h2{color:#3B82F6;margin-top:20px}table{border-collapse:collapse;width:100%;margin-top:8px}th{background:#3B82F6;color:#fff;padding:8px;text-align:left;font-size:12px}td{border:1px solid #ddd;padding:6px;font-size:12px}tr:nth-child(even){background:#f9f9f9}.kpi{display:inline-block;background:#f0f4ff;padding:10px 16px;border-radius:8px;margin:6px;text-align:center}.kv{font-size:20px;font-weight:bold;color:#1E3A8A}.kl{font-size:11px;color:#6B7280}</style></head><body>
    <h1>${projectData.name}</h1><p>${projectData.description}</p>
    <p><b>Period:</b> ${fmtD(projectData.startDate)} – ${fmtD(projectData.endDate)} | <b>Status:</b> ${projectData.status} | <b>Budget:</b> ${fmtC(projectData.budget)} | <b>Manager:</b> ${projectData.manager} | <b>Client:</b> ${projectData.client}</p>
    <div><span class="kpi"><div class="kv">${health.score}/100</div><div class="kl">Health (${health.label})</div></span><span class="kpi"><div class="kv">${Math.round(allActs.reduce((s,a)=>s+a.progress,0)/Math.max(allActs.length,1))}%</div><div class="kl">Progress</div></span><span class="kpi"><div class="kv">${allActs.length}</div><div class="kl">Activities</div></span><span class="kpi"><div class="kv">${evm.CPI.toFixed(2)}</div><div class="kl">CPI</div></span><span class="kpi"><div class="kv">${evm.SPI.toFixed(2)}</div><div class="kl">SPI</div></span><span class="kpi"><div class="kv">${fmtC(evm.EAC)}</div><div class="kl">EAC</div></span></div>
    <h2>Activities</h2><table><thead><tr><th>WBS</th><th>Name</th><th>Dur</th><th>Start</th><th>End</th><th>Progress</th><th>Status</th><th>Priority</th><th>Budget</th></tr></thead><tbody>${allActs.map(a=>`<tr><td>${a.wbs}</td><td>${a.name}</td><td>${a.duration}d</td><td>${fmtD(a.startDate)}</td><td>${fmtD(a.endDate)}</td><td>${a.progress}%</td><td>${a.status}</td><td>${a.priority}</td><td>${fmtC(a.cost)}</td></tr>`).join('')}</tbody></table>
    <h2>Risks</h2><table><thead><tr><th>Title</th><th>Category</th><th>Score</th><th>Status</th><th>Owner</th><th>Mitigation</th></tr></thead><tbody>${risks.map(r=>`<tr><td>${r.title}</td><td>${r.category}</td><td>${r.likelihood*r.impact}</td><td>${r.status}</td><td>${r.owner}</td><td>${r.mitigation}</td></tr>`).join('')}</tbody></table>
    <h2>Issues</h2><table><thead><tr><th>Title</th><th>Priority</th><th>Status</th><th>Assignee</th><th>Due</th></tr></thead><tbody>${issues.map(i=>`<tr><td>${i.title}</td><td>${i.priority}</td><td>${i.status}</td><td>${i.assignee}</td><td>${fmtD(i.dueDate)}</td></tr>`).join('')}</tbody></table>
    <h2>Milestones</h2><table><thead><tr><th>Name</th><th>Date</th><th>Status</th></tr></thead><tbody>${projectMilestones.map(m=>`<tr><td>${m.name}</td><td>${fmtD(m.date)}</td><td>${m.status}</td></tr>`).join('')}</tbody></table>
    <h2>EVM</h2><table><thead><tr><th>Metric</th><th>Value</th><th>Interpretation</th></tr></thead><tbody><tr><td>PV</td><td>${fmtC(evm.PV)}</td><td>Planned value</td></tr><tr><td>EV</td><td>${fmtC(evm.EV)}</td><td>Earned value</td></tr><tr><td>AC</td><td>${fmtC(evm.AC)}</td><td>Actual cost</td></tr><tr><td>BAC</td><td>${fmtC(evm.BAC)}</td><td>Total budget</td></tr><tr><td>EAC</td><td>${fmtC(evm.EAC)}</td><td>Forecast total</td></tr><tr><td>CPI</td><td>${evm.CPI.toFixed(3)}</td><td>${evm.CPI>=1?'Under budget':'Over budget'}</td></tr><tr><td>SPI</td><td>${evm.SPI.toFixed(3)}</td><td>${evm.SPI>=1?'Ahead of schedule':'Behind schedule'}</td></tr></tbody></table>
    </body></html>`;
  }, [calcEVM, calcHealth, projectData, risks, issues, projectMilestones]);

  // Timeline generation
  const generateTimeline = useCallback(() => {
    const timeline = [];
    const startDate = new Date(projectData.startDate);
    const endDate = new Date(projectData.endDate);
    const currentDate = new Date(startDate);
    const scaleConfig = {
      months: { width: 200, increment: 'month' },
      weeks: { width: 100, increment: 'week' },
      days: { width: 40, increment: 'day' },
    };
    const config = scaleConfig[timelineScale];
    if (!config) return [];

    while (isBefore(currentDate, endDate) || currentDate.getTime() === endDate.getTime()) {
      let periodEnd;
      let label;
      if (timelineScale === 'months') {
        periodEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        label = format(currentDate, 'MMM yyyy');
        currentDate.setMonth(currentDate.getMonth() + 1);
      } else if (timelineScale === 'weeks') {
        periodEnd = addDays(currentDate, 6);
        const weekNum = Math.ceil(currentDate.getDate() / 7);
        label = `W${weekNum}`;
        currentDate.setDate(currentDate.getDate() + 7);
      } else {
        periodEnd = new Date(currentDate);
        label = currentDate.getDate().toString();
        currentDate.setDate(currentDate.getDate() + 1);
      }
      timeline.push({
        label,
        startDate: new Date(periodEnd.getTime() - (timelineScale === 'days' ? 24 * 60 * 60 * 1000 : 0)),
        endDate: isAfter(periodEnd, endDate) ? endDate : periodEnd,
        width: config.width,
      });
    }
    return timeline;
  }, [projectData.startDate, projectData.endDate, timelineScale]);

  const timeline = useMemo(generateTimeline, [generateTimeline]);
  const timelineWidth = useMemo(() => timeline.reduce((sum, item) => sum + item.width, 0), [timeline]);

  // Activity position calculation
  const calculateActivityPosition = useCallback((activity) => {
    if (!activity?.startDate || !activity?.endDate) return { left: 0, width: 20 };
    try {
      const projectStart = new Date(projectData.startDate);
      const projectEnd = new Date(projectData.endDate);
      const activityStart = new Date(activity.startDate);
      const activityEnd = new Date(activity.endDate);
      const totalDays = differenceInDays(projectEnd, projectStart);
      if (totalDays <= 0) return { left: 0, width: 20 };
      const startDays = differenceInDays(activityStart, projectStart);
      const durationDays = differenceInDays(activityEnd, activityStart);
      const pixelsPerDay = timelineWidth / totalDays;
      return {
        left: Math.max(startDays * pixelsPerDay, 0),
        width: Math.max(durationDays * pixelsPerDay, 20),
      };
    } catch {
      return { left: 0, width: 20 };
    }
  }, [projectData.startDate, projectData.endDate, timelineWidth]);

  // ====================== UPDATED CRITICAL PATH WITH DEPENDENCY TYPES ======================
  const calculateCriticalPath = useCallback(() => {
    const activities = flatActivities.map(a => ({ ...a })); // clone

    const applyDependency = (activity, pred, predActivity) => {
      const predEnd = predActivity.earlyFinish;
      const predStart = predActivity.earlyStart;
      const lag = pred.lag || 0;
      switch (pred.type) {
        case 'FS': // Finish to Start
          return addDays(predEnd, lag + 1);
        case 'SS': // Start to Start
          return addDays(predStart, lag);
        case 'FF': // Finish to Finish
          return null; // special
        case 'SF': // Start to Finish
          return null; // special
        default:
          return addDays(predEnd, lag + 1);
      }
    };

    // Initialize
    activities.forEach(a => {
      a.earlyStart = new Date(a.startDate);
      a.earlyFinish = new Date(a.endDate);
      a.lateStart = new Date(a.startDate);
      a.lateFinish = new Date(a.endDate);
      a.totalFloat = 0;
    });

    // Forward pass
    activities.forEach(activity => {
      if (activity.predecessors && activity.predecessors.length) {
        let maxEarlyStart = new Date(0);
        activity.predecessors.forEach(pred => {
          const predActivity = activities.find(a => a.id === pred.id);
          if (!predActivity) return;
          const constraintStart = applyDependency(activity, pred, predActivity);
          if (constraintStart && constraintStart > maxEarlyStart) {
            maxEarlyStart = new Date(constraintStart);
          }
        });
        activity.earlyStart = maxEarlyStart > new Date(0) ? maxEarlyStart : activity.earlyStart;
      }
      activity.earlyFinish = addWorkingDays(activity.earlyStart, activity.duration);
    });

    // Backward pass
    const projectEnd = Math.max(...activities.map(a => a.earlyFinish));
    activities.reverse().forEach(activity => {
      if (activity.successors && activity.successors.length) {
        const successors = activities.filter(a => a.predecessors?.some(p => p.id === activity.id));
        let minLateStart = new Date(projectEnd);
        successors.forEach(succ => {
          const succPred = succ.predecessors.find(p => p.id === activity.id);
          if (!succPred) return;
          if (succPred.type === 'FS') {
            const requiredStart = addDays(activity.lateFinish, (succPred.lag || 0) + 1);
            if (requiredStart < minLateStart) minLateStart = new Date(requiredStart);
          } else if (succPred.type === 'SS') {
            const requiredStart = addDays(activity.lateStart, succPred.lag || 0);
            if (requiredStart < minLateStart) minLateStart = new Date(requiredStart);
          } // etc.
        });
        activity.lateFinish = addDays(minLateStart, -1);
      } else {
        activity.lateFinish = new Date(activity.earlyFinish);
      }
      activity.lateStart = addDays(activity.lateFinish, -activity.duration);
      activity.totalFloat = differenceInDays(activity.lateStart, activity.earlyStart);
      if (activity.totalFloat <= 0) activity.isCritical = true;
    });

    return activities.reverse();
  }, [flatActivities, addWorkingDays]);

  // Activity management
  const updateActivity = useCallback((activityId, updates) => {
    const updateTree = (activities) => {
      return activities.map(activity => {
        if (!activity || typeof activity !== 'object') return activity;
        if (activity.id === activityId) {
          const updated = { ...activity, ...updates };
          if (updates.duration !== undefined || updates.startDate !== undefined) {
            updated.endDate = addWorkingDays(updated.startDate, updated.duration);
          }
          if (updates.progress !== undefined) {
            if (updates.progress === 100) updated.status = 'Completed';
            else if (updates.progress > 0) updated.status = 'In Progress';
          }
          return updated;
        }
        if (activity.children) {
          return { ...activity, children: updateTree(activity.children) };
        }
        return activity;
      });
    };
    setProjectData(prev => ({ ...prev, activities: updateTree(prev.activities) }));
    if (autoSchedule) calculateCriticalPath();
    Animated.sequence([
      Animated.timing(scaleAnim, { toValue: 1.05, duration: 150, useNativeDriver: true }),
      Animated.timing(scaleAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
    ]).start();
  }, [addWorkingDays, autoSchedule]);

  const deleteActivity = useCallback((activityId) => {
    Alert.alert('Confirm Delete', 'Are you sure?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => {
          const deleteFromTree = (activities) => {
            return activities.filter(activity => {
              if (!activity || typeof activity !== 'object') return false;
              if (activity.id === activityId) return false;
              if (activity.children) {
                activity.children = deleteFromTree(activity.children);
              }
              return true;
            });
          };
          setProjectData(prev => ({ ...prev, activities: deleteFromTree(prev.activities) }));
          Alert.alert('Success', 'Activity deleted');
        },
      },
    ]);
  }, []);

  const addActivity = useCallback(() => {
    if (!newActivity.name.trim()) {
      Alert.alert('Error', 'Activity name is required');
      return;
    }
    const maxId = flatActivities.length > 0 ? Math.max(...flatActivities.map(a => a.id)) : 0;
    const newId = maxId + 1;
    const maxWBS = flatActivities.length > 0 ? Math.max(...flatActivities.map(a => parseInt(a.wbs.split('.')[0] || 0))) : 0;
    const newWBS = (maxWBS + 1).toString();
    const activity = {
      id: newId,
      wbs: newWBS,
      name: newActivity.name.trim(),
      description: newActivity.description.trim(),
      duration: parseInt(newActivity.duration) || 1,
      startDate: new Date(newActivity.startDate),
      endDate: addWorkingDays(new Date(newActivity.startDate), parseInt(newActivity.duration) || 1),
      progress: 0,
      status: newActivity.status,
      isCritical: newActivity.isCritical,
      priority: newActivity.priority,
      resources: newActivity.resources,
      cost: parseFloat(newActivity.cost) || 0,
      predecessors: newActivity.predecessors || [],
      tags: [],
      notes: '',
    };
    setProjectData(prev => ({ ...prev, activities: [...prev.activities, activity] }));
    setNewActivity({
      name: '',
      duration: '',
      startDate: new Date(),
      endDate: new Date(),
      progress: 0,
      status: 'Not Started',
      isCritical: false,
      resources: [],
      cost: '',
      description: '',
      priority: 'Medium',
      predecessors: [],
    });
    setShowAddActivityModal(false);
    Alert.alert('Success', 'Activity added');
  }, [newActivity, flatActivities, addWorkingDays]);

  const handleUpdateActivity = useCallback(() => {
    if (!editActivity.name.trim()) {
      Alert.alert('Error', 'Activity name is required');
      return;
    }
    updateActivity(editingActivity.id, {
      name: editActivity.name.trim(),
      description: editActivity.description.trim(),
      duration: parseInt(editActivity.duration) || 1,
      startDate: new Date(editActivity.startDate),
      progress: parseInt(editActivity.progress) || 0,
      status: editActivity.status,
      isCritical: editActivity.isCritical,
      priority: editActivity.priority,
      resources: editActivity.resources,
      cost: parseFloat(editActivity.cost) || 0,
      predecessors: editActivity.predecessors,
    });
    setShowEditActivityModal(false);
  }, [editActivity, editingActivity, updateActivity]);

  // Dependency management
  const addDependency = useCallback(() => {
    if (!newDependency.id) {
      Alert.alert('Error', 'Please select a predecessor activity');
      return;
    }
    if (newDependency.id === editingActivity.id) {
      Alert.alert('Error', 'Cannot depend on itself');
      return;
    }
    setEditActivity(prev => ({
      ...prev,
      predecessors: [...(prev.predecessors || []), { ...newDependency }],
    }));
    setShowDependencyModal(false);
    setNewDependency({ id: null, type: 'FS', lag: 0 });
  }, [newDependency, editingActivity]);

  const removeDependency = useCallback((index) => {
    setEditActivity(prev => ({
      ...prev,
      predecessors: prev.predecessors.filter((_, i) => i !== index),
    }));
  }, []);

  // Resource management
  const addResource = useCallback(() => {
    if (!newResource.name.trim() || !newResource.role.trim()) {
      Alert.alert('Error', 'Resource name and role are required');
      return;
    }
    const maxId = projectData.resources.length > 0 ? Math.max(...projectData.resources.map(r => r.id)) : 0;
    const newId = maxId + 1;
    const resource = {
      id: newId,
      name: newResource.name.trim(),
      role: newResource.role.trim(),
      rate: parseFloat(newResource.rate) || 0,
      utilization: parseInt(newResource.utilization) || 0,
      email: `${String(newResource.name ?? '').toLowerCase().replace(' ', '.')}@company.com`,
      skills: newResource.skills.split(',').map(s => s.trim()).filter(s => s),
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newResource.name)}&background=random`,
      department: 'Development',
      experience: '0 years',
      certification: '',
    };
    setProjectData(prev => ({ ...prev, resources: [...prev.resources, resource] }));
    setNewResource({ name: '', role: '', rate: '', utilization: 0, skills: '' });
    setShowAddResourceModal(false);
    Alert.alert('Success', 'Resource added');
  }, [newResource, projectData.resources]);

  const updateResource = useCallback((resourceId, updates) => {
    setProjectData(prev => ({
      ...prev,
      resources: prev.resources.map(r => (r && typeof r === 'object' && r.id === resourceId) ? { ...r, ...updates } : r),
    }));
  }, []);

  const deleteResource = useCallback((resourceId) => {
    Alert.alert('Confirm Delete', 'Are you sure you want to delete this resource?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => {
          setProjectData(prev => ({
            ...prev,
            resources: prev.resources.filter(r => r && typeof r === 'object' && r.id !== resourceId),
          }));
          Alert.alert('Success', 'Resource deleted');
        },
      },
    ]);
  }, []);

  // -----------------------------------------------------------------
  // 📤 EXPORT & SHARE FUNCTIONS (using Expo modules)
  // -----------------------------------------------------------------

  const getFileName = (extension) => {
    const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
    return `Project_${projectData.name.replace(/\s+/g, '_')}_${timestamp}.${extension}`;
  };

  const saveToCache = async (content, fileName) => {
    const path = `${FileSystem.cacheDirectory}${fileName}`;
    await FileSystem.writeAsStringAsync(path, content, { encoding: 'utf8' });
    return path;
  };

  const shareFile = async (path, message = '') => {
    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync(path, { dialogTitle: message });
    } else {
      Alert.alert('Sharing not available', 'Sharing is not available on this device.');
    }
  };

  const exportAsJSON = async () => {
    try {
      const jsonString = JSON.stringify(projectData, null, 2);
      const fileName = getFileName('json');
      const path = await saveToCache(jsonString, fileName);
      await shareFile(path, 'Project exported as JSON');
    } catch (error) {
      Alert.alert('Error', 'JSON export failed: ' + error.message);
    }
  };

  const exportAsCSV = async () => {
    try {
      const headers = ['WBS', 'Name', 'Duration', 'Start Date', 'End Date', 'Progress', 'Status', 'Priority', 'Cost', 'Resources', 'Predecessors'];
      const rows = flatActivities.map(a => [
        a.wbs,
        `"${a.name.replace(/"/g, '""')}"`,
        a.duration,
        formatDate(a.startDate),
        formatDate(a.endDate),
        `${a.progress}%`,
        a.status,
        a.priority,
        formatCurrency(a.cost).replace(/[^0-9.,-]/g, ''),
        a.resources.map(id => projectData.resources.find(r => r && r.id === id)?.name).filter(Boolean).join('; '),
        a.predecessors ? a.predecessors.map(p => `${p.id} (${p.type} ${p.lag})`).join('; ') : '',
      ]);
      const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
      const fileName = getFileName('csv');
      const path = await saveToCache(csvContent, fileName);
      await shareFile(path, 'Project exported as CSV');
    } catch (error) {
      Alert.alert('Error', 'CSV export failed: ' + error.message);
    }
  };

  const generateHTMLReport = () => {
    const activitiesHtml = flatActivities.map(a => `
      <tr>
        <td>${a.wbs}</td>
        <td>${a.name}</td>
        <td>${a.duration}</td>
        <td>${formatDate(a.startDate)}</td>
        <td>${formatDate(a.endDate)}</td>
        <td>${a.progress}%</td>
        <td>${a.status}</td>
        <td>${a.priority}</td>
        <td>${formatCurrency(a.cost)}</td>
        <td>${a.predecessors ? a.predecessors.map(p => `${p.id} (${p.type} ${p.lag})`).join(', ') : ''}</td>
      </tr>
    `).join('');

    return `
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #1E3A8A; }
          h2 { color: #3B82F6; margin-top: 30px; }
          table { border-collapse: collapse; width: 100%; margin-top: 20px; }
          th { background-color: #3B82F6; color: white; padding: 8px; text-align: left; }
          td { border: 1px solid #ddd; padding: 8px; }
          tr:nth-child(even) { background-color: #f2f2f2; }
          .summary { margin-top: 20px; display: flex; justify-content: space-around; }
          .stat { background: #f8fafc; padding: 10px; border-radius: 8px; text-align: center; }
        </style>
      </head>
      <body>
        <h1>${projectData.name}</h1>
        <p><strong>Description:</strong> ${projectData.description}</p>
        <p><strong>Period:</strong> ${formatDate(projectData.startDate)} - ${formatDate(projectData.endDate)}</p>
        <p><strong>Status:</strong> ${projectData.status} | <strong>Priority:</strong> ${projectData.priority}</p>
        <p><strong>Budget:</strong> ${formatCurrency(projectData.budget)}</p>

        <div class="summary">
          <div class="stat">Total Progress<br><strong>${Math.round(flatActivities.reduce((sum, a) => sum + a.progress, 0) / flatActivities.length)}%</strong></div>
          <div class="stat">Activities<br><strong>${flatActivities.length}</strong></div>
          <div class="stat">Resources<br><strong>${projectData.resources.length}</strong></div>
          <div class="stat">Critical Path<br><strong>${flatActivities.filter(a => a.isCritical).length}</strong></div>
        </div>

        <h2>Activities</h2>
        <table>
          <thead>
            <tr><th>WBS</th><th>Name</th><th>Dur.</th><th>Start</th><th>End</th><th>Progress</th><th>Status</th><th>Priority</th><th>Cost</th><th>Predecessors</th></tr>
          </thead>
          <tbody>
            ${activitiesHtml}
          </tbody>
        </table>

        <h2>Resources</h2>
        <table>
          <thead><tr><th>Name</th><th>Role</th><th>Rate</th><th>Utilization</th><th>Skills</th></tr></thead>
          <tbody>
            ${projectData.resources.filter(r => r && typeof r === 'object').map(r => `
              <tr>
                <td>${r.name}</td>
                <td>${r.role}</td>
                <td>${formatCurrency(r.rate)}/hr</td>
                <td>${r.utilization}%</td>
                <td>${r.skills.join(', ')}</td>
              </tr>`).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
  };

  const exportAsPDF = async () => {
    try {
      const html = generateHTMLReport();
      const { uri } = await Print.printToFileAsync({ html });
      await shareFile(uri, 'Project exported as PDF');
    } catch (error) {
      Alert.alert('Error', 'PDF export failed: ' + error.message);
    }
  };

  const exportAsMSProject = async () => {
    try {
      const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Project xmlns="http://schemas.microsoft.com/project">
  <Name>${projectData.name}</Name>
  <StartDate>${format(new Date(projectData.startDate), 'yyyy-MM-dd')}</StartDate>
  <FinishDate>${format(new Date(projectData.endDate), 'yyyy-MM-dd')}</FinishDate>
  <Tasks>
    ${flatActivities.map(a => `
    <Task>
      <UID>${a.id}</UID>
      <WBS>${a.wbs}</WBS>
      <Name>${a.name}</Name>
      <Duration>${a.duration}</Duration>
      <Start>${format(new Date(a.startDate), 'yyyy-MM-dd')}</Start>
      <Finish>${format(new Date(a.endDate), 'yyyy-MM-dd')}</Finish>
      <PercentComplete>${a.progress}</PercentComplete>
      ${a.predecessors?.length ? `<PredecessorLinks>${a.predecessors.map(p => `<PredecessorLink><PredecessorUID>${p.id}</PredecessorUID><Type>${p.type === 'FS' ? 1 : p.type === 'SS' ? 2 : p.type === 'FF' ? 3 : 4}</Type><Lag>${p.lag * 480}</Lag></PredecessorLink>`).join('')}</PredecessorLinks>` : ''}
    </Task>`).join('')}
  </Tasks>
  <Resources>
    ${projectData.resources.filter(r => r && typeof r === 'object').map(r => `
    <Resource>
      <UID>${r.id}</UID>
      <Name>${r.name}</Name>
      <Type>1</Type>
      <MaxUnits>${r.utilization}</MaxUnits>
    </Resource>`).join('')}
  </Resources>
  <Assignments>
  </Assignments>
</Project>`;
      const fileName = getFileName('xml');
      const path = await saveToCache(xml, fileName);
      await shareFile(path, 'Project exported as MS Project XML');
    } catch (error) {
      Alert.alert('Error', 'MS Project export failed: ' + error.message);
    }
  };

  const printProject = async () => {
    try {
      const html = generateHTMLReport();
      await Print.printAsync({ html });
    } catch (error) {
      Alert.alert('Error', 'Print failed: ' + error.message);
    }
  };

  // NEW: Export as Primavera XER
  const formatDateForXER = (date) => {
    if (!date) return '';
    return format(new Date(date), 'yyyy-MM-dd');
  };

  const exportAsXER = async () => {
    try {
      const lines = [];

      // PROJECT table
      lines.push('%T PROJECT');
      lines.push('%F proj_id,proj_short_name,proj_name,proj_desc,plan_start_date,plan_end_date,proj_status,budget,currency');
      lines.push(`%R 1,"${projectData.name}","${projectData.name}","${projectData.description || ''}",${formatDateForXER(projectData.startDate)},${formatDateForXER(projectData.endDate)},${projectData.status},${projectData.budget},${projectData.currency}`);
      lines.push('%E');

      // RESOURCE table
      if (projectData.resources.length) {
        lines.push('%T RESOURCE');
        lines.push('%F rsrc_id,rsrc_name,rsrc_role,cost_per_qty,avail_qty,skills');
        projectData.resources.forEach((res, idx) => {
          lines.push(`%R ${idx+1},"${res.name}","${res.role}",${res.rate},${res.utilization},"${res.skills.join(',')}"`);
        });
        lines.push('%E');
      }

      // TASK table
      lines.push('%T TASK');
      lines.push('%F task_id,wbs_code,task_name,task_desc,target_start_date,phys_complete,status_code,critical_task_flag,priority_type,target_cost');
      flatActivities.forEach((act, idx) => {
        const statusCode = act.status === 'Completed' ? 'CL' : act.status === 'In Progress' ? 'OP' : 'PL';
        const critical = act.isCritical ? 'Y' : 'N';
        lines.push(`%R ${idx+1},"${act.wbs}","${act.name}","${act.description || ''}",${formatDateForXER(act.startDate)},${act.progress},${statusCode},${critical},${act.priority},${act.cost}`);
      });
      lines.push('%E');

      // TASKPRED table
      const preds = [];
      flatActivities.forEach(act => {
        act.predecessors?.forEach(pred => {
          preds.push([act.id, pred.id, pred.type, pred.lag * 8]); // convert days to hours
        });
      });
      if (preds.length) {
        lines.push('%T TASKPRED');
        lines.push('%F task_id,pred_task_id,pred_type,lag_hr');
        preds.forEach(([taskId, predTaskId, type, lagHr]) => {
          lines.push(`%R ${taskId},${predTaskId},${type},${lagHr}`);
        });
        lines.push('%E');
      }

      // TASKRSRC table
      const assignments = [];
      flatActivities.forEach(act => {
        act.resources?.forEach(rsrcId => {
          assignments.push([act.id, rsrcId]);
        });
      });
      if (assignments.length) {
        lines.push('%T TASKRSRC');
        lines.push('%F task_id,rsrc_id');
        assignments.forEach(([taskId, rsrcId]) => {
          lines.push(`%R ${taskId},${rsrcId}`);
        });
        lines.push('%E');
      }

      const xerContent = lines.join('\n');
      const fileName = getFileName('xer');
      const path = await saveToCache(xerContent, fileName);
      await shareFile(path, 'Project exported as Primavera XER');
    } catch (error) {
      Alert.alert('Error', 'XER export failed: ' + error.message);
    }
  };

  const shareProject = () => {
    Alert.alert(
      'Share Project',
      'Choose format to share:',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'JSON', onPress: exportAsJSON },
        { text: 'CSV', onPress: exportAsCSV },
        { text: 'PDF', onPress: exportAsPDF },
        { text: 'MS Project', onPress: exportAsMSProject },
        { text: 'Primavera XER', onPress: exportAsXER },
        { text: 'Print', onPress: printProject },
      ]
    );
  };

  const exportProject = shareProject;

  // -----------------------------------------------------------------
  // 📥 IMPORT PRIMAVERA PROJECT (XER)
  // -----------------------------------------------------------------
  const importPrimaveraProject = async () => {
    try {
      setImportLoading(true);
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
      });

      if (result.type === 'success') {
        const fileContent = await FileSystem.readAsStringAsync(result.uri);
        parseXER(fileContent);
      }
    } catch (error) {
      Alert.alert('Import Error', error.message);
    } finally {
      setImportLoading(false);
    }
  };

  const parseXER = (content) => {
    const lines = content.split('\n').filter(line => line.trim() !== '');
    let currentTable = null;
    let columns = [];
    const tables = {};

    lines.forEach(line => {
      if (line.startsWith('%T')) {
        currentTable = line.substring(3).trim();
        tables[currentTable] = { columns: [], rows: [] };
      } else if (line.startsWith('%F') && currentTable) {
        columns = line.substring(3).split(',').map(c => c.trim());
        tables[currentTable].columns = columns;
      } else if (line.startsWith('%R') && currentTable) {
        const values = line.substring(3).split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const row = {};
        columns.forEach((col, idx) => {
          row[col] = values[idx] || '';
        });
        tables[currentTable].rows.push(row);
      } else if (line.startsWith('%E')) {
        currentTable = null;
      }
    });

    if (!tables['PROJECT']) {
      Alert.alert('Error', 'Invalid XER file: missing PROJECT table');
      return;
    }

    mapPrimaveraToProject(tables);
  };

  const mapPrimaveraToProject = (tables) => {
    const projectTable = tables['PROJECT']?.rows[0];
    if (!projectTable) {
      Alert.alert('Error', 'No project found in file');
      return;
    }

    // Helper functions
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const parts = dateStr.split(' ')[0].split('-');
      if (parts.length === 3) {
        return new Date(parts[0], parts[1] - 1, parts[2]);
      }
      return null;
    };

    const mapStatus = (code) => {
      switch (code) {
        case 'PL': return 'Planning';
        case 'OP': return 'In Progress';
        case 'CL': return 'Completed';
        default: return 'Not Started';
      }
    };

    const mapPriority = (code) => {
      if (code === 'High' || code === 'TH') return 'High';
      if (code === 'Medium' || code === 'TM') return 'Medium';
      return 'Low';
    };

    const mapPredType = (code) => {
      switch (code) {
        case 'FS': return 'FS';
        case 'SS': return 'SS';
        case 'FF': return 'FF';
        case 'SF': return 'SF';
        default: return 'FS';
      }
    };

    // Build new project data
    const newProject = {
      id: Date.now(),
      name: projectTable.proj_name || 'Imported Project',
      description: projectTable.proj_desc || '',
      startDate: parseDate(projectTable.plan_start_date) || new Date(),
      endDate: parseDate(projectTable.plan_end_date) || addDays(new Date(), 180),
      status: mapStatus(projectTable.proj_status),
      priority: mapPriority(projectTable.proj_priority),
      budget: parseFloat(projectTable.budget) || 0,
      manager: projectTable.manager || '',
      client: projectTable.client || '',
      currency: projectTable.currency || 'GBP',
      activities: [],
      resources: [],
      milestones: [],
      risks: [],
      settings: { ...projectData.settings },
    };

    // Map resources
    const resourceMap = {};
    if (tables['RESOURCE']) {
      tables['RESOURCE'].rows.forEach((res, index) => {
        const newId = index + 1;
        resourceMap[res.rsrc_id] = newId;
        newProject.resources.push({
          id: newId,
          name: res.rsrc_name || '',
          role: res.rsrc_role || '',
          rate: parseFloat(res.cost_per_qty) || 0,
          utilization: parseInt(res.avail_qty) || 100,
          skills: (res.skills || '').split(',').map(s => s.trim()),
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(res.rsrc_name)}&background=random`,
        });
      });
    }

    // Map tasks (activities)
    const taskMap = {};
    if (tables['TASK']) {
      tables['TASK'].rows.forEach((task, index) => {
        const newId = index + 1;
        taskMap[task.task_id] = newId;

        const start = parseDate(task.target_start_date) || parseDate(task.early_start_date) || newProject.startDate;
        const duration = parseInt(task.target_drtn_hr) / 8 || 1; // convert hours to days

        newProject.activities.push({
          id: newId,
          wbs: task.wbs_code || (index + 1).toString(),
          name: task.task_name || 'Unnamed',
          description: task.task_desc || '',
          duration: duration,
          startDate: new Date(start),
          endDate: addWorkingDays(new Date(start), duration),
          progress: parseFloat(task.phys_complete) || 0,
          status: mapStatus(task.status_code),
          isCritical: task.critical_task_flag === 'Y',
          priority: mapPriority(task.priority_type),
          resources: [],
          cost: parseFloat(task.target_cost) || 0,
          predecessors: [],
          tags: [],
        });
      });
    }

    // Map task predecessors
    if (tables['TASKPRED']) {
      tables['TASKPRED'].rows.forEach(pred => {
        const taskId = taskMap[pred.task_id];
        const predTaskId = taskMap[pred.pred_task_id];
        if (taskId && predTaskId) {
          const activity = newProject.activities.find(a => a.id === taskId);
          if (activity) {
            activity.predecessors.push({
              id: predTaskId,
              type: mapPredType(pred.pred_type),
              lag: (parseInt(pred.lag_hr) || 0) / 8,
            });
          }
        }
      });
    }

    // Map resource assignments
    if (tables['TASKRSRC']) {
      tables['TASKRSRC'].rows.forEach(ass => {
        const taskId = taskMap[ass.task_id];
        const rsrcId = resourceMap[ass.rsrc_id];
        if (taskId && rsrcId) {
          const activity = newProject.activities.find(a => a.id === taskId);
          if (activity) {
            if (!activity.resources) activity.resources = [];
            activity.resources.push(rsrcId);
          }
        }
      });
    }

    setProjectData(newProject);
    Alert.alert('Success', 'Project imported successfully');
  };

  // -----------------------------------------------------------------
  // 📥 OPEN PROJECT (JSON)
  // -----------------------------------------------------------------
  const importProjectJSON = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/json',
        copyToCacheDirectory: true,
      });
      if (result.type === 'success') {
        const fileContent = await FileSystem.readAsStringAsync(result.uri);
        const importedProject = JSON.parse(fileContent);
        // Validate basic structure
        if (!importedProject.name || !importedProject.activities) {
          Alert.alert('Error', 'Invalid project file');
          return;
        }
        // Convert date strings back to Date objects
        const convertDates = (obj) => {
          if (!obj || typeof obj !== 'object') return obj;
          if (obj.startDate) obj.startDate = new Date(obj.startDate);
          if (obj.endDate) obj.endDate = new Date(obj.endDate);
          if (obj.date) obj.date = new Date(obj.date);
          if (obj.children) obj.children.forEach(convertDates);
          return obj;
        };
        importedProject.startDate = new Date(importedProject.startDate);
        importedProject.endDate = new Date(importedProject.endDate);
        importedProject.activities = (importedProject.activities || []).map(convertDates);
        importedProject.resources = (importedProject.resources || []);
        importedProject.milestones = (importedProject.milestones || []).map(convertDates);
        setProjectData(importedProject);
        Alert.alert('Success', 'Project opened successfully');
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to open project: ' + error.message);
    }
  };

  // New Project Handlers
  const handleNewProject = () => {
    setProjectForm({
      name: '',
      description: '',
      startDate: new Date(),
      endDate: addDays(new Date(), 180),
      status: 'Planning',
      priority: 'Medium',
      budget: '',
      manager: '',
      client: '',
    });
    setShowNewProjectModal(true);
  };

  const createNewProject = () => {
    if (!projectForm.name.trim()) {
      Alert.alert('Error', 'Project name is required');
      return;
    }

    setProjectData({
      ...projectData,
      id: Date.now(),
      name: projectForm.name.trim(),
      description: projectForm.description.trim(),
      startDate: new Date(projectForm.startDate),
      endDate: new Date(projectForm.endDate),
      status: projectForm.status,
      priority: projectForm.priority,
      budget: parseFloat(projectForm.budget) || 0,
      manager: projectForm.manager.trim(),
      client: projectForm.client.trim(),
      activities: [],
      resources: [],
      milestones: [],
      risks: [],
    });

    setShowNewProjectModal(false);
    Alert.alert('Success', 'New project created');
  };

  // Edit Project Handlers
  const handleEditProject = () => {
    setProjectForm({
      name: projectData.name,
      description: projectData.description,
      startDate: new Date(projectData.startDate),
      endDate: new Date(projectData.endDate),
      status: projectData.status,
      priority: projectData.priority,
      budget: projectData.budget.toString(),
      manager: projectData.manager,
      client: projectData.client,
    });
    setShowEditProjectModal(true);
  };

  const updateProject = () => {
    if (!projectForm.name.trim()) {
      Alert.alert('Error', 'Project name is required');
      return;
    }

    setProjectData(prev => ({
      ...prev,
      name: projectForm.name.trim(),
      description: projectForm.description.trim(),
      startDate: new Date(projectForm.startDate),
      endDate: new Date(projectForm.endDate),
      status: projectForm.status,
      priority: projectForm.priority,
      budget: parseFloat(projectForm.budget) || prev.budget,
      manager: projectForm.manager.trim(),
      client: projectForm.client.trim(),
    }));

    setShowEditProjectModal(false);
    Alert.alert('Success', 'Project updated');
  };

  // Data persistence
  useEffect(() => {
    setIsLoading(true);
    Animated.sequence([
      Animated.parallel([
        Animated.timing(fadeAnim, { toValue: 1, duration: 800, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
        Animated.timing(slideAnim, { toValue: 0, duration: 800, easing: Easing.out(Easing.back(1.2)), useNativeDriver: true }),
        Animated.timing(scaleAnim, { toValue: 1, duration: 800, easing: Easing.out(Easing.back(1.1)), useNativeDriver: true }),
      ]),
      Animated.timing(progressAnim, { toValue: 1, duration: 1200, easing: Easing.out(Easing.cubic), useNativeDriver: false }),
    ]).start(() => setIsLoading(false));

    Animated.loop(Animated.timing(rotateAnim, { toValue: 1, duration: 2000, easing: Easing.linear, useNativeDriver: true })).start();
    loadProjectData();
  }, []);

  useEffect(() => {
    if (!isLoading) saveProjectData();
  }, [projectData, isLoading]);

  // Persist extra data
  useEffect(() => {
    if (!isLoading) {
      AsyncStorage.multiSet([
        ['pps_risks', JSON.stringify(risks)],
        ['pps_issues', JSON.stringify(issues)],
        ['pps_milestones', JSON.stringify(projectMilestones)],
        ['pps_time', JSON.stringify(timeEntries)],
        ['pps_notes', JSON.stringify(activityNotes)],
      ]).catch(()=>{});
    }
  }, [risks, issues, projectMilestones, timeEntries, activityNotes, isLoading]);

  // Update notifications
  useEffect(() => { setNotifications(genNotifications()); }, [genNotifications]);

  useEffect(() => {
    const listener = scrollY.addListener(({ value }) => {
      headerOpacity.setValue(Math.max(0, Math.min(1, 1 - value / 100)));
    });
    return () => scrollY.removeListener(listener);
  }, []);

  const loadProjectData = async () => {
    try {
      const saved = await AsyncStorage.getItem('projectPlanningData_v2');
      if (saved) {
        const parsed = JSON.parse(saved);
        const convertDates = (obj) => {
          if (!obj || typeof obj !== 'object') return obj;
          if (obj.startDate) obj.startDate = new Date(obj.startDate);
          if (obj.endDate) obj.endDate = new Date(obj.endDate);
          if (obj.date) obj.date = new Date(obj.date);
          if (obj.children) obj.children.forEach(convertDates);
          return obj;
        };
        parsed.startDate = new Date(parsed.startDate);
        parsed.endDate = new Date(parsed.endDate);
        parsed.activities = (parsed.activities || []).filter(a => a && typeof a === 'object').map(convertDates);
        parsed.resources = (parsed.resources || []).filter(r => r && typeof r === 'object');
        parsed.milestones = (parsed.milestones || []).filter(m => m && typeof m === 'object').map(convertDates);
        setProjectData(parsed);
      }
    } catch (error) {
      console.log('Error loading project data:', error);
    }
  };

  const saveProjectData = async () => {
    try {
      await AsyncStorage.setItem('projectPlanningData_v2', JSON.stringify(projectData));
    } catch (error) {
      console.log('Error saving project data:', error);
    }
  };

  // Refresh function
  const onRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      loadProjectData();
      setRefreshing(false);
      Animated.sequence([
        Animated.timing(rotateAnim, { toValue: 1, duration: 500, useNativeDriver: true }),
        Animated.timing(rotateAnim, { toValue: 0, duration: 0, useNativeDriver: true }),
      ]).start();
    }, 1000);
  };

  // ---------- Render Functions ----------

  const renderAnimatedHeader = () => {
    const headerTranslateY = scrollY.interpolate({ inputRange: [0, 100], outputRange: [0, -50], extrapolate: 'clamp' });
    const headerScale = scrollY.interpolate({ inputRange: [0, 100], outputRange: [1, 0.9], extrapolate: 'clamp' });
    return (
      <Animated.View style={[styles.animatedHeader, { opacity: headerOpacity, transform: [{ translateY: headerTranslateY }, { scale: headerScale }] }]}>
        <LinearGradient colors={['#1E3A8A', '#3B82F6', '#60A5FA']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.headerGradient}>
          <StatusBar barStyle="light-content" backgroundColor="#1E3A8A" />
          <View style={styles.headerTop}>
            <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton} activeOpacity={0.7} accessibilityLabel="Go back">
              <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
                <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
              </Animated.View>
            </TouchableOpacity>
            <Animated.View style={[styles.headerContent, { opacity: fadeAnim }]}>
              <Text style={styles.headerTitle} numberOfLines={1}>{projectData.name}</Text>
              <Text style={styles.headerSubtitle}>{formatDate(projectData.startDate)} - {formatDate(projectData.endDate)}</Text>
            </Animated.View>
            <TouchableOpacity style={[styles.headerAction,{position:'relative'}]} onPress={()=>setShowNotificationsPanel(true)} activeOpacity={0.7} accessibilityLabel="Notifications">
              <MaterialCommunityIcons name="bell-outline" size={24} color="#FFFFFF" />
              {(()=>{const n=flatActivities.filter(a=>a.status!=='Completed'&&isBefore(new Date(a.endDate),new Date())).length+issues.filter(i=>i.status==='Open'&&i.priority==='Critical').length;return n>0?<View style={{position:'absolute',top:-4,right:-4,minWidth:16,height:16,borderRadius:8,backgroundColor:'#EF4444',justifyContent:'center',alignItems:'center',paddingHorizontal:2}}><Text style={{fontSize:9,color:'#FFF',fontWeight:'bold'}}>{n}</Text></View>:null;})()}
            </TouchableOpacity>
            <TouchableOpacity style={styles.headerAction} onPress={()=>setShowSettingsModal(true)} activeOpacity={0.7} accessibilityLabel="Settings">
              <MaterialCommunityIcons name="cog-outline" size={24} color="#FFFFFF" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.headerAction} onPress={exportProject} activeOpacity={0.7} accessibilityLabel="Share / Export">
              <MaterialCommunityIcons name="share-variant" size={24} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
          <Animated.View style={[styles.projectStats, { transform: [{ translateY: slideAnim }] }]}>
            <View style={styles.statItem} accessible accessibilityLabel={`Overall progress ${Math.round(flatActivities.reduce((sum, a) => sum + a.progress, 0) / flatActivities.length)} percent`}>
              <Text style={styles.statValue}>{Math.round(flatActivities.reduce((sum, a) => sum + a.progress, 0) / flatActivities.length)}%</Text>
              <Text style={styles.statLabel}>Complete</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem} accessible accessibilityLabel={`Total budget ${formatCurrency(flatActivities.reduce((sum, a) => sum + a.cost, 0))}`}>
              <Text style={styles.statValue}>{formatCurrency(flatActivities.reduce((sum, a) => sum + a.cost, 0))}</Text>
              <Text style={styles.statLabel}>Budget</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem} accessible accessibilityLabel={`Team size ${projectData.resources.length}`}>
              <Text style={styles.statValue}>{projectData.resources.length}</Text>
              <Text style={styles.statLabel}>Team</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem} accessible accessibilityLabel={`Critical activities ${flatActivities.filter(a => a.isCritical).length}`}>
              <Text style={styles.statValue}>{flatActivities.filter(a => a.isCritical).length}</Text>
              <Text style={styles.statLabel}>Critical</Text>
            </View>
          </Animated.View>
        </LinearGradient>
      </Animated.View>
    );
  };

  const renderToolbar = () => (
    <Animated.View style={[styles.toolbar, { opacity: fadeAnim }]}>
      <LinearGradient colors={['#FFFFFF', '#F8FAFC']} style={styles.toolbarGradient}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.toolbarContent}>
          <TouchableOpacity
            style={[styles.toolbarButton, styles.primaryButton]}
            onPress={handleNewProject}
            activeOpacity={0.8}
            accessibilityLabel="Create new project"
          >
            <LinearGradient colors={['#10B981', '#059669']} style={styles.buttonGradient}>
              <MaterialCommunityIcons name="file-plus" size={18} color="#FFFFFF" />
              <Text style={styles.primaryButtonText}>New Project</Text>
            </LinearGradient>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.toolbarButton, styles.primaryButton]}
            onPress={handleEditProject}
            activeOpacity={0.8}
            accessibilityLabel="Edit current project"
          >
            <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.buttonGradient}>
              <MaterialCommunityIcons name="pencil" size={18} color="#FFFFFF" />
              <Text style={styles.primaryButtonText}>Edit Project</Text>
            </LinearGradient>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.toolbarButton, styles.primaryButton]}
            onPress={() => setShowAddActivityModal(true)}
            activeOpacity={0.8}
            accessibilityLabel="Add activity"
          >
            <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.buttonGradient}>
              <MaterialCommunityIcons name="plus" size={18} color="#FFFFFF" />
              <Text style={styles.primaryButtonText}>Add Activity</Text>
            </LinearGradient>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.toolbarButton, styles.secondaryButton]}
            onPress={() => setShowAddResourceModal(true)}
            activeOpacity={0.8}
            accessibilityLabel="Add resource"
          >
            <MaterialCommunityIcons name="account-plus" size={18} color="#10B981" />
            <Text style={styles.secondaryButtonText}>Add Resource</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.toolbarButton, styles.secondaryButton]}
            onPress={() => {
              const critical = calculateCriticalPath().filter(a => a.isCritical);
              Alert.alert('Critical Path', `Found ${critical.length} critical activities:\n${critical.map(a => `• ${a.name}`).join('\n')}`);
            }}
            activeOpacity={0.8}
            accessibilityLabel="Show critical path"
          >
            <MaterialCommunityIcons name="chart-timeline-variant" size={18} color="#EF4444" />
            <Text style={styles.secondaryButtonText}>Critical Path</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.toolbarButton, styles.secondaryButton]}
            onPress={importPrimaveraProject}
            disabled={importLoading}
            activeOpacity={0.8}
            accessibilityLabel="Import Primavera project"
          >
            <MaterialCommunityIcons name="file-import" size={18} color="#8B5CF6" />
            <Text style={styles.secondaryButtonText}>{importLoading ? 'Importing...' : 'Import'}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.toolbarButton, styles.secondaryButton]}
            onPress={importProjectJSON}
            activeOpacity={0.8}
            accessibilityLabel="Open project from JSON file"
          >
            <MaterialCommunityIcons name="folder-open" size={18} color="#8B5CF6" />
            <Text style={styles.secondaryButtonText}>Open Project</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.toolbarButton, styles.secondaryButton]}
            onPress={exportProject}
            activeOpacity={0.8}
            accessibilityLabel="Share / Export"
          >
            <MaterialCommunityIcons name="share-variant" size={18} color="#8B5CF6" />
            <Text style={styles.secondaryButtonText}>Share</Text>
          </TouchableOpacity>

          <View style={styles.toolbarSeparator} />

          <View style={styles.toolbarGroup}>
            <Text style={styles.toolbarLabel}>Timeline:</Text>
            {['days', 'weeks', 'months'].map(scale => (
              <TouchableOpacity
                key={scale}
                style={[styles.scaleButton, timelineScale === scale && styles.scaleButtonActive]}
                onPress={() => setTimelineScale(scale)}
                activeOpacity={0.7}
                accessibilityLabel={`Scale ${scale}`}
                accessibilityState={{ selected: timelineScale === scale }}
              >
                <Text style={[styles.scaleButtonText, timelineScale === scale && styles.scaleButtonTextActive]}>
                  {scale.charAt(0).toUpperCase() + scale.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.toolbarSeparator} />

          <View style={styles.toolbarGroup}>
            <Text style={styles.toolbarLabel}>Auto Schedule:</Text>
            <Switch
              value={autoSchedule}
              onValueChange={setAutoSchedule}
              trackColor={{ false: '#D1D5DB', true: '#3B82F6' }}
              thumbColor={autoSchedule ? '#FFFFFF' : '#9CA3AF'}
              ios_backgroundColor="#D1D5DB"
              accessibilityLabel="Toggle auto schedule"
            />
          </View>

          <TouchableOpacity
            style={[styles.toolbarButton, styles.refreshButton]}
            onPress={onRefresh}
            activeOpacity={0.8}
            accessibilityLabel="Refresh"
          >
            <Animated.View style={{ transform: [{ rotate: refreshing ? rotateAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] }) : '0deg' }] }}>
              <MaterialCommunityIcons name="refresh" size={18} color={refreshing ? '#3B82F6' : '#6B7280'} />
            </Animated.View>
          </TouchableOpacity>

          <View style={styles.toolbarSeparator} />

          <TouchableOpacity style={[styles.toolbarButton, styles.secondaryButton]} onPress={()=>{setRiskForm({...EMPTY_RISK});setEditingRisk(null);setShowRiskModal(true);}} activeOpacity={0.8}>
            <MaterialCommunityIcons name="shield-plus" size={18} color="#F97316" />
            <Text style={[styles.secondaryButtonText,{color:'#F97316'}]}>Add Risk</Text>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.toolbarButton, styles.secondaryButton]} onPress={()=>{setIssueForm({...EMPTY_ISSUE});setEditingIssue(null);setShowIssueModal(true);}} activeOpacity={0.8}>
            <MaterialCommunityIcons name="bug-check" size={18} color="#8B5CF6" />
            <Text style={[styles.secondaryButtonText,{color:'#8B5CF6'}]}>Add Issue</Text>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.toolbarButton, styles.secondaryButton]} onPress={()=>{setMsForm({...EMPTY_MS});setEditingMs(null);setShowMsModal(true);}} activeOpacity={0.8}>
            <MaterialCommunityIcons name="flag-plus" size={18} color="#EC4899" />
            <Text style={[styles.secondaryButtonText,{color:'#EC4899'}]}>Add Milestone</Text>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.toolbarButton, styles.secondaryButton]} onPress={()=>{setTimeForm({...EMPTY_TIME});setEditingTime(null);setShowTimeModal(true);}} activeOpacity={0.8}>
            <MaterialCommunityIcons name="clock-plus" size={18} color="#14B8A6" />
            <Text style={[styles.secondaryButtonText,{color:'#14B8A6'}]}>Log Time</Text>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.toolbarButton, styles.secondaryButton]} onPress={()=>setShowBaselineModal(true)} activeOpacity={0.8}>
            <MaterialCommunityIcons name="bookmark-check" size={18} color="#6366F1" />
            <Text style={[styles.secondaryButtonText,{color:'#6366F1'}]}>{baseline?'Update Baseline':'Save Baseline'}</Text>
          </TouchableOpacity>

          <View style={styles.toolbarSeparator} />

          <TouchableOpacity style={[styles.toolbarButton, styles.secondaryButton]} onPress={handleUndo} disabled={!undoStack.length} activeOpacity={0.8}>
            <MaterialCommunityIcons name="undo-variant" size={18} color={undoStack.length?'#3B82F6':'#D1D5DB'} />
            <Text style={[styles.secondaryButtonText,{color:undoStack.length?'#3B82F6':'#D1D5DB'}]}>Undo</Text>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.toolbarButton, styles.secondaryButton]} onPress={handleRedo} disabled={!redoStack.length} activeOpacity={0.8}>
            <MaterialCommunityIcons name="redo-variant" size={18} color={redoStack.length?'#3B82F6':'#D1D5DB'} />
            <Text style={[styles.secondaryButtonText,{color:redoStack.length?'#3B82F6':'#D1D5DB'}]}>Redo</Text>
          </TouchableOpacity>
        </ScrollView>
      </LinearGradient>
    </Animated.View>
  );

  const renderFilters = () => (
    <Animated.View style={[styles.filtersContainer, { opacity: fadeAnim }]}>
      <View style={styles.searchContainer}>
        <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search activities, WBS, or tags..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#9CA3AF"
          accessibilityLabel="Search activities"
        />
        {searchQuery ? (
          <TouchableOpacity onPress={() => setSearchQuery('')} activeOpacity={0.7} accessibilityLabel="Clear search">
            <MaterialCommunityIcons name="close-circle" size={20} color="#6B7280" />
          </TouchableOpacity>
        ) : null}
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterButtons} contentContainerStyle={styles.filterButtonsContent}>
        {['All', 'Not Started', 'In Progress', 'Completed', 'On Hold', 'Delayed'].map(status => (
          <TouchableOpacity
            key={status}
            style={[styles.filterButton, filterStatus === status && styles.filterButtonActive]}
            onPress={() => setFilterStatus(status)}
            activeOpacity={0.8}
            accessibilityLabel={`Filter by ${status}`}
            accessibilityState={{ selected: filterStatus === status }}
          >
            {filterStatus === status && <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.filterButtonGradient} />}
            <Text style={[styles.filterButtonText, filterStatus === status && styles.filterButtonTextActive]}>{status}</Text>
            {status !== 'All' && (
              <View style={[styles.filterBadge, { backgroundColor: getStatusColor(status) }]}>
                <Text style={styles.filterBadgeText}>{flatActivities.filter(a => a.status === status).length}</Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
      <View style={styles.sortContainer}>
        <Text style={styles.sortLabel}>Sort by:</Text>
        <TouchableOpacity
          style={styles.sortButton}
          onPress={() => {
            const options = [
              { key: 'wbs', label: 'WBS Code' },
              { key: 'name', label: 'Activity Name' },
              { key: 'startDate', label: 'Start Date' },
              { key: 'endDate', label: 'End Date' },
              { key: 'progress', label: 'Progress' },
              { key: 'priority', label: 'Priority' },
              { key: 'cost', label: 'Cost' },
            ];
            Alert.alert('Sort Activities', 'Choose criteria:', options.map(opt => ({ text: opt.label, onPress: () => setSortBy(opt.key) })));
          }}
          activeOpacity={0.8}
          accessibilityLabel="Sort activities"
        >
          <Text style={styles.sortButtonText}>
            {sortBy === 'wbs' ? 'WBS Code' : sortBy === 'name' ? 'Activity Name' : sortBy === 'startDate' ? 'Start Date' : sortBy === 'endDate' ? 'End Date' : sortBy === 'progress' ? 'Progress' : sortBy === 'priority' ? 'Priority' : 'Cost'}
          </Text>
          <MaterialCommunityIcons name="chevron-down" size={16} color="#6B7280" />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  const renderTabNavigation = () => (
    <Animated.View style={[styles.tabContainer, { opacity: fadeAnim }]}>
      <LinearGradient colors={['#FFFFFF', '#F8FAFC']} style={styles.tabGradient}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabContent}>
          {[
            { key: 'dashboard', label: 'Dashboard', icon: 'view-dashboard' },
            { key: 'gantt', label: 'Gantt', icon: 'chart-gantt' },
            { key: 'wbs', label: 'WBS', icon: 'file-tree' },
            { key: 'resources', label: 'Resources', icon: 'account-group' },
            { key: 'analytics', label: 'Analytics', icon: 'chart-box' },
            { key: 'calendar', label: 'Calendar', icon: 'calendar' },
            { key: 'reports', label: 'Reports', icon: 'file-chart' },
            { key: 'risks', label: 'Risks', icon: 'shield-alert', badge: risks.filter(r=>r.status==='Open').length },
            { key: 'issues', label: 'Issues', icon: 'bug', badge: issues.filter(i=>i.status==='Open').length },
            { key: 'milestones', label: 'Milestones', icon: 'flag-checkered' },
          ].map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, activeView === tab.key && styles.activeTab]}
              onPress={() => { setActiveView(tab.key); Animated.sequence([Animated.timing(scaleAnim,{toValue:0.95,duration:100,useNativeDriver:true}),Animated.timing(scaleAnim,{toValue:1,duration:100,useNativeDriver:true})]).start(); }}
              activeOpacity={0.8}
            >
              {activeView === tab.key && <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.activeTabGradient} />}
              <MaterialCommunityIcons name={tab.icon} size={20} color={activeView === tab.key ? '#FFFFFF' : '#6B7280'} />
              <Text style={[styles.tabText, activeView === tab.key && styles.activeTabText]}>{tab.label}</Text>
              {tab.badge > 0 && <View style={{position:'absolute',top:4,right:4,backgroundColor:'#EF4444',borderRadius:8,minWidth:16,paddingHorizontal:3,alignItems:'center'}}><Text style={{color:'#FFF',fontSize:9,fontWeight:'700'}}>{tab.badge}</Text></View>}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </LinearGradient>
    </Animated.View>
  );

  // ---------- WBS View ----------
  const renderWBSItem = ({ item }) => {
    if (!item || typeof item !== 'object') return null;
    const isExpanded = expandedWBS[item.id];
    return (
      <View key={item.id}>
        <TouchableOpacity
          style={[styles.wbsItem, { paddingLeft: 20 + (item.level || 0) * 20 }]}
          onPress={() => {
            if (item.children?.length) {
              setExpandedWBS(prev => ({ ...prev, [item.id]: !prev[item.id] }));
            }
          }}
          onLongPress={() => {
            Alert.alert('Activity Options', `Options for "${item.name}"`, [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Edit', onPress: () => { setEditingActivity(item); setShowEditActivityModal(true); } },
              { text: 'Delete', style: 'destructive', onPress: () => deleteActivity(item.id) },
            ]);
          }}
          activeOpacity={0.7}
        >
          <View style={styles.wbsItemContent}>
            <View style={styles.wbsLeft}>
              {item.children?.length > 0 && (
                <MaterialCommunityIcons
                  name={isExpanded ? 'chevron-down' : 'chevron-right'}
                  size={20}
                  color="#6B7280"
                  style={styles.wbsChevron}
                />
              )}
              <Text style={styles.wbsCode}>{item.wbs}</Text>
              <Text style={styles.wbsName} numberOfLines={1}>{item.name}</Text>
            </View>
            <View style={styles.wbsRight}>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
                <Text style={styles.statusText}>{item.progress}%</Text>
              </View>
            </View>
          </View>
        </TouchableOpacity>
        {isExpanded && item.children?.filter(child => child && typeof child === 'object').map(child => renderWBSItem({ item: child }))}
      </View>
    );
  };

  const renderWBSView = () => {
    const topLevelActivities = projectData.activities.filter(a => a && typeof a === 'object');
    return (
      <ScrollView style={styles.wbsContainer}>
        {topLevelActivities.map(activity => renderWBSItem({ item: activity }))}
      </ScrollView>
    );
  };

  // ---------- Resources View ----------
  const renderResourceItem = ({ item }) => {
    if (!item || typeof item !== 'object') return null;
    return (
      <TouchableOpacity
        key={item.id}
        style={styles.resourceCard}
        onLongPress={() => {
          Alert.alert('Resource Options', `Options for "${item.name}"`, [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Edit', onPress: () => { setSelectedResource(item); setShowEditResourceModal(true); } },
            { text: 'Delete', style: 'destructive', onPress: () => deleteResource(item.id) },
          ]);
        }}
        activeOpacity={0.8}
      >
        <Image source={{ uri: (item.avatar && item.avatar.startsWith('http')) ? item.avatar : undefined }} style={styles.resourceAvatarLarge} />
        <View style={styles.resourceInfo}>
          <Text style={styles.resourceName}>{item.name}</Text>
          <Text style={styles.resourceRole}>{item.role}</Text>
          <View style={styles.resourceDetails}>
            <Text style={styles.resourceRate}>{formatCurrency(item.rate)}/hr</Text>
            <Text style={styles.resourceUtilization}>Utilization: {item.utilization}%</Text>
          </View>
          <View style={styles.skillsContainer}>
            {item.skills.map((skill, idx) => (
              <View key={idx} style={styles.skillTag}><Text style={styles.skillText}>{skill}</Text></View>
            ))}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderResourcesView = () => (
    <FlatList
      data={projectData.resources.filter(r => r && typeof r === 'object')}
      keyExtractor={item => item?.id?.toString() ?? Math.random().toString()}
      renderItem={renderResourceItem}
      contentContainerStyle={styles.resourcesList}
    />
  );

  // ---------- Dependency Lines for Gantt ----------
  const renderDependencyLines = (activities) => {
    const lines = [];
    activities.forEach(activity => {
      if (!activity.predecessors || activity.predecessors.length === 0) return;
      const succPos = calculateActivityPosition(activity);
      const succTop = activities.findIndex(a => a.id === activity.id) * 100 + 50; // vertical center of the bar row

      activity.predecessors.forEach(pred => {
        const predActivity = activities.find(a => a.id === pred.id);
        if (!predActivity) return;
        const predPos = calculateActivityPosition(predActivity);
        const predTop = activities.findIndex(a => a.id === pred.id) * 100 + 50;

        // Calculate start and end points
        let startX = predPos.left + predPos.width; // end of predecessor bar
        let startY = predTop;
        let endX = succPos.left; // start of successor bar
        let endY = succTop;

        // For simplicity, we draw a horizontal line from end of pred to start of succ at the vertical center
        // In a real app, you might draw arrows and curves
        lines.push(
          <View
            key={`dep-${pred.id}-${activity.id}`}
            style={[
              styles.dependencyLine,
              {
                left: startX,
                top: startY - 1, // center the line vertically
                width: endX - startX,
              },
            ]}
          >
            {/* Simple arrow at the end */}
            <View style={[styles.dependencyArrow, { right: -6 }]} />
          </View>
        );
      });
    });
    return lines;
  };

  // ---------- Gantt View ----------
  const renderGanttView = () => {
    const activitiesToShow = filteredActivities.filter(a => a && typeof a === 'object');

    return (
      <Animated.View style={[styles.ganttContainer, { opacity: fadeAnim }]}>
        <View style={styles.ganttContent}>
          {/* Activity List (Left Side) */}
          <View style={styles.activityListContainer}>
            <LinearGradient colors={['#F8FAFC', '#E2E8F0']} style={styles.activityListHeader}>
              <View style={styles.headerRow}>
                <Text style={styles.headerText}>Activity</Text>
                <Text style={styles.headerText}>Duration</Text>
                <Text style={styles.headerText}>Progress</Text>
              </View>
            </LinearGradient>
            <FlatList
              data={activitiesToShow}
              keyExtractor={item => item?.id?.toString() ?? Math.random().toString()}
              renderItem={renderActivityItem}
              showsVerticalScrollIndicator
              onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], { useNativeDriver: false })}
              scrollEventThrottle={16}
              initialNumToRender={20}
              maxToRenderPerBatch={20}
              windowSize={10}
            />
          </View>

          {/* Timeline and Gantt Bars (Right Side) */}
          <View style={styles.ganttChartContainer}>
            <LinearGradient colors={['#F8FAFC', '#E2E8F0']} style={styles.timelineHeader}>
              <ScrollView horizontal showsHorizontalScrollIndicator style={styles.timelineScrollView} contentContainerStyle={{ width: Math.max(timelineWidth, screenWidth - 300) }}>
                <View style={styles.timelineHeaderContent}>
                  {timeline.map((period, index) => (
                    <View key={index} style={[styles.timelinePeriod, { width: period.width }]}>
                      <Text style={styles.timelineText}>{period.label}</Text>
                      <Text style={styles.timelineSubtext}>
                        {timelineScale === 'months' ? period.startDate.getFullYear() :
                         timelineScale === 'weeks' ? format(period.startDate, 'MMM') :
                         format(period.startDate, 'EEE')}
                      </Text>
                    </View>
                  ))}
                </View>
              </ScrollView>
            </LinearGradient>

            <View style={styles.ganttBarsContainer}>
              <ScrollView horizontal showsHorizontalScrollIndicator style={styles.ganttScrollView} contentContainerStyle={{ width: Math.max(timelineWidth, screenWidth - 300) }}>
                <View style={[styles.ganttBarsContent, { height: Math.max(activitiesToShow.length * 100, 400) }]}>
                  {/* Grid lines */}
                  {timeline.map((period, index) => (
                    <View key={`grid-${index}`} style={[styles.gridLine, { left: timeline.slice(0, index + 1).reduce((sum, p) => sum + p.width, 0) - 1 }]} />
                  ))}

                  {/* Dependency lines */}
                  {renderDependencyLines(activitiesToShow)}

                  {/* Gantt bars */}
                  {activitiesToShow.map((activity, index) => {
                    const pos = calculateActivityPosition(activity);
                    return (
                      <Animated.View key={activity.id} style={[styles.ganttBarRow, { top: index * 100, opacity: fadeAnim }]}>
                        <TouchableOpacity
                          style={[styles.ganttBar, { left: pos.left, width: Math.max(pos.width, 30) }]}
                          onPress={() => {
                            setEditingActivity(activity);
                            setEditActivity({
                              name: activity.name,
                              description: activity.description || '',
                              duration: activity.duration.toString(),
                              startDate: new Date(activity.startDate),
                              endDate: new Date(activity.endDate),
                              progress: activity.progress,
                              status: activity.status,
                              isCritical: activity.isCritical || false,
                              priority: activity.priority,
                              resources: activity.resources || [],
                              cost: activity.cost ? activity.cost.toString() : '',
                              predecessors: activity.predecessors || [],
                            });
                            setShowEditActivityModal(true);
                          }}
                          onLongPress={() => {
                            Alert.alert('Quick Update', `Update "${activity.name}":`, [
                              { text: 'Cancel' },
                              { text: '0%', onPress: () => updateActivity(activity.id, { progress: 0 }) },
                              { text: '25%', onPress: () => updateActivity(activity.id, { progress: 25 }) },
                              { text: '50%', onPress: () => updateActivity(activity.id, { progress: 50 }) },
                              { text: '75%', onPress: () => updateActivity(activity.id, { progress: 75 }) },
                              { text: '100%', onPress: () => updateActivity(activity.id, { progress: 100, status: 'Completed' }) },
                            ]);
                          }}
                          activeOpacity={0.8}
                        >
                          <LinearGradient colors={activity.isCritical ? ['#EF4444', '#DC2626'] : ['#3B82F6', '#1D4ED8']} style={styles.ganttBarGradient}>
                            <Animated.View style={[styles.ganttBarProgress, { width: `${activity.progress}%`, backgroundColor: 'rgba(255,255,255,0.3)' }]} />
                            <View style={styles.ganttBarContent}>
                              <Text style={styles.ganttBarText} numberOfLines={1}>{activity.name}</Text>
                              <Text style={styles.ganttBarProgress}>{activity.progress}%</Text>
                            </View>
                            {activity.isCritical && (
                              <View style={styles.criticalMarker}>
                                <MaterialCommunityIcons name="alert" size={12} color="#FFFFFF" />
                              </View>
                            )}
                          </LinearGradient>
                        </TouchableOpacity>
                        {activity.progress === 100 && (
                          <Animated.View style={[styles.milestoneMarker, { left: pos.left + Math.max(pos.width, 30), opacity: fadeAnim }]}>
                            <MaterialCommunityIcons name="flag-checkered" size={16} color="#10B981" />
                          </Animated.View>
                        )}
                      </Animated.View>
                    );
                  })}

                  {/* Today line */}
                  {(() => {
                    const today = new Date();
                    const projStart = new Date(projectData.startDate);
                    const projEnd = new Date(projectData.endDate);
                    if (isAfter(today, projStart) && isBefore(today, projEnd)) {
                      const totalDays = differenceInDays(projEnd, projStart);
                      const daysFromStart = differenceInDays(today, projStart);
                      const pos = (daysFromStart / totalDays) * timelineWidth;
                      return (
                        <Animated.View style={[styles.todayLine, { left: pos, opacity: fadeAnim }]}>
                          <LinearGradient colors={['#EF4444', '#DC2626']} style={styles.todayLineGradient} />
                          <View style={styles.todayMarker}><Text style={styles.todayText}>TODAY</Text></View>
                        </Animated.View>
                      );
                    }
                    return null;
                  })()}
                </View>
              </ScrollView>
            </View>
          </View>
        </View>
      </Animated.View>
    );
  };

  // Activity item renderer for Gantt list (left side)
  const renderActivityItem = ({ item }) => {
    if (!item || typeof item !== 'object') return null;
    return (
      <Animated.View
        key={item.id}
        style={[
          styles.activityRow,
          selectedActivity?.id === item.id && styles.selectedActivityRow,
          { paddingLeft: 15 + (item.level || 0) * 25 },
        ]}
        accessible
        accessibilityLabel={`Activity ${item.wbs}: ${item.name}, progress ${item.progress}%`}
      >
        <TouchableOpacity
          onPress={() => {
            setEditingActivity(item);
            setEditActivity({
              name: item.name,
              description: item.description || '',
              duration: item.duration.toString(),
              startDate: new Date(item.startDate),
              endDate: new Date(item.endDate),
              progress: item.progress,
              status: item.status,
              isCritical: item.isCritical || false,
              priority: item.priority,
              resources: item.resources || [],
              cost: item.cost ? item.cost.toString() : '',
              predecessors: item.predecessors || [],
            });
            setShowEditActivityModal(true);
          }}
          onLongPress={() => {
            Alert.alert('Activity Options', `Options for "${item.name}":`, [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Update Progress', onPress: () => {
                Alert.alert('Update Progress', `Current: ${item.progress}%`, [
                  { text: 'Cancel', style: 'cancel' },
                  { text: '0%', onPress: () => updateActivity(item.id, { progress: 0 }) },
                  { text: '25%', onPress: () => updateActivity(item.id, { progress: 25 }) },
                  { text: '50%', onPress: () => updateActivity(item.id, { progress: 50 }) },
                  { text: '75%', onPress: () => updateActivity(item.id, { progress: 75 }) },
                  { text: '100%', onPress: () => updateActivity(item.id, { progress: 100, status: 'Completed' }) },
                ]);
              } },
              { text: 'Delete', style: 'destructive', onPress: () => deleteActivity(item.id) },
            ]);
          }}
          style={styles.activityTouchable}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityHint="Double tap to edit, long press for options"
        >
          <View style={styles.activityInfo}>
            <View style={styles.activityNameContainer}>
              <View style={styles.wbsContainer}>
                <Text style={styles.activityWBS}>{item.wbs}</Text>
                {item.isCritical && (
                  <View style={styles.criticalIndicator}>
                    <MaterialCommunityIcons name="alert" size={12} color="#EF4444" />
                  </View>
                )}
              </View>
              <Text style={styles.activityName} numberOfLines={2}>{item.name}</Text>
              {item.tags?.length > 0 && (
                <View style={styles.tagsContainer}>
                  {item.tags.slice(0, 2).map((tag, i) => (
                    <View key={i} style={styles.tag}><Text style={styles.tagText}>{tag}</Text></View>
                  ))}
                </View>
              )}
            </View>
            <View style={styles.durationContainer}>
              <Text style={styles.activityDuration}>{item.duration}d</Text>
              <Text style={styles.dateRange}>{formatDate(item.startDate)}</Text>
            </View>
            <View style={styles.progressContainer}>
              <View style={styles.progressBar}>
                <View style={[styles.progressFill, { width: `${item.progress}%`, backgroundColor: getProgressColor(item.progress) }]} />
              </View>
              <Text style={styles.progressText}>{item.progress}%</Text>
            </View>
          </View>
          <View style={styles.activityMeta}>
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
              <Text style={styles.statusText}>{item.status}</Text>
            </View>
            <View style={[styles.priorityBadge, { borderColor: getPriorityColor(item.priority) }]}>
              <Text style={[styles.priorityText, { color: getPriorityColor(item.priority) }]}>{item.priority}</Text>
            </View>
            {item.resources?.length > 0 && (
              <View style={styles.resourcesContainer}>
                {item.resources.slice(0, 3).map((resId, i) => {
                  const res = projectData.resources.find(r => r && r.id === resId);
                  return res ? (
                    <View key={i} style={styles.resourceAvatar} accessible accessibilityLabel={res.name}>
                      <Text style={styles.resourceInitials}>{res.name.split(' ').map(n => n[0]).join('')}</Text>
                    </View>
                  ) : null;
                })}
                {item.resources.length > 3 && (
                  <View style={[styles.resourceAvatar, styles.moreResources]}>
                    <Text style={styles.resourceInitials}>+{item.resources.length - 3}</Text>
                  </View>
                )}
              </View>
            )}
            {item.predecessors?.length > 0 && (
              <View style={styles.dependencyBadge}>
                <MaterialCommunityIcons name="link" size={12} color="#6B7280" />
                <Text style={styles.dependencyText}>{item.predecessors.length}</Text>
              </View>
            )}
          </View>
        </TouchableOpacity>
      </Animated.View>
    );
  };

  // ---------- Modals ----------

  const renderAddActivityModal = () => (
    <Modal visible={showAddActivityModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalContainer}>
        <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowAddActivityModal(false)} style={styles.modalCloseButton} activeOpacity={0.8} accessibilityLabel="Close">
            <MaterialCommunityIcons name="close" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Add New Activity</Text>
          <TouchableOpacity onPress={addActivity} style={styles.modalSaveButton} activeOpacity={0.8} accessibilityLabel="Save activity">
            <Text style={styles.modalSaveText}>Save</Text>
          </TouchableOpacity>
        </LinearGradient>
        <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Basic Information</Text>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Activity Name *</Text>
              <TextInput style={styles.formInput} value={newActivity.name} onChangeText={(text) => setNewActivity(prev => ({ ...prev, name: text }))} placeholder="Enter activity name" placeholderTextColor="#9CA3AF" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Description</Text>
              <TextInput style={[styles.formInput, styles.formTextArea]} value={newActivity.description} onChangeText={(text) => setNewActivity(prev => ({ ...prev, description: text }))} placeholder="Enter activity description" placeholderTextColor="#9CA3AF" multiline numberOfLines={3} />
            </View>
          </View>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Schedule & Duration</Text>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Duration (days) *</Text>
                <TextInput style={styles.formInput} value={newActivity.duration} onChangeText={(text) => setNewActivity(prev => ({ ...prev, duration: text }))} placeholder="0" placeholderTextColor="#9CA3AF" keyboardType="numeric" />
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Priority</Text>
                <TouchableOpacity style={styles.pickerButton} onPress={() => {
                  Alert.alert('Select Priority', 'Choose priority:', ['High','Medium','Low'].map(p => ({ text: p, onPress: () => setNewActivity(prev => ({ ...prev, priority: p })) })));
                }} activeOpacity={0.8}>
                  <Text style={styles.pickerButtonText}>{newActivity.priority}</Text>
                  <MaterialCommunityIcons name="chevron-down" size={20} color="#6B7280" />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Start Date</Text>
              <TouchableOpacity style={styles.dateButton} onPress={() => { setDatePickerMode('start'); setShowDatePicker(true); }} activeOpacity={0.8}>
                <MaterialCommunityIcons name="calendar" size={20} color="#6B7280" />
                <Text style={styles.dateButtonText}>{formatDate(newActivity.startDate)}</Text>
                <MaterialCommunityIcons name="chevron-right" size={16} color="#6B7280" />
              </TouchableOpacity>
            </View>
          </View>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Status & Cost</Text>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Status</Text>
                <TouchableOpacity style={styles.pickerButton} onPress={() => {
                  Alert.alert('Select Status', 'Choose status:', ['Not Started','In Progress','Completed','On Hold','Cancelled'].map(s => ({ text: s, onPress: () => setNewActivity(prev => ({ ...prev, status: s })) })));
                }} activeOpacity={0.8}>
                  <View style={[styles.statusIndicator, { backgroundColor: getStatusColor(newActivity.status) }]} />
                  <Text style={styles.pickerButtonText}>{newActivity.status}</Text>
                  <MaterialCommunityIcons name="chevron-down" size={20} color="#6B7280" />
                </TouchableOpacity>
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Cost (£)</Text>
                <TextInput style={styles.formInput} value={newActivity.cost} onChangeText={(text) => setNewActivity(prev => ({ ...prev, cost: text }))} placeholder="0" placeholderTextColor="#9CA3AF" keyboardType="numeric" />
              </View>
            </View>
          </View>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Options</Text>
            <View style={styles.switchContainer}>
              <View style={styles.switchInfo}>
                <Text style={styles.switchLabel}>Critical Activity</Text>
                <Text style={styles.switchDescription}>Mark as critical path activity</Text>
              </View>
              <Switch value={newActivity.isCritical} onValueChange={(value) => setNewActivity(prev => ({ ...prev, isCritical: value }))} trackColor={{ false: '#D1D5DB', true: '#EF4444' }} thumbColor={newActivity.isCritical ? '#FFFFFF' : '#9CA3AF'} ios_backgroundColor="#D1D5DB" />
            </View>
          </View>
        </ScrollView>
        {showDatePicker && (
          <DateTimePicker
            value={datePickerMode === 'start' ? newActivity.startDate : newActivity.endDate}
            mode="date"
            display="default"
            onChange={(event, selectedDate) => {
              setShowDatePicker(false);
              if (selectedDate) {
                if (datePickerMode === 'start') {
                  setNewActivity(prev => ({ ...prev, startDate: selectedDate }));
                } else {
                  setNewActivity(prev => ({ ...prev, endDate: selectedDate }));
                }
              }
            }}
          />
        )}
      </SafeAreaView>
    </Modal>
  );

  const renderDependencyModal = () => (
    <Modal visible={showDependencyModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalContainer}>
        <LinearGradient colors={['#8B5CF6', '#6D28D9']} style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowDependencyModal(false)} style={styles.modalCloseButton} activeOpacity={0.8} accessibilityLabel="Close">
            <MaterialCommunityIcons name="close" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Add Dependency</Text>
          <TouchableOpacity onPress={addDependency} style={styles.modalSaveButton} activeOpacity={0.8} accessibilityLabel="Add dependency">
            <Text style={styles.modalSaveText}>Add</Text>
          </TouchableOpacity>
        </LinearGradient>
        <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Predecessor Activity</Text>
            <FlatList
              data={flatActivities.filter(a => a.id !== editingActivity?.id)}
              keyExtractor={item => item.id.toString()}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[styles.dependencyItem, newDependency.id === item.id && styles.dependencyItemSelected]}
                  onPress={() => setNewDependency(prev => ({ ...prev, id: item.id }))}
                >
                  <Text style={styles.dependencyItemText}>{item.wbs} - {item.name}</Text>
                  {newDependency.id === item.id && (
                    <MaterialCommunityIcons name="check" size={20} color="#3B82F6" />
                  )}
                </TouchableOpacity>
              )}
              scrollEnabled={false}
            />
          </View>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Dependency Type</Text>
            <View style={styles.typeButtons}>
              {['FS', 'SS', 'FF', 'SF'].map(type => (
                <TouchableOpacity
                  key={type}
                  style={[styles.typeButton, newDependency.type === type && styles.typeButtonSelected]}
                  onPress={() => setNewDependency(prev => ({ ...prev, type }))}
                >
                  <Text style={[styles.typeButtonText, newDependency.type === type && styles.typeButtonTextSelected]}>{type}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Lag (days)</Text>
            <TextInput
              style={styles.formInput}
              value={newDependency.lag.toString()}
              onChangeText={(text) => setNewDependency(prev => ({ ...prev, lag: parseInt(text) || 0 }))}
              placeholder="0"
              placeholderTextColor="#9CA3AF"
              keyboardType="numeric"
            />
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderEditActivityModal = () => (
    <Modal visible={showEditActivityModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalContainer}>
        <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowEditActivityModal(false)} style={styles.modalCloseButton} activeOpacity={0.8} accessibilityLabel="Close">
            <MaterialCommunityIcons name="close" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Edit Activity</Text>
          <TouchableOpacity onPress={handleUpdateActivity} style={styles.modalSaveButton} activeOpacity={0.8} accessibilityLabel="Save changes">
            <Text style={styles.modalSaveText}>Save</Text>
          </TouchableOpacity>
        </LinearGradient>
        <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Basic Information</Text>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Activity Name *</Text>
              <TextInput
                style={styles.formInput}
                value={editActivity.name}
                onChangeText={(text) => setEditActivity(prev => ({ ...prev, name: text }))}
                placeholder="Enter activity name"
                placeholderTextColor="#9CA3AF"
              />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Description</Text>
              <TextInput
                style={[styles.formInput, styles.formTextArea]}
                value={editActivity.description}
                onChangeText={(text) => setEditActivity(prev => ({ ...prev, description: text }))}
                placeholder="Enter activity description"
                placeholderTextColor="#9CA3AF"
                multiline
                numberOfLines={3}
              />
            </View>
          </View>

          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Schedule & Duration</Text>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Duration (days) *</Text>
                <TextInput
                  style={styles.formInput}
                  value={editActivity.duration}
                  onChangeText={(text) => setEditActivity(prev => ({ ...prev, duration: text }))}
                  placeholder="0"
                  placeholderTextColor="#9CA3AF"
                  keyboardType="numeric"
                />
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Priority</Text>
                <TouchableOpacity
                  style={styles.pickerButton}
                  onPress={() => {
                    Alert.alert('Select Priority', 'Choose priority:', ['High','Medium','Low'].map(p => ({
                      text: p,
                      onPress: () => setEditActivity(prev => ({ ...prev, priority: p }))
                    })));
                  }}
                  activeOpacity={0.8}
                >
                  <Text style={styles.pickerButtonText}>{editActivity.priority}</Text>
                  <MaterialCommunityIcons name="chevron-down" size={20} color="#6B7280" />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Start Date</Text>
              <TouchableOpacity
                style={styles.dateButton}
                onPress={() => { setEditDatePickerMode('start'); setShowEditDatePicker(true); }}
                activeOpacity={0.8}
              >
                <MaterialCommunityIcons name="calendar" size={20} color="#6B7280" />
                <Text style={styles.dateButtonText}>{formatDate(editActivity.startDate)}</Text>
                <MaterialCommunityIcons name="chevron-right" size={16} color="#6B7280" />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Status & Progress</Text>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Progress (%)</Text>
                <TextInput
                  style={styles.formInput}
                  value={editActivity.progress.toString()}
                  onChangeText={(text) => setEditActivity(prev => ({ ...prev, progress: parseInt(text) || 0 }))}
                  placeholder="0"
                  placeholderTextColor="#9CA3AF"
                  keyboardType="numeric"
                />
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Status</Text>
                <TouchableOpacity
                  style={styles.pickerButton}
                  onPress={() => {
                    Alert.alert('Select Status', 'Choose status:', ['Not Started','In Progress','Completed','On Hold','Cancelled'].map(s => ({
                      text: s,
                      onPress: () => setEditActivity(prev => ({ ...prev, status: s }))
                    })));
                  }}
                  activeOpacity={0.8}
                >
                  <View style={[styles.statusIndicator, { backgroundColor: getStatusColor(editActivity.status) }]} />
                  <Text style={styles.pickerButtonText}>{editActivity.status}</Text>
                  <MaterialCommunityIcons name="chevron-down" size={20} color="#6B7280" />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Cost (£)</Text>
                <TextInput
                  style={styles.formInput}
                  value={editActivity.cost}
                  onChangeText={(text) => setEditActivity(prev => ({ ...prev, cost: text }))}
                  placeholder="0"
                  placeholderTextColor="#9CA3AF"
                  keyboardType="numeric"
                />
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]} />
            </View>
          </View>

          {/* Dependencies Section */}
          <View style={styles.formSection}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Dependencies</Text>
              <TouchableOpacity
                style={styles.addButton}
                onPress={() => {
                  setNewDependency({ id: null, type: 'FS', lag: 0 });
                  setShowDependencyModal(true);
                }}
              >
                <MaterialCommunityIcons name="plus" size={20} color="#3B82F6" />
                <Text style={styles.addButtonText}>Add</Text>
              </TouchableOpacity>
            </View>
            {editActivity.predecessors?.length > 0 ? (
              editActivity.predecessors.map((pred, index) => {
                const predActivity = flatActivities.find(a => a.id === pred.id);
                return (
                  <View key={index} style={styles.dependencyRow}>
                    <View style={styles.dependencyInfo}>
                      <Text style={styles.dependencyName}>{predActivity?.wbs} - {predActivity?.name}</Text>
                      <Text style={styles.dependencyType}>{pred.type} +{pred.lag}d</Text>
                    </View>
                    <TouchableOpacity onPress={() => removeDependency(index)}>
                      <MaterialCommunityIcons name="close" size={20} color="#EF4444" />
                    </TouchableOpacity>
                  </View>
                );
              })
            ) : (
              <Text style={styles.placeholderText}>No dependencies</Text>
            )}
          </View>

          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Options</Text>
            <View style={styles.switchContainer}>
              <View style={styles.switchInfo}>
                <Text style={styles.switchLabel}>Critical Activity</Text>
                <Text style={styles.switchDescription}>Mark as critical path activity</Text>
              </View>
              <Switch
                value={editActivity.isCritical}
                onValueChange={(value) => setEditActivity(prev => ({ ...prev, isCritical: value }))}
                trackColor={{ false: '#D1D5DB', true: '#EF4444' }}
                thumbColor={editActivity.isCritical ? '#FFFFFF' : '#9CA3AF'}
                ios_backgroundColor="#D1D5DB"
              />
            </View>
          </View>
        </ScrollView>
        {showEditDatePicker && (
          <DateTimePicker
            value={editDatePickerMode === 'start' ? editActivity.startDate : editActivity.endDate}
            mode="date"
            display="default"
            onChange={(event, selectedDate) => {
              setShowEditDatePicker(false);
              if (selectedDate) {
                if (editDatePickerMode === 'start') {
                  setEditActivity(prev => ({ ...prev, startDate: selectedDate }));
                } else {
                  setEditActivity(prev => ({ ...prev, endDate: selectedDate }));
                }
              }
            }}
          />
        )}
      </SafeAreaView>
    </Modal>
  );

  const renderAddResourceModal = () => (
    <Modal visible={showAddResourceModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalContainer}>
        <LinearGradient colors={['#10B981', '#059669']} style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowAddResourceModal(false)} style={styles.modalCloseButton} activeOpacity={0.8} accessibilityLabel="Close">
            <MaterialCommunityIcons name="close" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Add New Resource</Text>
          <TouchableOpacity onPress={addResource} style={styles.modalSaveButton} activeOpacity={0.8} accessibilityLabel="Save resource">
            <Text style={styles.modalSaveText}>Save</Text>
          </TouchableOpacity>
        </LinearGradient>
        <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Resource Information</Text>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Full Name *</Text>
              <TextInput style={styles.formInput} value={newResource.name} onChangeText={(text) => setNewResource(prev => ({ ...prev, name: text }))} placeholder="Enter full name" placeholderTextColor="#9CA3AF" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Role/Position *</Text>
              <TextInput style={styles.formInput} value={newResource.role} onChangeText={(text) => setNewResource(prev => ({ ...prev, role: text }))} placeholder="e.g., Senior Developer, Project Manager" placeholderTextColor="#9CA3AF" />
            </View>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Hourly Rate (£)</Text>
                <TextInput style={styles.formInput} value={newResource.rate} onChangeText={(text) => setNewResource(prev => ({ ...prev, rate: text }))} placeholder="0" placeholderTextColor="#9CA3AF" keyboardType="numeric" />
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Utilization (%)</Text>
                <TextInput style={styles.formInput} value={newResource.utilization.toString()} onChangeText={(text) => setNewResource(prev => ({ ...prev, utilization: parseInt(text) || 0 }))} placeholder="0" placeholderTextColor="#9CA3AF" keyboardType="numeric" />
              </View>
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Skills (comma separated)</Text>
              <TextInput style={styles.formInput} value={newResource.skills} onChangeText={(text) => setNewResource(prev => ({ ...prev, skills: text }))} placeholder="e.g., React Native, Node.js, Project Management" placeholderTextColor="#9CA3AF" />
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
const fetchProjectData = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.projects);
    if (!response.ok) throw new Error('API failed');
    const data = await response.json();
    // Process data
  } catch (error) {
    console.log('📱 Network unavailable, using local data');
    // Use default/cached data instead
  }
};

  const renderEditResourceModal = () => (
    <Modal visible={showEditResourceModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalContainer}>
        <LinearGradient colors={['#10B981', '#059669']} style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowEditResourceModal(false)} style={styles.modalCloseButton} activeOpacity={0.8} accessibilityLabel="Close">
            <MaterialCommunityIcons name="close" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Edit Resource</Text>
          <TouchableOpacity onPress={() => {
            if (selectedResource) {
              updateResource(selectedResource.id, newResource);
              setShowEditResourceModal(false);
            }
          }} style={styles.modalSaveButton} activeOpacity={0.8} accessibilityLabel="Save">
            <Text style={styles.modalSaveText}>Save</Text>
          </TouchableOpacity>
        </LinearGradient>
        <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Resource Information</Text>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Full Name *</Text>
              <TextInput
                style={styles.formInput}
                value={newResource.name}
                onChangeText={(text) => setNewResource(prev => ({ ...prev, name: text }))}
                placeholder="Enter full name"
                placeholderTextColor="#9CA3AF"
              />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Role/Position *</Text>
              <TextInput
                style={styles.formInput}
                value={newResource.role}
                onChangeText={(text) => setNewResource(prev => ({ ...prev, role: text }))}
                placeholder="e.g., Senior Developer, Project Manager"
                placeholderTextColor="#9CA3AF"
              />
            </View>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Hourly Rate (£)</Text>
                <TextInput
                  style={styles.formInput}
                  value={newResource.rate}
                  onChangeText={(text) => setNewResource(prev => ({ ...prev, rate: text }))}
                  placeholder="0"
                  placeholderTextColor="#9CA3AF"
                  keyboardType="numeric"
                />
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Utilization (%)</Text>
                <TextInput
                  style={styles.formInput}
                  value={newResource.utilization.toString()}
                  onChangeText={(text) => setNewResource(prev => ({ ...prev, utilization: parseInt(text) || 0 }))}
                  placeholder="0"
                  placeholderTextColor="#9CA3AF"
                  keyboardType="numeric"
                />
              </View>
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Skills (comma separated)</Text>
              <TextInput
                style={styles.formInput}
                value={newResource.skills}
                onChangeText={(text) => setNewResource(prev => ({ ...prev, skills: text }))}
                placeholder="e.g., React Native, Node.js, Project Management"
                placeholderTextColor="#9CA3AF"
              />
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // Unified project modal (used for both New and Edit)
  const renderProjectModal = (isEdit) => (
    <Modal visible={isEdit ? showEditProjectModal : showNewProjectModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalContainer}>
        <LinearGradient colors={['#10B981', '#059669']} style={styles.modalHeader}>
          <TouchableOpacity onPress={() => isEdit ? setShowEditProjectModal(false) : setShowNewProjectModal(false)} style={styles.modalCloseButton} activeOpacity={0.8} accessibilityLabel="Cancel">
            <MaterialCommunityIcons name="close" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>{isEdit ? 'Edit Project' : 'New Project'}</Text>
          <TouchableOpacity onPress={isEdit ? updateProject : createNewProject} style={styles.modalSaveButton} activeOpacity={0.8} accessibilityLabel={isEdit ? 'Save changes' : 'Create project'}>
            <Text style={styles.modalSaveText}>{isEdit ? 'Save' : 'Create'}</Text>
          </TouchableOpacity>
        </LinearGradient>
        <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Basic Information</Text>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Project Name *</Text>
              <TextInput
                style={styles.formInput}
                value={projectForm.name}
                onChangeText={(text) => setProjectForm(prev => ({ ...prev, name: text }))}
                placeholder="Enter project name"
                placeholderTextColor="#9CA3AF"
              />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Description</Text>
              <TextInput
                style={[styles.formInput, styles.formTextArea]}
                value={projectForm.description}
                onChangeText={(text) => setProjectForm(prev => ({ ...prev, description: text }))}
                placeholder="Enter project description"
                placeholderTextColor="#9CA3AF"
                multiline
                numberOfLines={3}
              />
            </View>
          </View>

          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Schedule & Status</Text>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Start Date</Text>
                <TouchableOpacity
                  style={styles.dateButton}
                  onPress={() => {
                    setProjectDatePickerMode('start');
                    setShowProjectDatePicker(true);
                  }}
                  activeOpacity={0.8}
                >
                  <MaterialCommunityIcons name="calendar" size={20} color="#6B7280" />
                  <Text style={styles.dateButtonText}>{formatDate(projectForm.startDate)}</Text>
                  <MaterialCommunityIcons name="chevron-right" size={16} color="#6B7280" />
                </TouchableOpacity>
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>End Date</Text>
                <TouchableOpacity
                  style={styles.dateButton}
                  onPress={() => {
                    setProjectDatePickerMode('end');
                    setShowProjectDatePicker(true);
                  }}
                  activeOpacity={0.8}
                >
                  <MaterialCommunityIcons name="calendar" size={20} color="#6B7280" />
                  <Text style={styles.dateButtonText}>{formatDate(projectForm.endDate)}</Text>
                  <MaterialCommunityIcons name="chevron-right" size={16} color="#6B7280" />
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.formRow}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Status</Text>
                <TouchableOpacity
                  style={styles.pickerButton}
                  onPress={() => {
                    Alert.alert(
                      'Select Status',
                      'Choose project status:',
                      ['Planning', 'In Progress', 'On Hold', 'Completed', 'Cancelled'].map(s => ({
                        text: s,
                        onPress: () => setProjectForm(prev => ({ ...prev, status: s })),
                      }))
                    );
                  }}
                  activeOpacity={0.8}
                >
                  <View style={[styles.statusIndicator, { backgroundColor: getStatusColor(projectForm.status) }]} />
                  <Text style={styles.pickerButtonText}>{projectForm.status}</Text>
                  <MaterialCommunityIcons name="chevron-down" size={20} color="#6B7280" />
                </TouchableOpacity>
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Priority</Text>
                <TouchableOpacity
                  style={styles.pickerButton}
                  onPress={() => {
                    Alert.alert(
                      'Select Priority',
                      'Choose project priority:',
                      ['High', 'Medium', 'Low'].map(p => ({
                        text: p,
                        onPress: () => setProjectForm(prev => ({ ...prev, priority: p })),
                      }))
                    );
                  }}
                  activeOpacity={0.8}
                >
                  <Text style={styles.pickerButtonText}>{projectForm.priority}</Text>
                  <MaterialCommunityIcons name="chevron-down" size={20} color="#6B7280" />
                </TouchableOpacity>
              </View>
            </View>
          </View>

          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Budget & Team</Text>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Budget (£)</Text>
                <TextInput
                  style={styles.formInput}
                  value={projectForm.budget}
                  onChangeText={(text) => setProjectForm(prev => ({ ...prev, budget: text }))}
                  placeholder="0"
                  placeholderTextColor="#9CA3AF"
                  keyboardType="numeric"
                />
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.formLabel}>Manager</Text>
                <TextInput
                  style={styles.formInput}
                  value={projectForm.manager}
                  onChangeText={(text) => setProjectForm(prev => ({ ...prev, manager: text }))}
                  placeholder="Project manager"
                  placeholderTextColor="#9CA3AF"
                />
              </View>
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Client</Text>
              <TextInput
                style={styles.formInput}
                value={projectForm.client}
                onChangeText={(text) => setProjectForm(prev => ({ ...prev, client: text }))}
                placeholder="Client name"
                placeholderTextColor="#9CA3AF"
              />
            </View>
          </View>
        </ScrollView>

        {showProjectDatePicker && (
          <DateTimePicker
            value={projectDatePickerMode === 'start' ? projectForm.startDate : projectForm.endDate}
            mode="date"
            display="default"
            onChange={(event, selectedDate) => {
              setShowProjectDatePicker(false);
              if (selectedDate) {
                if (projectDatePickerMode === 'start') {
                  setProjectForm(prev => ({ ...prev, startDate: selectedDate }));
                } else {
                  setProjectForm(prev => ({ ...prev, endDate: selectedDate }));
                }
              }
            }}
          />
        )}
      </SafeAreaView>
    </Modal>
  );

  // Loading screen
  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <LinearGradient colors={['#1E3A8A', '#3B82F6', '#60A5FA']} style={styles.loadingGradient}>
          <Animated.View style={[styles.loadingContent, { opacity: fadeAnim, transform: [{ scale: scaleAnim }] }]}>
            <Animated.View style={{ transform: [{ rotate: rotateAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] }) }] }}>
              <MaterialCommunityIcons name="chart-gantt" size={64} color="#FFFFFF" />
            </Animated.View>
            <Text style={styles.loadingTitle}>Project Planning</Text>
            <Text style={styles.loadingSubtitle}>Loading your project data...</Text>
            <View style={styles.loadingProgress}>
              <Animated.View style={[styles.loadingProgressBar, { width: progressAnim.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }) }]} />
            </View>
          </Animated.View>
        </LinearGradient>
      </SafeAreaView>
    );
  }

  // ══════════════════════ DASHBOARD ══════════════════════
  const renderDashboardView = () => {
    const evm=calcEVM(),health=calcHealth(),today=new Date();
    const fa=(acts)=>{let r=[];if(!acts)return r;acts.forEach(a=>{r.push(a);if(a.children)r=r.concat(fa(a.children));});return r;};
    const allActs=fa(projectData.activities);
    const overallProg=allActs.length?Math.round(allActs.reduce((s,a)=>s+a.progress,0)/allActs.length):0;
    const overdue=allActs.filter(a=>a.status!=='Completed'&&isBefore(new Date(a.endDate),today));
    const fmtC=(v)=>new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format(v||0);
    const fmtD=(d)=>{if(!d)return'N/A';try{const dt=d instanceof Date?d:parseISO(d);return format(dt,'dd/MM/yyyy');}catch{return'N/A';}};
    return (
      <ScrollView style={{flex:1,padding:12}} showsVerticalScrollIndicator={false}>
        <View style={[xstyles.healthBanner,{borderColor:health.color,backgroundColor:health.color+'15'}]}>
          <View style={[xstyles.healthBadge,{backgroundColor:health.color}]}><Text style={xstyles.healthBadgeNum}>{health.score}</Text></View>
          <View style={{flex:1,marginLeft:12}}><Text style={[xstyles.healthLabel,{color:health.color}]}>Project Health: {health.label}</Text><Text style={xstyles.healthSub}>{health.score>=80?'Project is on track':'Attention needed — check risks and issues'}</Text></View>
          <MaterialCommunityIcons name={health.score>=80?'check-circle':'alert-circle'} size={32} color={health.color}/>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginVertical:8}}>
          {[{icon:'percent',c:'#3B82F6',v:`${overallProg}%`,l:'Progress'},{icon:'currency-gbp',c:'#10B981',v:fmtC(allActs.reduce((s,a)=>s+a.cost,0)),l:'Budget'},{icon:'currency-gbp',c:'#F97316',v:fmtC(evm.AC),l:'Actual Cost'},{icon:'trending-up',c:'#8B5CF6',v:evm.CPI.toFixed(2),l:'CPI'},{icon:'clock-fast',c:'#EC4899',v:evm.SPI.toFixed(2),l:'SPI'},{icon:'clock-alert',c:'#EF4444',v:String(overdue.length),l:'Overdue'},{icon:'calendar-clock',c:'#6366F1',v:`${Math.max(0,differenceInDays(new Date(projectData.endDate),today))}d`,l:'Days Left'}].map((k,i)=>(
            <View key={i} style={[xstyles.kpiCard,{borderTopColor:k.c}]}><MaterialCommunityIcons name={k.icon} size={20} color={k.c}/><Text style={[xstyles.kpiVal,{color:k.c}]}>{k.v}</Text><Text style={xstyles.kpiLabel}>{k.l}</Text></View>
          ))}
        </ScrollView>
        <View style={xstyles.card}>
          <Text style={xstyles.cardTitle}>Overall Progress</Text>
          <View style={xstyles.barBg}><View style={[xstyles.barFill,{width:`${overallProg}%`,backgroundColor:getProgressColor(overallProg)}]}/></View>
          <Text style={xstyles.barLabel}>{overallProg}% · {allActs.filter(a=>a.status==='Completed').length}/{allActs.length} activities complete</Text>
        </View>
        <View style={xstyles.card}>
          <Text style={xstyles.cardTitle}>Earned Value Management</Text>
          <View style={xstyles.evmGrid}>
            {[{l:'PV',v:fmtC(evm.PV),c:'#3B82F6'},{l:'EV',v:fmtC(evm.EV),c:'#10B981'},{l:'AC',v:fmtC(evm.AC),c:'#F97316'},{l:'BAC',v:fmtC(evm.BAC),c:'#6366F1'},{l:'EAC',v:fmtC(evm.EAC),c:'#8B5CF6'},{l:'CV',v:fmtC(evm.CV),c:evm.CV>=0?'#10B981':'#EF4444'},{l:'SV',v:fmtC(evm.SV),c:evm.SV>=0?'#10B981':'#EF4444'},{l:'CPI/SPI',v:`${evm.CPI.toFixed(2)}/${evm.SPI.toFixed(2)}`,c:evm.CPI>=1&&evm.SPI>=1?'#10B981':'#EF4444'}].map((m,i)=>(
              <View key={i} style={xstyles.evmCell}><Text style={[xstyles.evmVal,{color:m.c}]}>{m.v}</Text><Text style={xstyles.evmLabel}>{m.l}</Text></View>
            ))}
          </View>
        </View>
        <View style={xstyles.card}>
          <Text style={xstyles.cardTitle}>Status Distribution</Text>
          {['Not Started','In Progress','Completed','On Hold','Cancelled','Delayed'].map(st=>{const cnt=allActs.filter(a=>a.status===st).length;if(!cnt)return null;return(<View key={st} style={xstyles.distRow}><View style={[xstyles.distDot,{backgroundColor:getStatusColor(st)}]}/><Text style={xstyles.distLabel}>{st}</Text><View style={[xstyles.barBg,{flex:1,marginHorizontal:8}]}><View style={[xstyles.barFill,{width:`${allActs.length>0?(cnt/allActs.length)*100:0}%`,backgroundColor:getStatusColor(st)}]}/></View><Text style={xstyles.distCount}>{cnt}</Text></View>);})}
        </View>
        <View style={{flexDirection:'row',marginBottom:8}}>
          <View style={[xstyles.card,{flex:1,marginRight:4}]}>
            <Text style={xstyles.cardTitle}>Overdue ({overdue.length})</Text>
            {overdue.length===0?<Text style={xstyles.emptyMsg}>All on track ✓</Text>:overdue.slice(0,4).map(a=>(<View key={a.id} style={xstyles.listRow}><View style={[xstyles.dot,{backgroundColor:getPriorityColor(a.priority)}]}/><View style={{flex:1,marginLeft:6}}><Text style={xstyles.listTitle} numberOfLines={1}>{a.name}</Text><Text style={xstyles.listSub}>Due {fmtD(a.endDate)}</Text></View></View>))}
          </View>
          <View style={[xstyles.card,{flex:1,marginLeft:4}]}>
            <Text style={xstyles.cardTitle}>Upcoming Milestones</Text>
            {projectMilestones.filter(m=>{const d=differenceInDays(new Date(m.date),today);return d>=0&&d<=30&&m.status!=='Completed';}).length===0?<Text style={xstyles.emptyMsg}>None in 30d</Text>:projectMilestones.filter(m=>{const d=differenceInDays(new Date(m.date),today);return d>=0&&d<=30&&m.status!=='Completed';}).slice(0,4).map(m=>(<View key={m.id} style={xstyles.listRow}><MaterialCommunityIcons name="flag" size={14} color="#8B5CF6"/><View style={{flex:1,marginLeft:6}}><Text style={xstyles.listTitle} numberOfLines={1}>{m.name}</Text><Text style={xstyles.listSub}>{fmtD(m.date)}</Text></View></View>))}
          </View>
        </View>
        <View style={{flexDirection:'row',marginBottom:8}}>
          <View style={[xstyles.card,{flex:1,marginRight:4}]}>
            <Text style={xstyles.cardTitle}>Top Risks</Text>
            {risks.filter(r=>r.status==='Open').length===0?<Text style={xstyles.emptyMsg}>No open risks</Text>:risks.filter(r=>r.status==='Open').sort((a,b)=>b.likelihood*b.impact-a.likelihood*a.impact).slice(0,3).map(r=>{const sc=r.likelihood*r.impact;const sc_c=sc>=20?'#EF4444':sc>=12?'#F97316':sc>=6?'#F59E0B':'#10B981';return(<View key={r.id} style={xstyles.listRow}><View style={{width:20,height:20,borderRadius:10,backgroundColor:sc_c,alignItems:'center',justifyContent:'center'}}><Text style={{color:'#FFF',fontSize:9,fontWeight:'700'}}>{sc}</Text></View><Text style={[xstyles.listTitle,{flex:1,marginLeft:6}]} numberOfLines={1}>{r.title}</Text></View>);})}
          </View>
          <View style={[xstyles.card,{flex:1,marginLeft:4}]}>
            <Text style={xstyles.cardTitle}>Open Issues</Text>
            {issues.filter(i=>i.status==='Open'||i.status==='In Progress').length===0?<Text style={xstyles.emptyMsg}>No open issues 🎉</Text>:issues.filter(i=>i.status==='Open'||i.status==='In Progress').slice(0,3).map(i=>(<View key={i.id} style={xstyles.listRow}><View style={[xstyles.dot,{backgroundColor:getPriorityColor(i.priority)}]}/><View style={{flex:1,marginLeft:6}}><Text style={xstyles.listTitle} numberOfLines={1}>{i.title}</Text><Text style={xstyles.listSub}>{i.priority}</Text></View></View>))}
          </View>
        </View>
        <View style={{height:40}}/>
      </ScrollView>
    );
  };

  // ══════════════════════ ANALYTICS ══════════════════════
  const renderAnalyticsView = () => {
    const evm=calcEVM();
    const fa=(acts)=>{let r=[];if(!acts)return r;acts.forEach(a=>{r.push(a);if(a.children)r=r.concat(fa(a.children));});return r;};
    const allActs=fa(projectData.activities);
    const fmtC=(v)=>new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format(v||0);
    const SUBTABS=['overview','cost','resources','evm'];
    return (
      <View style={{flex:1}}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={xstyles.subTabRow} contentContainerStyle={{paddingHorizontal:12,paddingVertical:6}}>
          {SUBTABS.map(t=>(<TouchableOpacity key={t} style={[xstyles.subTab,analyticsTab===t&&xstyles.subTabActive]} onPress={()=>setAnalyticsTab(t)}><Text style={[xstyles.subTabText,analyticsTab===t&&{color:'#FFF'}]}>{t.charAt(0).toUpperCase()+t.slice(1)}</Text></TouchableOpacity>))}
        </ScrollView>
        <ScrollView style={{padding:12}} showsVerticalScrollIndicator={false}>
          {analyticsTab==='overview'&&<>
            <View style={xstyles.card}><Text style={xstyles.cardTitle}>Status Breakdown</Text>{['Not Started','In Progress','Completed','On Hold','Cancelled','Delayed'].map(st=>{const cnt=allActs.filter(a=>a.status===st).length;if(!cnt)return null;return(<View key={st} style={xstyles.distRow}><View style={[xstyles.distDot,{backgroundColor:getStatusColor(st)}]}/><Text style={xstyles.distLabel}>{st}</Text><View style={[xstyles.barBg,{flex:1,marginHorizontal:8}]}><View style={[xstyles.barFill,{width:`${allActs.length>0?(cnt/allActs.length)*100:0}%`,backgroundColor:getStatusColor(st)}]}/></View><Text style={xstyles.distCount}>{cnt}</Text></View>);})}</View>
            <View style={xstyles.card}><Text style={xstyles.cardTitle}>Priority Distribution</Text>{['Critical','High','Medium','Low'].map(p=>{const cnt=allActs.filter(a=>a.priority===p).length;if(!cnt)return null;return(<View key={p} style={xstyles.distRow}><View style={[xstyles.distDot,{backgroundColor:getPriorityColor(p)}]}/><Text style={xstyles.distLabel}>{p}</Text><View style={[xstyles.barBg,{flex:1,marginHorizontal:8}]}><View style={[xstyles.barFill,{width:`${allActs.length>0?(cnt/allActs.length)*100:0}%`,backgroundColor:getPriorityColor(p)}]}/></View><Text style={xstyles.distCount}>{cnt}</Text></View>);})}</View>
          </>}
          {analyticsTab==='cost'&&<>
            <View style={xstyles.card}><Text style={xstyles.cardTitle}>Budget vs Actual</Text>{[{l:'Total Budget',v:fmtC(allActs.reduce((s,a)=>s+a.cost,0)),c:'#3B82F6'},{l:'Actual Cost',v:fmtC(evm.AC),c:evm.AC>projectData.budget?'#EF4444':'#10B981'},{l:'Forecast (EAC)',v:fmtC(evm.EAC),c:evm.EAC>projectData.budget?'#EF4444':'#10B981'},{l:'Variance (VAC)',v:fmtC(evm.VAC),c:evm.VAC>=0?'#10B981':'#EF4444'}].map((m,i)=>(<View key={i} style={[xstyles.listRow,{justifyContent:'space-between'}]}><Text style={xstyles.listTitle}>{m.l}</Text><Text style={[xstyles.listSub,{color:m.c,fontWeight:'700',fontSize:13}]}>{m.v}</Text></View>))}</View>
            <View style={xstyles.card}><Text style={xstyles.cardTitle}>Top Activities by Cost</Text>{allActs.sort((a,b)=>b.cost-a.cost).slice(0,8).map((a,i)=>{const tot=allActs.reduce((s,x)=>s+x.cost,0);return(<View key={i} style={xstyles.distRow}><Text style={[xstyles.distLabel,{flex:1}]} numberOfLines={1}>{a.name}</Text><View style={[xstyles.barBg,{width:80,marginHorizontal:8}]}><View style={[xstyles.barFill,{width:`${tot>0?(a.cost/tot)*100:0}%`,backgroundColor:'#3B82F6'}]}/></View><Text style={xstyles.distCount}>{fmtC(a.cost)}</Text></View>);})}</View>
          </>}
          {analyticsTab==='resources'&&<View style={xstyles.card}><Text style={xstyles.cardTitle}>Resource Utilization</Text>{projectData.resources.map(r=>(<View key={r.id} style={xstyles.distRow}><Text style={xstyles.distLabel}>{r.name}</Text><View style={[xstyles.barBg,{flex:1,marginHorizontal:8}]}><View style={[xstyles.barFill,{width:`${r.utilization}%`,backgroundColor:r.utilization>90?'#EF4444':r.utilization>70?'#F59E0B':'#10B981'}]}/></View><Text style={xstyles.distCount}>{r.utilization}%</Text></View>))}</View>}
          {analyticsTab==='evm'&&<>
            <View style={xstyles.card}><Text style={xstyles.cardTitle}>EVM Metrics</Text><View style={xstyles.evmGrid}>{[{l:'PV',v:fmtC(evm.PV),c:'#3B82F6'},{l:'EV',v:fmtC(evm.EV),c:'#10B981'},{l:'AC',v:fmtC(evm.AC),c:'#F97316'},{l:'BAC',v:fmtC(evm.BAC),c:'#6366F1'},{l:'EAC',v:fmtC(evm.EAC),c:'#8B5CF6'},{l:'ETC',v:fmtC(evm.ETC),c:'#EC4899'},{l:'CV',v:fmtC(evm.CV),c:evm.CV>=0?'#10B981':'#EF4444'},{l:'SV',v:fmtC(evm.SV),c:evm.SV>=0?'#10B981':'#EF4444'},{l:'VAC',v:fmtC(evm.VAC),c:evm.VAC>=0?'#10B981':'#EF4444'},{l:'CPI',v:evm.CPI.toFixed(3),c:evm.CPI>=1?'#10B981':'#EF4444'},{l:'SPI',v:evm.SPI.toFixed(3),c:evm.SPI>=1?'#10B981':'#EF4444'},{l:'TCPI',v:evm.TCPI.toFixed(3),c:evm.TCPI<=1?'#10B981':'#EF4444'}].map((m,i)=>(<View key={i} style={xstyles.evmCell}><Text style={[xstyles.evmVal,{color:m.c}]}>{m.v}</Text><Text style={xstyles.evmLabel}>{m.l}</Text></View>))}</View></View>
            <View style={xstyles.card}><Text style={xstyles.cardTitle}>Performance Status</Text>{[{l:'Cost Performance',ok:evm.CPI>=1,msg:evm.CPI>=1?`CPI ${evm.CPI.toFixed(2)} – Under budget`:`CPI ${evm.CPI.toFixed(2)} – Over budget`},{l:'Schedule Performance',ok:evm.SPI>=1,msg:evm.SPI>=1?`SPI ${evm.SPI.toFixed(2)} – Ahead`:`SPI ${evm.SPI.toFixed(2)} – Behind`},{l:'Completion Forecast',ok:evm.EAC<=evm.BAC,msg:evm.EAC<=evm.BAC?'Forecast within budget':`Overrun: ${fmtC(evm.EAC-evm.BAC)}`}].map((it,i)=>(<View key={i} style={[xstyles.listRow,{marginBottom:8}]}><MaterialCommunityIcons name={it.ok?'check-circle':'alert-circle'} size={20} color={it.ok?'#10B981':'#EF4444'}/><View style={{marginLeft:8,flex:1}}><Text style={xstyles.listTitle}>{it.l}</Text><Text style={[xstyles.listSub,{color:it.ok?'#10B981':'#EF4444'}]}>{it.msg}</Text></View></View>))}</View>
          </>}
          <View style={{height:40}}/>
        </ScrollView>
      </View>
    );
  };

  // ══════════════════════ CALENDAR ══════════════════════
  const renderCalendarView = () => {
    const year=calendarMonth.getFullYear(),month=calendarMonth.getMonth();
    const firstDay=new Date(year,month,1);
    const startDate=new Date(firstDay);startDate.setDate(startDate.getDate()-(startDate.getDay()||7)+1);
    const days=[];const cur=new Date(startDate);for(let i=0;i<42;i++){days.push(new Date(cur));cur.setDate(cur.getDate()+1);}
    const fa=(acts)=>{let r=[];if(!acts)return r;acts.forEach(a=>{r.push(a);if(a.children)r=r.concat(fa(a.children));});return r;};
    const allActs=fa(projectData.activities);
    const actsForDay=(day)=>allActs.filter(a=>{const as=new Date(a.startDate),ae=new Date(a.endDate);return(isSameDay(day,as)||isAfter(day,as))&&(isSameDay(day,ae)||isBefore(day,ae));});
    const msForDay=(day)=>projectMilestones.filter(m=>isSameDay(new Date(m.date),day));
    const today=new Date();
    const selActs=selectedCalDate?actsForDay(selectedCalDate):[];
    const selMs=selectedCalDate?msForDay(selectedCalDate):[];
    return (
      <View style={{flex:1}}>
        <View style={xstyles.calNav}>
          <TouchableOpacity onPress={()=>setCalendarMonth(m=>{const d=new Date(m);d.setMonth(d.getMonth()-1);return d;})} style={xstyles.calNavBtn}><MaterialCommunityIcons name="chevron-left" size={24} color="#3B82F6"/></TouchableOpacity>
          <Text style={xstyles.calMonthLabel}>{format(calendarMonth,'MMMM yyyy')}</Text>
          <TouchableOpacity onPress={()=>setCalendarMonth(m=>{const d=new Date(m);d.setMonth(d.getMonth()+1);return d;})} style={xstyles.calNavBtn}><MaterialCommunityIcons name="chevron-right" size={24} color="#3B82F6"/></TouchableOpacity>
        </View>
        <View style={xstyles.calDaysRow}>
          {['M','T','W','T','F','S','S'].map((d,i)=><Text key={i} style={[xstyles.calDayHdr,i>=5&&{color:'#9CA3AF'}]}>{d}</Text>)}
        </View>
        <View style={xstyles.calGrid}>
          {days.map((day,i)=>{
            const acts=actsForDay(day),ms=msForDay(day);
            const isToday=isSameDay(day,today),isCurMon=day.getMonth()===month,isSel=selectedCalDate&&isSameDay(day,selectedCalDate);
            return (
              <TouchableOpacity key={i} style={[xstyles.calCell,!isCurMon&&{opacity:0.3},isToday&&{backgroundColor:'#DBEAFE'},isSel&&{backgroundColor:'#3B82F6',borderRadius:6}]} onPress={()=>setSelectedCalDate(p=>p&&isSameDay(p,day)?null:day)} activeOpacity={0.7}>
                <Text style={[xstyles.calDayNum,isSel&&{color:'#FFF'},isToday&&!isSel&&{color:'#3B82F6',fontWeight:'700'}]}>{day.getDate()}</Text>
                <View style={{flexDirection:'row',flexWrap:'wrap',justifyContent:'center'}}>
                  {acts.slice(0,2).map((_,j)=><View key={j} style={[xstyles.calDot,{backgroundColor:_?.isCritical?'#EF4444':'#3B82F6'}]}/>)}
                  {ms.length>0&&<View style={[xstyles.calDot,{backgroundColor:'#EC4899'}]}/>}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
        {selectedCalDate&&(
          <ScrollView style={xstyles.calDetail} showsVerticalScrollIndicator={false}>
            <Text style={xstyles.calDetailTitle}>{format(selectedCalDate,'EEEE, d MMMM yyyy')}</Text>
            {selActs.length===0&&selMs.length===0&&<Text style={xstyles.emptyMsg}>No activities or milestones</Text>}
            {selMs.map(m=><View key={m.id} style={[xstyles.listRow,{borderLeftWidth:3,borderLeftColor:'#EC4899',paddingLeft:8,marginBottom:6}]}><MaterialCommunityIcons name="flag" size={14} color="#EC4899"/><View style={{marginLeft:6,flex:1}}><Text style={[xstyles.listTitle,{color:'#EC4899'}]}>MILESTONE: {m.name}</Text><Text style={xstyles.listSub}>{m.description}</Text></View></View>)}
            {selActs.map(a=><View key={a.id} style={[xstyles.listRow,{borderLeftWidth:3,borderLeftColor:getStatusColor(a.status),paddingLeft:8,marginBottom:6}]}><View style={{flex:1}}><Text style={xstyles.listTitle}>{a.wbs}. {a.name}</Text><Text style={xstyles.listSub}>{a.status} · {a.progress}%</Text></View></View>)}
          </ScrollView>
        )}
      </View>
    );
  };

  // ══════════════════════ REPORTS ══════════════════════
  const renderReportsView = () => {
    const evm=calcEVM(),health=calcHealth();
    const fa=(acts)=>{let r=[];if(!acts)return r;acts.forEach(a=>{r.push(a);if(a.children)r=r.concat(fa(a.children));});return r;};
    const allActs=fa(projectData.activities);
    const fmtC=(v)=>new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format(v||0);
    const fmtD=(d)=>{if(!d)return'N/A';try{const dt=d instanceof Date?d:parseISO(d);return format(dt,'dd/MM/yyyy');}catch{return'N/A';}};
    return (
      <View style={{flex:1}}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={xstyles.subTabRow} contentContainerStyle={{paddingHorizontal:12,paddingVertical:6}}>
          {['summary','activities','resources','risks','issues','evm'].map(t=>(<TouchableOpacity key={t} style={[xstyles.subTab,reportType===t&&xstyles.subTabActive]} onPress={()=>setReportType(t)}><Text style={[xstyles.subTabText,reportType===t&&{color:'#FFF'}]}>{t.charAt(0).toUpperCase()+t.slice(1)}</Text></TouchableOpacity>))}
        </ScrollView>
        <TouchableOpacity style={xstyles.exportBtn} onPress={async()=>{setReportGenerating(true);try{const{uri}=await Print.printToFileAsync({html:genHTMLReport()});await Sharing.shareAsync(uri,{dialogTitle:'Share Report'});}catch(e){Alert.alert('Error',e.message);}finally{setReportGenerating(false);}}} disabled={reportGenerating}>
          <MaterialCommunityIcons name="export" size={16} color="#FFF"/>
          <Text style={xstyles.exportBtnText}>{reportGenerating?'Generating…':'Export Full PDF Report'}</Text>
        </TouchableOpacity>
        <ScrollView style={{padding:12}} showsVerticalScrollIndicator={false}>
          {reportType==='summary'&&<>
            <View style={xstyles.card}><Text style={xstyles.cardTitle}>Executive Summary</Text>{[['Project',projectData.name],['Description',projectData.description],['Period',`${fmtD(projectData.startDate)} – ${fmtD(projectData.endDate)}`],['Status',projectData.status],['Manager',projectData.manager],['Client',projectData.client],['Budget',fmtC(projectData.budget)],['Health',`${health.score}/100 – ${health.label}`]].map(([l,v],i)=>(<View key={i} style={[xstyles.listRow,{justifyContent:'space-between',paddingVertical:4,borderBottomWidth:1,borderBottomColor:'#F3F4F6'}]}><Text style={xstyles.listSub}>{l}</Text><Text style={[xstyles.listTitle,{textAlign:'right',flex:1}]}>{v}</Text></View>))}</View>
            <View style={xstyles.card}><Text style={xstyles.cardTitle}>Key Statistics</Text>{[['Activities',allActs.length],['Completed',allActs.filter(a=>a.status==='Completed').length],['Overdue',allActs.filter(a=>a.status!=='Completed'&&isBefore(new Date(a.endDate),new Date())).length],['Open Risks',risks.filter(r=>r.status==='Open').length],['Open Issues',issues.filter(i=>i.status==='Open').length],['Team',projectData.resources.length]].map(([l,v],i)=>(<View key={i} style={[xstyles.listRow,{justifyContent:'space-between'}]}><Text style={xstyles.listTitle}>{l}</Text><Text style={[xstyles.listSub,{fontWeight:'700',color:'#1E3A8A'}]}>{v}</Text></View>))}</View>
          </>}
          {reportType==='activities'&&allActs.map(a=>(<View key={a.id} style={xstyles.card}><View style={{flexDirection:'row',alignItems:'center',marginBottom:6}}><Text style={[xstyles.cardTitle,{flex:1}]}>{a.wbs}. {a.name}</Text><View style={[xstyles.statusChip,{backgroundColor:getStatusColor(a.status)}]}><Text style={xstyles.statusChipText}>{a.status}</Text></View></View>{[['Duration',`${a.duration}d`],['Start',fmtD(a.startDate)],['End',fmtD(a.endDate)],['Progress',`${a.progress}%`],['Priority',a.priority],['Budget',fmtC(a.cost)]].map(([l,v],i)=>(<View key={i} style={[xstyles.listRow,{justifyContent:'space-between'}]}><Text style={xstyles.listSub}>{l}</Text><Text style={xstyles.listTitle}>{v}</Text></View>))}<View style={xstyles.barBg}><View style={[xstyles.barFill,{width:`${a.progress}%`,backgroundColor:getProgressColor(a.progress)}]}/></View></View>))}
          {reportType==='resources'&&projectData.resources.map(r=>(<View key={r.id} style={xstyles.card}><View style={{flexDirection:'row',alignItems:'center',marginBottom:6}}><Image source={{ uri: (r.avatar && r.avatar.startsWith('http')) ? r.avatar : undefined }} style={{width:36,height:36,borderRadius:18}}/><Text style={[xstyles.cardTitle,{marginLeft:8}]}>{r.name}</Text></View>{[['Role',r.role],['Utilization',`${r.utilization}%`],['Rate',fmtC(r.rate)+'/hr'],['Skills',(r.skills||[]).join(', ')||'—']].map(([l,v],i)=>(<View key={i} style={[xstyles.listRow,{justifyContent:'space-between'}]}><Text style={xstyles.listSub}>{l}</Text><Text style={xstyles.listTitle}>{v}</Text></View>))}</View>))}
          {reportType==='risks'&&risks.map(r=>{const sc=r.likelihood*r.impact;const sc_c=sc>=20?'#EF4444':sc>=12?'#F97316':sc>=6?'#F59E0B':'#10B981';return(<View key={r.id} style={[xstyles.card,{borderLeftWidth:4,borderLeftColor:sc_c}]}><View style={{flexDirection:'row',alignItems:'center',marginBottom:4}}><Text style={[xstyles.cardTitle,{flex:1}]}>{r.title}</Text><View style={[xstyles.statusChip,{backgroundColor:sc_c}]}><Text style={xstyles.statusChipText}>Score: {sc}</Text></View></View><Text style={{color:'#6B7280',fontSize:12,marginBottom:6}}>{r.description}</Text>{[['Category',r.category],['Likelihood',`${r.likelihood}`],['Impact',`${r.impact}`],['Status',r.status],['Owner',r.owner]].map(([l,v],i)=>(<View key={i} style={[xstyles.listRow,{justifyContent:'space-between'}]}><Text style={xstyles.listSub}>{l}</Text><Text style={xstyles.listTitle}>{v}</Text></View>))}{!!r.mitigation&&<Text style={{marginTop:4,color:'#059669',fontSize:12,fontStyle:'italic'}}>Mitigation: {r.mitigation}</Text>}</View>);})}
          {reportType==='issues'&&issues.map(i=>(<View key={i.id} style={[xstyles.card,{borderLeftWidth:4,borderLeftColor:getPriorityColor(i.priority)}]}><View style={{flexDirection:'row',alignItems:'center',marginBottom:4}}><Text style={[xstyles.cardTitle,{flex:1}]}>{i.title}</Text><View style={[xstyles.statusChip,{backgroundColor:getPriorityColor(i.priority)}]}><Text style={xstyles.statusChipText}>{i.priority}</Text></View></View>{[['Category',i.category],['Status',i.status],['Assignee',i.assignee],['Due',fmtD(i.dueDate)]].map(([l,v],j)=>(<View key={j} style={[xstyles.listRow,{justifyContent:'space-between'}]}><Text style={xstyles.listSub}>{l}</Text><Text style={xstyles.listTitle}>{v}</Text></View>))}</View>))}
          {reportType==='evm'&&<View style={xstyles.card}><Text style={xstyles.cardTitle}>EVM Report</Text>{[['PV',fmtC(evm.PV),'Budgeted cost scheduled'],['EV',fmtC(evm.EV),'Budgeted cost done'],['AC',fmtC(evm.AC),'Actual cost incurred'],['BAC',fmtC(evm.BAC),'Total approved budget'],['EAC',fmtC(evm.EAC),evm.EAC<=evm.BAC?'Within budget':'OVER budget'],['ETC',fmtC(evm.ETC),'Cost to finish'],['CV',fmtC(evm.CV),evm.CV>=0?'Under budget':'Over budget'],['SV',fmtC(evm.SV),evm.SV>=0?'Ahead':'Behind'],['CPI',evm.CPI.toFixed(3),evm.CPI>=1?'Cost efficient':'Inefficient'],['SPI',evm.SPI.toFixed(3),evm.SPI>=1?'On schedule':'Behind'],['TCPI',evm.TCPI.toFixed(3),evm.TCPI<=1?'Achievable':'Challenging']].map(([l,v,d],i)=>(<View key={i} style={{borderBottomWidth:1,borderBottomColor:'#F3F4F6',paddingVertical:8}}><View style={[xstyles.listRow,{justifyContent:'space-between'}]}><Text style={xstyles.listTitle}>{l}</Text><Text style={[xstyles.listSub,{fontWeight:'700',color:'#1E3A8A'}]}>{v}</Text></View><Text style={[xstyles.listSub,{color:'#9CA3AF',marginTop:2}]}>{d}</Text></View>))}</View>}
          <View style={{height:40}}/>
        </ScrollView>
      </View>
    );
  };

  // ══════════════════════ RISKS ══════════════════════
  const renderRisksView = () => {
    const filtered=riskFilter==='All'?risks:risks.filter(r=>r.status===riskFilter);
    const fmtD=(d)=>{if(!d)return'N/A';try{const dt=d instanceof Date?d:parseISO(d);return format(dt,'dd/MM/yyyy');}catch{return'N/A';}};
    const MATRIX=Array.from({length:5},(_,i)=>Array.from({length:5},(_,j)=>({likelihood:5-i,impact:j+1,score:(5-i)*(j+1)})));
    const sc_c=(s)=>s>=20?'#EF4444':s>=12?'#F97316':s>=6?'#F59E0B':'#10B981';
    return (
      <View style={{flex:1}}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={xstyles.subTabRow} contentContainerStyle={{paddingHorizontal:12,paddingVertical:6}}>
          {['All','Open','Mitigated','Closed'].map(f=>(<TouchableOpacity key={f} style={[xstyles.subTab,riskFilter===f&&xstyles.subTabActive]} onPress={()=>setRiskFilter(f)}><Text style={[xstyles.subTabText,riskFilter===f&&{color:'#FFF'}]}>{f} {f!=='All'&&`(${risks.filter(r=>r.status===f).length})`}</Text></TouchableOpacity>))}
        </ScrollView>
        <ScrollView style={{padding:12}} showsVerticalScrollIndicator={false}>
          <View style={xstyles.card}>
            <Text style={xstyles.cardTitle}>Risk Matrix (Likelihood × Impact)</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View>{MATRIX.map((row,ri)=>(<View key={ri} style={{flexDirection:'row',alignItems:'center'}}><Text style={{width:18,textAlign:'center',fontSize:10,color:'#6B7280'}}>{5-ri}</Text>{row.map((cell,ci)=>{const here=risks.filter(r=>r.status==='Open'&&r.likelihood===(5-ri)&&r.impact===(ci+1));return(<View key={ci} style={{width:48,height:40,margin:1,borderRadius:4,backgroundColor:sc_c(cell.score)+'33',borderWidth:1,borderColor:sc_c(cell.score),alignItems:'center',justifyContent:'center'}}><Text style={{fontSize:10,fontWeight:'700',color:sc_c(cell.score)}}>{cell.score}</Text>{here.length>0&&<View style={{position:'absolute',top:2,right:2,backgroundColor:sc_c(cell.score),borderRadius:6,width:12,height:12,alignItems:'center',justifyContent:'center'}}><Text style={{color:'#FFF',fontSize:8,fontWeight:'700'}}>{here.length}</Text></View>}</View>);})}</View>))}<View style={{flexDirection:'row',marginLeft:22}}>{[1,2,3,4,5].map(v=><Text key={v} style={{width:50,textAlign:'center',fontSize:10,color:'#6B7280'}}>{v}</Text>)}</View><Text style={{textAlign:'center',fontSize:10,color:'#6B7280',marginTop:2}}>Impact →</Text></View>
            </ScrollView>
          </View>
          {filtered.length===0&&<View style={xstyles.emptyState}><MaterialCommunityIcons name="shield-check" size={56} color="#D1D5DB"/><Text style={xstyles.emptyTitle}>No risks found</Text></View>}
          {filtered.map(r=>{const sc=r.likelihood*r.impact;return(
            <View key={r.id} style={[xstyles.card,{borderLeftWidth:4,borderLeftColor:sc_c(sc)}]}>
              <View style={{flexDirection:'row',alignItems:'flex-start',marginBottom:4}}>
                <View style={{backgroundColor:sc_c(sc)+'22',borderRadius:8,paddingHorizontal:8,paddingVertical:3,marginRight:8}}><Text style={{color:sc_c(sc),fontWeight:'700',fontSize:12}}>Score: {sc}</Text></View>
                <Text style={[xstyles.cardTitle,{flex:1}]}>{r.title}</Text>
                <View style={[xstyles.statusChip,{backgroundColor:r.status==='Open'?'#FEE2E2':r.status==='Mitigated'?'#DCFCE7':'#F3F4F6'}]}><Text style={[xstyles.statusChipText,{color:r.status==='Open'?'#EF4444':r.status==='Mitigated'?'#10B981':'#6B7280'}]}>{r.status}</Text></View>
              </View>
              <Text style={{color:'#6B7280',fontSize:12,marginBottom:6}}>{r.description}</Text>
              <View style={{flexDirection:'row',flexWrap:'wrap',gap:4,marginBottom:6}}>{[r.category,`L:${r.likelihood}`,`I:${r.impact}`,r.owner,`Due: ${fmtD(r.dueDate)}`].map((t,i)=><View key={i} style={{backgroundColor:'#F3F4F6',borderRadius:10,paddingHorizontal:8,paddingVertical:3}}><Text style={{fontSize:11,color:'#374151'}}>{t}</Text></View>)}</View>
              {!!r.mitigation&&<View style={{flexDirection:'row',alignItems:'flex-start',backgroundColor:'#F0FDF4',borderRadius:6,padding:8,marginBottom:8}}><MaterialCommunityIcons name="shield-check" size={14} color="#059669"/><Text style={{flex:1,marginLeft:4,fontSize:12,color:'#059669'}}>{r.mitigation}</Text></View>}
              <View style={{flexDirection:'row',gap:8}}>
                <TouchableOpacity onPress={()=>{setRiskForm({title:r.title,description:r.description,category:r.category,likelihood:r.likelihood,impact:r.impact,status:r.status,mitigation:r.mitigation,owner:r.owner,dueDate:new Date(r.dueDate)});setEditingRisk(r);setShowRiskModal(true);}} style={xstyles.actionBtn}><MaterialCommunityIcons name="pencil" size={14} color="#3B82F6"/><Text style={[xstyles.actionBtnText,{color:'#3B82F6'}]}>Edit</Text></TouchableOpacity>
                <TouchableOpacity onPress={()=>setRisks(p=>p.map(x=>x.id===r.id?{...x,status:r.status==='Open'?'Mitigated':'Open'}:x))} style={xstyles.actionBtn}><MaterialCommunityIcons name={r.status==='Open'?'shield-check':'shield-off'} size={14} color="#10B981"/><Text style={[xstyles.actionBtnText,{color:'#10B981'}]}>{r.status==='Open'?'Mitigate':'Reopen'}</Text></TouchableOpacity>
                <TouchableOpacity onPress={()=>deleteRisk(r.id)} style={xstyles.actionBtn}><MaterialCommunityIcons name="delete-outline" size={14} color="#EF4444"/><Text style={[xstyles.actionBtnText,{color:'#EF4444'}]}>Delete</Text></TouchableOpacity>
              </View>
            </View>
          );})}
          <View style={{height:40}}/>
        </ScrollView>
      </View>
    );
  };

  // ══════════════════════ ISSUES ══════════════════════
  const renderIssuesView = () => {
    const filtered=issueFilter==='All'?issues:issues.filter(i=>i.status===issueFilter);
    const fmtD=(d)=>{if(!d)return'N/A';try{const dt=d instanceof Date?d:parseISO(d);return format(dt,'dd/MM/yyyy');}catch{return'N/A';}};
    return (
      <View style={{flex:1}}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={xstyles.subTabRow} contentContainerStyle={{paddingHorizontal:12,paddingVertical:6}}>
          {['All','Open','In Progress','Resolved','Closed'].map(f=>(<TouchableOpacity key={f} style={[xstyles.subTab,issueFilter===f&&xstyles.subTabActive]} onPress={()=>setIssueFilter(f)}><Text style={[xstyles.subTabText,issueFilter===f&&{color:'#FFF'}]}>{f} {f!=='All'&&`(${issues.filter(i=>i.status===f).length})`}</Text></TouchableOpacity>))}
        </ScrollView>
        <ScrollView style={{padding:12}} showsVerticalScrollIndicator={false}>
          {filtered.length===0&&<View style={xstyles.emptyState}><MaterialCommunityIcons name="check-all" size={56} color="#D1D5DB"/><Text style={xstyles.emptyTitle}>No issues found 🎉</Text></View>}
          {filtered.map(i=>(
            <View key={i.id} style={[xstyles.card,{borderLeftWidth:4,borderLeftColor:getPriorityColor(i.priority)}]}>
              <View style={{flexDirection:'row',alignItems:'flex-start',marginBottom:4}}>
                <View style={[xstyles.statusChip,{backgroundColor:getPriorityColor(i.priority),marginRight:8}]}><Text style={xstyles.statusChipText}>{i.priority}</Text></View>
                <Text style={[xstyles.cardTitle,{flex:1}]}>{i.title}</Text>
                <View style={[xstyles.statusChip,{backgroundColor:i.status==='Resolved'||i.status==='Closed'?'#DCFCE7':i.status==='In Progress'?'#DBEAFE':'#FEE2E2'}]}><Text style={[xstyles.statusChipText,{color:i.status==='Resolved'||i.status==='Closed'?'#10B981':i.status==='In Progress'?'#3B82F6':'#EF4444'}]}>{i.status}</Text></View>
              </View>
              <Text style={{color:'#6B7280',fontSize:12,marginBottom:6}}>{i.description}</Text>
              <View style={{flexDirection:'row',flexWrap:'wrap',gap:4,marginBottom:6}}>{[i.category,`👤 ${i.assignee||'Unassigned'}`,`📅 ${fmtD(i.dueDate)}`].map((t,j)=><View key={j} style={{backgroundColor:'#F3F4F6',borderRadius:10,paddingHorizontal:8,paddingVertical:3}}><Text style={{fontSize:11,color:'#374151'}}>{t}</Text></View>)}</View>
              <View style={{flexDirection:'row',gap:8}}>
                <TouchableOpacity onPress={()=>{setIssueForm({title:i.title,description:i.description,priority:i.priority,status:i.status,assignee:i.assignee,dueDate:new Date(i.dueDate),category:i.category,linkedActivityId:i.linkedActivityId});setEditingIssue(i);setShowIssueModal(true);}} style={xstyles.actionBtn}><MaterialCommunityIcons name="pencil" size={14} color="#3B82F6"/><Text style={[xstyles.actionBtnText,{color:'#3B82F6'}]}>Edit</Text></TouchableOpacity>
                {i.status!=='Resolved'&&i.status!=='Closed'&&<TouchableOpacity onPress={()=>resolveIssue(i.id)} style={xstyles.actionBtn}><MaterialCommunityIcons name="check-circle-outline" size={14} color="#10B981"/><Text style={[xstyles.actionBtnText,{color:'#10B981'}]}>Resolve</Text></TouchableOpacity>}
                <TouchableOpacity onPress={()=>deleteIssue(i.id)} style={xstyles.actionBtn}><MaterialCommunityIcons name="delete-outline" size={14} color="#EF4444"/><Text style={[xstyles.actionBtnText,{color:'#EF4444'}]}>Delete</Text></TouchableOpacity>
              </View>
            </View>
          ))}
          <View style={{height:40}}/>
        </ScrollView>
      </View>
    );
  };

  // ══════════════════════ MILESTONES ══════════════════════
  const renderMilestonesView = () => {
    const today=new Date();
    const sorted=[...projectMilestones].sort((a,b)=>new Date(a.date)-new Date(b.date));
    const fmtD=(d)=>{if(!d)return'N/A';try{const dt=d instanceof Date?d:parseISO(d);return format(dt,'dd/MM/yyyy');}catch{return'N/A';}};
    return (
      <ScrollView style={{padding:12}} showsVerticalScrollIndicator={false}>
        {sorted.length===0&&<View style={xstyles.emptyState}><MaterialCommunityIcons name="flag-outline" size={56} color="#D1D5DB"/><Text style={xstyles.emptyTitle}>No milestones yet</Text></View>}
        {sorted.map(m=>{
          const daysTo=differenceInDays(new Date(m.date),today),isPast=daysTo<0,isNear=daysTo>=0&&daysTo<=7;
          const sColor=m.status==='Completed'?'#10B981':isPast?'#EF4444':isNear?'#F59E0B':'#3B82F6';
          return (
            <View key={m.id} style={[xstyles.card,{borderLeftWidth:4,borderLeftColor:sColor}]}>
              <View style={{flexDirection:'row',alignItems:'center',marginBottom:4}}>
                <MaterialCommunityIcons name={m.status==='Completed'?'flag-checkered':'flag'} size={20} color={sColor}/>
                <Text style={[xstyles.cardTitle,{flex:1,marginLeft:8}]}>{m.name}</Text>
                <View style={[xstyles.statusChip,{backgroundColor:sColor+'22'}]}><Text style={[xstyles.statusChipText,{color:sColor}]}>{m.status==='Completed'?'Done':isPast?'Overdue':isNear?`${daysTo}d`:`in ${daysTo}d`}</Text></View>
              </View>
              <Text style={{color:'#6B7280',fontSize:12,marginBottom:4}}>Date: {fmtD(m.date)}</Text>
              {!!m.description&&<Text style={{color:'#374151',fontSize:13,marginBottom:6}}>{m.description}</Text>}
              <View style={{flexDirection:'row',gap:8}}>
                <TouchableOpacity onPress={()=>{setMsForm({name:m.name,date:new Date(m.date),status:m.status,description:m.description,linkedActivityId:m.linkedActivityId});setEditingMs(m);setShowMsModal(true);}} style={xstyles.actionBtn}><MaterialCommunityIcons name="pencil" size={14} color="#3B82F6"/><Text style={[xstyles.actionBtnText,{color:'#3B82F6'}]}>Edit</Text></TouchableOpacity>
                {m.status!=='Completed'&&<TouchableOpacity onPress={()=>setProjectMilestones(p=>p.map(x=>x.id===m.id?{...x,status:'Completed'}:x))} style={xstyles.actionBtn}><MaterialCommunityIcons name="check-circle-outline" size={14} color="#10B981"/><Text style={[xstyles.actionBtnText,{color:'#10B981'}]}>Complete</Text></TouchableOpacity>}
                <TouchableOpacity onPress={()=>deleteMilestone(m.id)} style={xstyles.actionBtn}><MaterialCommunityIcons name="delete-outline" size={14} color="#EF4444"/><Text style={[xstyles.actionBtnText,{color:'#EF4444'}]}>Delete</Text></TouchableOpacity>
              </View>
            </View>
          );
        })}
        <View style={{height:40}}/>
      </ScrollView>
    );
  };

  // ══════════════════════ NEW MODALS ══════════════════════
  // Add these constants right here, before the modal render functions
  const LIKELIHOOD_LABELS = {
    1: 'Very Low',
    2: 'Low',
    3: 'Medium',
    4: 'High',
    5: 'Very High'
  };

  const IMPACT_LABELS = {
    1: 'Very Low',
    2: 'Low',
    3: 'Medium',
    4: 'High',
    5: 'Very High'
  };

  const renderRiskModal = () => (
    <Modal visible={showRiskModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{flex:1,backgroundColor:'#FFF'}}>
        <LinearGradient colors={['#F97316','#EA580C']} style={{flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14}}>
          <TouchableOpacity onPress={()=>{setShowRiskModal(false);setEditingRisk(null);}} style={{marginRight:12}}><MaterialCommunityIcons name="close" size={24} color="#FFF"/></TouchableOpacity>
          <Text style={{color:'#FFF',fontSize:18,fontWeight:'700',flex:1}}>{editingRisk?'Edit Risk':'Add Risk'}</Text>
          <TouchableOpacity onPress={saveRisk} style={{backgroundColor:'rgba(255,255,255,0.2)',paddingHorizontal:16,paddingVertical:8,borderRadius:8}}><Text style={{color:'#FFF',fontWeight:'700'}}>Save</Text></TouchableOpacity>
        </LinearGradient>
        <ScrollView style={{padding:16}} keyboardShouldPersistTaps="handled">
          <Text style={xstyles.formLabel}>Title *</Text>
          <TextInput style={xstyles.formInput} value={riskForm.title} onChangeText={v=>setRiskForm(p=>({...p,title:v}))} placeholder="Risk title" placeholderTextColor="#9CA3AF"/>
          <Text style={xstyles.formLabel}>Description</Text>
          <TextInput style={[xstyles.formInput,{height:72,textAlignVertical:'top'}]} value={riskForm.description} onChangeText={v=>setRiskForm(p=>({...p,description:v}))} placeholder="Describe the risk" placeholderTextColor="#9CA3AF" multiline/>
          <View style={{flexDirection:'row',gap:12}}>
            <View style={{flex:1}}><Text style={xstyles.formLabel}>Category</Text><TouchableOpacity style={xstyles.formSelect} onPress={()=>Alert.alert('Category','',['Technical','Scope','Resource','Schedule','Cost','External','Quality','Legal'].map(c=>({text:c,onPress:()=>setRiskForm(p=>({...p,category:c}))})).concat([{text:'Cancel',style:'cancel'}]))}><Text style={xstyles.formSelectText}>{riskForm.category}</Text><MaterialCommunityIcons name="chevron-down" size={18} color="#6B7280"/></TouchableOpacity></View>
            <View style={{flex:1}}><Text style={xstyles.formLabel}>Status</Text><TouchableOpacity style={xstyles.formSelect} onPress={()=>Alert.alert('Status','',['Open','Mitigated','Closed','Escalated'].map(s=>({text:s,onPress:()=>setRiskForm(p=>({...p,status:s}))})).concat([{text:'Cancel',style:'cancel'}]))}><Text style={xstyles.formSelectText}>{riskForm.status}</Text><MaterialCommunityIcons name="chevron-down" size={18} color="#6B7280"/></TouchableOpacity></View>
          </View>
          <Text style={xstyles.formLabel}>Likelihood (1–5): {riskForm.likelihood} – {LIKELIHOOD_LABELS[riskForm.likelihood]}</Text>
          <View style={{flexDirection:'row',gap:6,marginBottom:12}}>{[1,2,3,4,5].map(v=><TouchableOpacity key={v} onPress={()=>setRiskForm(p=>({...p,likelihood:v}))} style={{flex:1,padding:10,borderRadius:8,backgroundColor:riskForm.likelihood===v?'#F97316':'#F3F4F6',alignItems:'center'}}><Text style={{fontWeight:'700',color:riskForm.likelihood===v?'#FFF':'#374151'}}>{v}</Text></TouchableOpacity>)}</View>
          <Text style={xstyles.formLabel}>Impact (1–5): {riskForm.impact} – {IMPACT_LABELS[riskForm.impact]}</Text>
          <View style={{flexDirection:'row',gap:6,marginBottom:12}}>{[1,2,3,4,5].map(v=><TouchableOpacity key={v} onPress={()=>setRiskForm(p=>({...p,impact:v}))} style={{flex:1,padding:10,borderRadius:8,backgroundColor:riskForm.impact===v?'#3B82F6':'#F3F4F6',alignItems:'center'}}><Text style={{fontWeight:'700',color:riskForm.impact===v?'#FFF':'#374151'}}>{v}</Text></TouchableOpacity>)}</View>
          <Text style={xstyles.formLabel}>Mitigation Plan</Text>
          <TextInput style={[xstyles.formInput,{height:72,textAlignVertical:'top'}]} value={riskForm.mitigation} onChangeText={v=>setRiskForm(p=>({...p,mitigation:v}))} placeholder="How will this risk be mitigated?" placeholderTextColor="#9CA3AF" multiline/>
          <Text style={xstyles.formLabel}>Owner</Text>
          <TextInput style={xstyles.formInput} value={riskForm.owner} onChangeText={v=>setRiskForm(p=>({...p,owner:v}))} placeholder="Risk owner" placeholderTextColor="#9CA3AF"/>
          <View style={{height:40}}/>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderIssueModal = () => (
    <Modal visible={showIssueModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{flex:1,backgroundColor:'#FFF'}}>
        <LinearGradient colors={['#8B5CF6','#6D28D9']} style={{flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14}}>
          <TouchableOpacity onPress={()=>{setShowIssueModal(false);setEditingIssue(null);}} style={{marginRight:12}}><MaterialCommunityIcons name="close" size={24} color="#FFF"/></TouchableOpacity>
          <Text style={{color:'#FFF',fontSize:18,fontWeight:'700',flex:1}}>{editingIssue?'Edit Issue':'Add Issue'}</Text>
          <TouchableOpacity onPress={saveIssue} style={{backgroundColor:'rgba(255,255,255,0.2)',paddingHorizontal:16,paddingVertical:8,borderRadius:8}}><Text style={{color:'#FFF',fontWeight:'700'}}>Save</Text></TouchableOpacity>
        </LinearGradient>
        <ScrollView style={{padding:16}} keyboardShouldPersistTaps="handled">
          <Text style={xstyles.formLabel}>Title *</Text>
          <TextInput style={xstyles.formInput} value={issueForm.title} onChangeText={v=>setIssueForm(p=>({...p,title:v}))} placeholder="Issue title" placeholderTextColor="#9CA3AF"/>
          <Text style={xstyles.formLabel}>Description</Text>
          <TextInput style={[xstyles.formInput,{height:72,textAlignVertical:'top'}]} value={issueForm.description} onChangeText={v=>setIssueForm(p=>({...p,description:v}))} placeholder="Describe the issue" placeholderTextColor="#9CA3AF" multiline/>
          <View style={{flexDirection:'row',gap:12}}>
            <View style={{flex:1}}><Text style={xstyles.formLabel}>Priority</Text><TouchableOpacity style={xstyles.formSelect} onPress={()=>Alert.alert('Priority','',['Critical','High','Medium','Low'].map(p=>({text:p,onPress:()=>setIssueForm(prev=>({...prev,priority:p}))})).concat([{text:'Cancel',style:'cancel'}]))}><Text style={xstyles.formSelectText}>{issueForm.priority}</Text><MaterialCommunityIcons name="chevron-down" size={18} color="#6B7280"/></TouchableOpacity></View>
            <View style={{flex:1}}><Text style={xstyles.formLabel}>Status</Text><TouchableOpacity style={xstyles.formSelect} onPress={()=>Alert.alert('Status','',['Open','In Progress','Resolved','Closed'].map(s=>({text:s,onPress:()=>setIssueForm(p=>({...p,status:s}))})).concat([{text:'Cancel',style:'cancel'}]))}><Text style={xstyles.formSelectText}>{issueForm.status}</Text><MaterialCommunityIcons name="chevron-down" size={18} color="#6B7280"/></TouchableOpacity></View>
          </View>
          <Text style={xstyles.formLabel}>Assignee</Text>
          <TextInput style={xstyles.formInput} value={issueForm.assignee} onChangeText={v=>setIssueForm(p=>({...p,assignee:v}))} placeholder="Assigned to" placeholderTextColor="#9CA3AF"/>
          <Text style={xstyles.formLabel}>Category</Text>
          <TouchableOpacity style={xstyles.formSelect} onPress={()=>Alert.alert('Category','',['Bug','Feature','Performance','Security','General'].map(c=>({text:c,onPress:()=>setIssueForm(p=>({...p,category:c}))})).concat([{text:'Cancel',style:'cancel'}]))}><Text style={xstyles.formSelectText}>{issueForm.category}</Text><MaterialCommunityIcons name="chevron-down" size={18} color="#6B7280"/></TouchableOpacity>
          <View style={{height:40}}/>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderMilestoneModal = () => (
    <Modal visible={showMsModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{flex:1,backgroundColor:'#FFF'}}>
        <LinearGradient colors={['#EC4899','#BE185D']} style={{flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14}}>
          <TouchableOpacity onPress={()=>{setShowMsModal(false);setEditingMs(null);}} style={{marginRight:12}}><MaterialCommunityIcons name="close" size={24} color="#FFF"/></TouchableOpacity>
          <Text style={{color:'#FFF',fontSize:18,fontWeight:'700',flex:1}}>{editingMs?'Edit Milestone':'Add Milestone'}</Text>
          <TouchableOpacity onPress={saveMilestone} style={{backgroundColor:'rgba(255,255,255,0.2)',paddingHorizontal:16,paddingVertical:8,borderRadius:8}}><Text style={{color:'#FFF',fontWeight:'700'}}>Save</Text></TouchableOpacity>
        </LinearGradient>
        <ScrollView style={{padding:16}} keyboardShouldPersistTaps="handled">
          <Text style={xstyles.formLabel}>Name *</Text>
          <TextInput style={xstyles.formInput} value={msForm.name} onChangeText={v=>setMsForm(p=>({...p,name:v}))} placeholder="Milestone name" placeholderTextColor="#9CA3AF"/>
          <Text style={xstyles.formLabel}>Description</Text>
          <TextInput style={[xstyles.formInput,{height:72,textAlignVertical:'top'}]} value={msForm.description} onChangeText={v=>setMsForm(p=>({...p,description:v}))} placeholder="Describe this milestone" placeholderTextColor="#9CA3AF" multiline/>
          <Text style={xstyles.formLabel}>Due Date</Text>
          <TouchableOpacity style={xstyles.formSelect} onPress={()=>setShowMsDatePicker(true)}><MaterialCommunityIcons name="calendar" size={18} color="#6B7280"/><Text style={[xstyles.formSelectText,{marginLeft:8}]}>{msForm.date?format(new Date(msForm.date),'dd/MM/yyyy'):'Select date'}</Text></TouchableOpacity>
          <Text style={xstyles.formLabel}>Status</Text>
          <TouchableOpacity style={xstyles.formSelect} onPress={()=>Alert.alert('Status','',['Pending','In Progress','Completed','At Risk'].map(s=>({text:s,onPress:()=>setMsForm(p=>({...p,status:s}))})).concat([{text:'Cancel',style:'cancel'}]))}><Text style={xstyles.formSelectText}>{msForm.status}</Text><MaterialCommunityIcons name="chevron-down" size={18} color="#6B7280"/></TouchableOpacity>
          <View style={{height:40}}/>
        </ScrollView>
        {showMsDatePicker&&<DateTimePicker value={new Date(msForm.date)} mode="date" display="default" onChange={(_,d)=>{setShowMsDatePicker(false);if(d)setMsForm(p=>({...p,date:d}));}}/>}
      </SafeAreaView>
    </Modal>
  );

  const renderTimeModal = () => {
    const fa=(acts)=>{let r=[];if(!acts)return r;acts.forEach(a=>{r.push(a);if(a.children)r=r.concat(fa(a.children));});return r;};
    return (
      <Modal visible={showTimeModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{flex:1,backgroundColor:'#FFF'}}>
          <LinearGradient colors={['#14B8A6','#0F766E']} style={{flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14}}>
            <TouchableOpacity onPress={()=>{setShowTimeModal(false);setEditingTime(null);}} style={{marginRight:12}}><MaterialCommunityIcons name="close" size={24} color="#FFF"/></TouchableOpacity>
            <Text style={{color:'#FFF',fontSize:18,fontWeight:'700',flex:1}}>{editingTime?'Edit Time Entry':'Log Time'}</Text>
            <TouchableOpacity onPress={saveTime} style={{backgroundColor:'rgba(255,255,255,0.2)',paddingHorizontal:16,paddingVertical:8,borderRadius:8}}><Text style={{color:'#FFF',fontWeight:'700'}}>Save</Text></TouchableOpacity>
          </LinearGradient>
          <ScrollView style={{padding:16}} keyboardShouldPersistTaps="handled">
            <Text style={xstyles.formLabel}>Activity *</Text>
            <TouchableOpacity style={xstyles.formSelect} onPress={()=>Alert.alert('Select Activity','',fa(projectData.activities).filter(a=>a&&typeof a==='object').map(a=>({text:`${a.wbs}. ${a.name}`,onPress:()=>setTimeForm(p=>({...p,activityId:a.id}))})).concat([{text:'Cancel',style:'cancel'}]))}><Text style={xstyles.formSelectText}>{timeForm.activityId?fa(projectData.activities).find(a=>a?.id===timeForm.activityId)?.name||'Selected':'Select activity'}</Text><MaterialCommunityIcons name="chevron-down" size={18} color="#6B7280"/></TouchableOpacity>
            <Text style={xstyles.formLabel}>Resource</Text>
            <TouchableOpacity style={xstyles.formSelect} onPress={()=>Alert.alert('Select Resource','',projectData.resources.map(r=>({text:r.name,onPress:()=>setTimeForm(p=>({...p,resourceId:r.id}))})).concat([{text:'Cancel',style:'cancel'}]))}><Text style={xstyles.formSelectText}>{timeForm.resourceId?projectData.resources.find(r=>r?.id===timeForm.resourceId)?.name||'Selected':'Select resource (optional)'}</Text><MaterialCommunityIcons name="chevron-down" size={18} color="#6B7280"/></TouchableOpacity>
            <Text style={xstyles.formLabel}>Hours *</Text>
            <TextInput style={xstyles.formInput} value={timeForm.hours.toString()} onChangeText={v=>setTimeForm(p=>({...p,hours:v}))} placeholder="e.g. 4.5" placeholderTextColor="#9CA3AF" keyboardType="numeric"/>
            <Text style={xstyles.formLabel}>Description</Text>
            <TextInput style={[xstyles.formInput,{height:72,textAlignVertical:'top'}]} value={timeForm.description} onChangeText={v=>setTimeForm(p=>({...p,description:v}))} placeholder="What was worked on?" placeholderTextColor="#9CA3AF" multiline/>
            <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
              <Text style={xstyles.formLabel}>Billable</Text>
              <Switch value={timeForm.billable} onValueChange={v=>setTimeForm(p=>({...p,billable:v}))} trackColor={{false:'#D1D5DB',true:'#14B8A6'}} thumbColor="#FFF"/>
            </View>
            <View style={{height:40}}/>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  const renderNotesModal = () => (
    <Modal visible={showNotesModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{flex:1,backgroundColor:'#FFF'}}>
        <LinearGradient colors={['#F59E0B','#D97706']} style={{flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14}}>
          <TouchableOpacity onPress={()=>setShowNotesModal(false)} style={{marginRight:12}}><MaterialCommunityIcons name="close" size={24} color="#FFF"/></TouchableOpacity>
          <Text style={{color:'#FFF',fontSize:18,fontWeight:'700',flex:1}}>Activity Notes</Text>
          <TouchableOpacity onPress={()=>{setActivityNotes(p=>({...p,[noteActId]:noteText}));setShowNotesModal(false);Alert.alert('Saved','Notes saved');}} style={{backgroundColor:'rgba(255,255,255,0.2)',paddingHorizontal:16,paddingVertical:8,borderRadius:8}}><Text style={{color:'#FFF',fontWeight:'700'}}>Save</Text></TouchableOpacity>
        </LinearGradient>
        <TextInput style={{flex:1,padding:16,fontSize:14,color:'#111827',textAlignVertical:'top'}} value={noteText} onChangeText={setNoteText} placeholder="Write notes about this activity..." placeholderTextColor="#9CA3AF" multiline autoFocus/>
      </SafeAreaView>
    </Modal>
  );

  const renderBaselineModal = () => (
    <Modal visible={showBaselineModal} animationType="slide" transparent>
      <View style={{flex:1,backgroundColor:'rgba(0,0,0,0.5)',justifyContent:'center',alignItems:'center',padding:24}}>
        <View style={{backgroundColor:'#FFF',borderRadius:16,padding:24,width:'100%',maxWidth:400}}>
          <Text style={{fontSize:18,fontWeight:'700',color:'#111827',marginBottom:8}}>Save Baseline</Text>
          <Text style={{color:'#6B7280',fontSize:13,marginBottom:16}}>Snapshot of current schedule for comparison.</Text>
          {baseline&&<Text style={{color:'#F97316',fontSize:12,marginBottom:12}}>Existing baseline "{baseline.name}" will be replaced.</Text>}
          <TextInput style={xstyles.formInput} value={baselineName} onChangeText={setBaselineName} placeholder="Baseline name (e.g. v1.0)" placeholderTextColor="#9CA3AF" autoFocus/>
          <View style={{flexDirection:'row',gap:12,marginTop:16}}>
            <TouchableOpacity onPress={()=>{setShowBaselineModal(false);setBaselineName('');}} style={{flex:1,padding:12,borderRadius:8,borderWidth:1,borderColor:'#D1D5DB',alignItems:'center'}}><Text style={{color:'#374151'}}>Cancel</Text></TouchableOpacity>
            <TouchableOpacity onPress={saveBaseline} style={{flex:1,padding:12,borderRadius:8,backgroundColor:'#6366F1',alignItems:'center'}}><Text style={{color:'#FFF',fontWeight:'700'}}>Save Baseline</Text></TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.container}>
      {renderAnimatedHeader()}
      {renderTabNavigation()}
      <View style={styles.content}>
        {renderToolbar()}
        {renderFilters()}
        <Animated.View style={[styles.viewContainer, { opacity: fadeAnim }]}>
          {activeView === 'gantt' && renderGanttView()}
          {activeView === 'wbs' && renderWBSView()}
          {activeView === 'resources' && renderResourcesView()}
          {activeView === 'dashboard' && renderDashboardView()}
          {activeView === 'analytics' && renderAnalyticsView()}
          {activeView === 'calendar' && renderCalendarView()}
          {activeView === 'reports' && renderReportsView()}
          {activeView === 'risks' && renderRisksView()}
          {activeView === 'issues' && renderIssuesView()}
          {activeView === 'milestones' && renderMilestonesView()}
        </Animated.View>
      </View>

      {/* Modals */}
      {renderAddActivityModal()}
      {renderEditActivityModal()}
      {renderDependencyModal()}
      {renderAddResourceModal()}
      {renderEditResourceModal()}
      {/* New Project Modal */}
      {renderProjectModal(false)}
      {/* Edit Project Modal */}
      {renderProjectModal(true)}
      {renderRiskModal()}
      {renderIssueModal()}
      {renderMilestoneModal()}
      {renderTimeModal()}
      {renderNotesModal()}
      {renderBaselineModal()}

      {/* Settings Modal */}
      <Modal visible={showSettingsModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={styles.modalContainer}>
          <LinearGradient colors={['#374151','#1F2937']} style={styles.modalHeader}>
            <TouchableOpacity onPress={()=>setShowSettingsModal(false)} style={styles.modalCloseButton}><MaterialCommunityIcons name="close" size={24} color="#FFF"/></TouchableOpacity>
            <Text style={styles.modalTitle}>Settings</Text>
            <View style={{width:60}}/>
          </LinearGradient>
          <ScrollView style={styles.modalContent}>
            <View style={styles.formSection}>
              <Text style={styles.sectionTitle}>Application</Text>
              <View style={[styles.switchContainer,{marginBottom:14}]}>
                <View style={styles.switchInfo}><Text style={styles.switchLabel}>Auto Save</Text><Text style={styles.switchDescription}>Save data automatically</Text></View>
                <Switch value={!!appSettings.autoSave} onValueChange={v=>setAppSettings(p=>({...p,autoSave:v}))} trackColor={{false:'#D1D5DB',true:'#3B82F6'}} thumbColor="#FFF" ios_backgroundColor="#D1D5DB"/>
              </View>
              <View style={[styles.switchContainer,{marginBottom:14}]}>
                <View style={styles.switchInfo}><Text style={styles.switchLabel}>Notifications</Text><Text style={styles.switchDescription}>Show task alerts</Text></View>
                <Switch value={!!appSettings.notifications} onValueChange={v=>setAppSettings(p=>({...p,notifications:v}))} trackColor={{false:'#D1D5DB',true:'#3B82F6'}} thumbColor="#FFF" ios_backgroundColor="#D1D5DB"/>
              </View>
            </View>
            <View style={styles.formSection}>
              <Text style={styles.sectionTitle}>About</Text>
              <Text style={{fontSize:13,color:'#374151',lineHeight:20}}>{"Project Planning Screen v3.0\nBuilt with React Native + Expo\n\nFeatures: Dashboard, Gantt, WBS, Resources, Analytics, Calendar, Reports, Risks, Issues, Milestones, Time Tracking, EVM, Baseline, Undo/Redo, Export PDF/CSV/JSON"}</Text>
            </View>
            <View style={{height:40}}/>
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Notifications Panel */}
      <Modal visible={!!showNotificationsPanel} animationType="fade" transparent>
        <TouchableOpacity style={{flex:1,backgroundColor:'rgba(0,0,0,0.4)'}} onPress={()=>setShowNotificationsPanel(false)} activeOpacity={1}>
          <View style={{position:'absolute',top:Platform.OS==='ios'?100:60,right:12,width:Math.min(screenWidth-24,340),backgroundColor:'#FFF',borderRadius:12,elevation:12,shadowColor:'#000',shadowOffset:{width:0,height:8},shadowOpacity:0.2,shadowRadius:16,maxHeight:400}}>
            <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',padding:14,borderBottomWidth:1,borderBottomColor:'#F3F4F6'}}>
              <Text style={{fontSize:16,fontWeight:'bold',color:'#111827'}}>Notifications</Text>
              <TouchableOpacity onPress={()=>setShowNotificationsPanel(false)}><MaterialCommunityIcons name="close" size={20} color="#6B7280"/></TouchableOpacity>
            </View>
            <ScrollView style={{maxHeight:320}}>
              {(()=>{
                const today=new Date();
                const notes=[];
                flatActivities.forEach(a=>{if(a.status!=='Completed'&&isBefore(new Date(a.endDate),today))notes.push({id:'od-'+a.id,type:'warning',icon:'clock-alert',msg:`"${a.name}" is overdue`});});
                issues.forEach(i=>{if(i.status==='Open'&&i.priority==='Critical')notes.push({id:'iss-'+i.id,type:'error',icon:'bug',msg:`Critical: ${i.title}`});});
                risks.forEach(r=>{if(r.status==='Open'&&r.likelihood*r.impact>=15)notes.push({id:'rsk-'+r.id,type:'error',icon:'shield-alert',msg:`High risk: ${r.title}`});});
                projectMilestones.forEach(m=>{const d=differenceInDays(new Date(m.date),today);if(d>=0&&d<=7&&m.status!=='Completed')notes.push({id:'ms-'+m.id,type:'info',icon:'flag',msg:`Milestone "${m.name}" in ${d}d`});});
                if(notes.length===0)return <View style={{padding:20,alignItems:'center'}}><MaterialCommunityIcons name="bell-check-outline" size={36} color="#D1D5DB"/><Text style={{fontSize:14,color:'#9CA3AF',marginTop:8}}>All clear!</Text></View>;
                return notes.map(n=>(
                  <View key={n.id} style={{flexDirection:'row',alignItems:'flex-start',padding:12,borderBottomWidth:1,borderBottomColor:'#F9FAFB',borderLeftWidth:3,borderLeftColor:n.type==='error'?'#EF4444':n.type==='warning'?'#F59E0B':'#3B82F6'}}>
                    <MaterialCommunityIcons name={n.icon} size={16} color={n.type==='error'?'#EF4444':n.type==='warning'?'#F59E0B':'#3B82F6'} style={{marginRight:8,marginTop:1}}/>
                    <Text style={{flex:1,fontSize:13,color:'#374151'}}>{n.msg}</Text>
                  </View>
                ));
              })()}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {selectedActivity && (
        <Animated.View style={[styles.activityDetails, { opacity: fadeAnim }]}>
          <BlurView intensity={80} style={styles.activityDetailsBlur}>
            <View style={styles.activityDetailsContent}>
              <View style={styles.activityDetailsHeader}>
                <Text style={styles.activityDetailsTitle}>{selectedActivity.name}</Text>
                <TouchableOpacity onPress={() => setSelectedActivity(null)} style={styles.activityDetailsClose} activeOpacity={0.8} accessibilityLabel="Close details">
                  <MaterialCommunityIcons name="close" size={20} color="#6B7280" />
                </TouchableOpacity>
              </View>
              <View style={styles.activityDetailsBody}>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>WBS:</Text>
                  <Text style={styles.detailValue}>{selectedActivity.wbs}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Duration:</Text>
                  <Text style={styles.detailValue}>{selectedActivity.duration} days</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Progress:</Text>
                  <Text style={styles.detailValue}>{selectedActivity.progress}%</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Status:</Text>
                  <View style={[styles.statusBadge, { backgroundColor: getStatusColor(selectedActivity.status) }]}>
                    <Text style={styles.statusText}>{selectedActivity.status}</Text>
                  </View>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Cost:</Text>
                  <Text style={styles.detailValue}>{formatCurrency(selectedActivity.cost)}</Text>
                </View>
                {selectedActivity.predecessors?.length > 0 && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Predecessors:</Text>
                    <Text style={styles.detailValue}>
                      {selectedActivity.predecessors.map(p => p.id).join(', ')}
                    </Text>
                  </View>
                )}
                {selectedActivity.description && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Description:</Text>
                    <Text style={styles.detailDescription}>{selectedActivity.description}</Text>
                    
                  </View>
                )}
              </View>
            </View>
          </BlurView>
        </Animated.View>
      )}
    </SafeAreaView>
  );
};

// ── Extended styles for new views ──
const xstyles = StyleSheet.create({
  card:{backgroundColor:'#FFF',borderRadius:12,padding:14,marginBottom:10,shadowColor:'#000',shadowOffset:{width:0,height:1},shadowOpacity:0.08,shadowRadius:4,elevation:2},
  cardTitle:{fontSize:14,fontWeight:'700',color:'#111827',marginBottom:8},
  barBg:{height:6,backgroundColor:'#F3F4F6',borderRadius:3,overflow:'hidden'},
  barFill:{height:6,borderRadius:3},
  barLabel:{fontSize:11,color:'#6B7280',marginTop:4},
  kpiCard:{backgroundColor:'#FFF',borderRadius:12,padding:12,marginRight:8,minWidth:100,borderTopWidth:3,shadowColor:'#000',shadowOffset:{width:0,height:1},shadowOpacity:0.08,shadowRadius:4,elevation:2,alignItems:'center'},
  kpiVal:{fontSize:16,fontWeight:'700',marginTop:4},
  kpiLabel:{fontSize:11,color:'#6B7280',marginTop:2,textAlign:'center'},
  healthBanner:{flexDirection:'row',alignItems:'center',padding:14,borderRadius:12,borderWidth:2,marginBottom:10},
  healthBadge:{width:48,height:48,borderRadius:24,alignItems:'center',justifyContent:'center'},
  healthBadgeNum:{color:'#FFF',fontWeight:'700',fontSize:18},
  healthLabel:{fontWeight:'700',fontSize:15},
  healthSub:{color:'#6B7280',fontSize:12,marginTop:2},
  evmGrid:{flexDirection:'row',flexWrap:'wrap',gap:8},
  evmCell:{width:'47%',backgroundColor:'#F8FAFF',borderRadius:8,padding:10},
  evmVal:{fontWeight:'700',fontSize:15},
  evmLabel:{fontSize:11,color:'#6B7280',marginTop:2},
  distRow:{flexGrow:0,flexShrink:0,flexDirection:'row',alignItems:'center',marginBottom:8},
  distDot:{width:10,height:10,borderRadius:5,marginRight:8},
  distLabel:{fontSize:12,color:'#374151',width:90},
  distCount:{fontSize:12,fontWeight:'700',color:'#111827',minWidth:24,textAlign:'right'},
  listRow:{flexDirection:'row',alignItems:'center',paddingVertical:4},
  listTitle:{fontSize:13,fontWeight:'600',color:'#111827',flex:1},
  listSub:{fontSize:11,color:'#6B7280'},
  dot:{width:8,height:8,borderRadius:4},
  riskBadge:{width:24,height:24,borderRadius:12,alignItems:'center',justifyContent:'center'},
  riskBadgeText:{color:'#FFF',fontSize:9,fontWeight:'700'},
  emptyMsg:{color:'#9CA3AF',fontSize:12,fontStyle:'italic',textAlign:'center',paddingVertical:8},
  emptyState:{alignItems:'center',paddingVertical:48},
  emptyTitle:{fontSize:16,fontWeight:'600',color:'#6B7280',marginTop:12},
  subTabRow:{backgroundColor:'#F8FAFC',borderBottomWidth:1,borderBottomColor:'#E5E7EB'},
  subTab:{paddingHorizontal:14,paddingVertical:8,borderRadius:8,marginRight:4,backgroundColor:'#F3F4F6'},
  subTabActive:{backgroundColor:'#3B82F6'},
  subTabText:{fontSize:13,fontWeight:'600',color:'#374151'},
  statusChip:{borderRadius:10,paddingHorizontal:8,paddingVertical:3},
  statusChipText:{fontSize:11,fontWeight:'600',color:'#FFF'},
  actionBtn:{flexDirection:'row',alignItems:'center',paddingHorizontal:10,paddingVertical:6,borderRadius:8,backgroundColor:'#F3F4F6',gap:4},
  actionBtnText:{fontSize:12,fontWeight:'600'},
  calNav:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',paddingHorizontal:16,paddingVertical:10,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},
  calNavBtn:{padding:8,borderRadius:8,backgroundColor:'#F3F4F6'},
  calMonthLabel:{fontSize:16,fontWeight:'700',color:'#111827'},
  calDaysRow:{flexDirection:'row',paddingHorizontal:8,paddingVertical:6,backgroundColor:'#F8FAFC'},
  calDayHdr:{flex:1,textAlign:'center',fontSize:12,fontWeight:'600',color:'#374151'},
  calGrid:{flexDirection:'row',flexWrap:'wrap',paddingHorizontal:8},
  calCell:{width:'14.28%',aspectRatio:1,alignItems:'center',justifyContent:'flex-start',paddingTop:4,paddingHorizontal:2},
  calDayNum:{fontSize:13,fontWeight:'500',color:'#111827'},
  calDot:{width:5,height:5,borderRadius:2.5,margin:1},
  calDetail:{maxHeight:200,padding:12,borderTopWidth:1,borderTopColor:'#E5E7EB'},
  calDetailTitle:{fontSize:14,fontWeight:'700',color:'#111827',marginBottom:8},
  exportBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#3B82F6',margin:12,padding:12,borderRadius:10,gap:8},
  exportBtnText:{color:'#FFF',fontWeight:'700',fontSize:14},
  formLabel:{fontSize:13,fontWeight:'600',color:'#374151',marginBottom:6,marginTop:10},
  formInput:{backgroundColor:'#F9FAFB',borderWidth:1,borderColor:'#E5E7EB',borderRadius:10,paddingHorizontal:14,paddingVertical:12,fontSize:14,color:'#111827',marginBottom:4},
  formSelect:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',backgroundColor:'#F9FAFB',borderWidth:1,borderColor:'#E5E7EB',borderRadius:10,paddingHorizontal:14,paddingVertical:12,marginBottom:4},
  formSelectText:{fontSize:14,color:'#111827',flex:1},
});

const styles = StyleSheet.create({
  dependencyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 12,
    marginLeft: 4,
  },
  dependencyText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#6B7280',
    marginLeft: 2,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EBF4FF',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 16,
  },
  addButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#3B82F6',
    marginLeft: 4,
  },
  dependencyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 10,
    borderRadius: 8,
    marginBottom: 8,
  },
  dependencyInfo: {
    flex: 1,
  },
  dependencyName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  dependencyType: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  dependencyItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  dependencyItemSelected: {
    backgroundColor: '#EBF4FF',
  },
  dependencyItemText: {
    fontSize: 14,
    color: '#111827',
  },
  typeButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  typeButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  typeButtonSelected: {
    backgroundColor: '#3B82F6',
    borderColor: '#3B82F6',
  },
  typeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },
  typeButtonTextSelected: {
    color: '#FFFFFF',
  },
  dependencyLine: {
    position: 'absolute',
    height: 2,
    backgroundColor: '#6B7280',
    zIndex: 5,
  },
  dependencyArrow: {
    position: 'absolute',
    top: -3,
    width: 0,
    height: 0,
    backgroundColor: 'transparent',
    borderStyle: 'solid',
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderBottomWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: '#6B7280',
    transform: [{ rotate: '90deg' }],
  },
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  loadingContainer: { flex: 1 },
  loadingGradient: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingContent: { alignItems: 'center', padding: 40 },
  loadingTitle: { fontSize: 28, fontWeight: 'bold', color: '#FFFFFF', marginTop: 20, marginBottom: 8 },
  loadingSubtitle: { fontSize: 16, color: 'rgba(255,255,255,0.8)', marginBottom: 30 },
  loadingProgress: { width: 200, height: 4, backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: 2, overflow: 'hidden' },
  loadingProgressBar: { height: '100%', backgroundColor: '#FFFFFF', borderRadius: 2 },
  animatedHeader: { zIndex: 1000 },
  headerGradient: { paddingTop: Platform.OS === 'ios' ? 0 : StatusBar.currentHeight },
  headerTop: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12 },
  backButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  headerContent: { flex: 1, marginHorizontal: 16 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#FFFFFF', marginBottom: 2 },
  headerSubtitle: { fontSize: 14, color: 'rgba(255,255,255,0.8)' },
  headerAction: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  projectStats: { flexDirection: 'row', paddingHorizontal: 16, paddingBottom: 16, justifyContent: 'space-around' },
  statItem: { alignItems: 'center' },
  statValue: { fontSize: 18, fontWeight: 'bold', color: '#FFFFFF' },
  statLabel: { fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 2 },
  statDivider: { width: 1, backgroundColor: 'rgba(255,255,255,0.3)', marginHorizontal: 8 },
  tabContainer: { backgroundColor: '#FFFFFF', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  tabGradient: { paddingVertical: 8 },
  tabContent: { paddingHorizontal: 16 },
  tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 9, marginRight: 8, borderRadius: 20, minWidth: 90, justifyContent: 'center', position: 'relative', overflow: 'hidden' },
  activeTab: { backgroundColor: '#3B82F6' },
  activeTabGradient: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 20 },
  tabText: { fontSize: 14, fontWeight: '600', color: '#6B7280', marginLeft: 6 },
  activeTabText: { color: '#FFFFFF' },
  toolbar: { backgroundColor: '#FFFFFF', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  toolbarGradient: { paddingVertical: 12 },
  toolbarContent: { paddingHorizontal: 16, alignItems: 'center' },
  toolbarButton: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, marginRight: 8, borderRadius: 8, overflow: 'hidden' },
  primaryButton: { backgroundColor: '#3B82F6' },
  buttonGradient: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8 },
  primaryButtonText: { fontSize: 14, fontWeight: '600', color: '#FFFFFF', marginLeft: 6 },
  secondaryButton: { backgroundColor: '#F3F4F6', borderWidth: 1, borderColor: '#E5E7EB' },
  secondaryButtonText: { fontSize: 14, fontWeight: '600', color: '#374151', marginLeft: 6 },
  refreshButton: { backgroundColor: '#F3F4F6', borderWidth: 1, borderColor: '#E5E7EB', paddingHorizontal: 12, paddingVertical: 8 },
  toolbarSeparator: { width: 1, height: 24, backgroundColor: '#E5E7EB', marginHorizontal: 12 },
  toolbarGroup: { flexDirection: 'row', alignItems: 'center', marginRight: 12 },
  toolbarLabel: { fontSize: 12, fontWeight: '600', color: '#6B7280', marginRight: 8 },
  scaleButton: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, marginRight: 4, backgroundColor: '#F3F4F6' },
  scaleButtonActive: { backgroundColor: '#3B82F6' },
  scaleButtonText: { fontSize: 12, fontWeight: '600', color: '#6B7280' },
  scaleButtonTextActive: { color: '#FFFFFF' },
  filtersContainer: { backgroundColor: '#FFFFFF', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  searchContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, marginBottom: 12 },
  searchInput: { flex: 1, fontSize: 14, color: '#374151', marginLeft: 8 },
  filterButtons: { marginBottom: 12 },
  filterButtonsContent: { paddingRight: 16 },
  filterButton: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 6, marginRight: 8, borderRadius: 16, backgroundColor: '#F3F4F6', borderWidth: 1, borderColor: '#E5E7EB', position: 'relative', overflow: 'hidden' },
  filterButtonActive: { backgroundColor: '#3B82F6', borderColor: '#3B82F6' },
  filterButtonGradient: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 16 },
  filterButtonText: { fontSize: 12, fontWeight: '600', color: '#6B7280' },
  filterButtonTextActive: { color: '#FFFFFF' },
  filterBadge: { marginLeft: 6, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8, minWidth: 20, alignItems: 'center' },
  filterBadgeText: { fontSize: 10, fontWeight: 'bold', color: '#FFFFFF' },
  sortContainer: { flexDirection: 'row', alignItems: 'center' },
  sortLabel: { fontSize: 12, fontWeight: '600', color: '#6B7280', marginRight: 8 },
  sortButton: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 4, backgroundColor: '#F3F4F6', borderRadius: 6, borderWidth: 1, borderColor: '#E5E7EB' },
  sortButtonText: { fontSize: 12, fontWeight: '600', color: '#374151', marginRight: 4 },
  content: { flex: 1 },
  viewContainer: { flex: 1 },
  ganttContainer: { flex: 1, backgroundColor: '#FFFFFF' },
  ganttContent: { flex: 1, flexDirection: 'row' },
  activityListContainer: { width: 300, backgroundColor: '#FFFFFF', borderRightWidth: 1, borderRightColor: '#E5E7EB' },
  activityListHeader: { paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  headerRow: { flexDirection: 'row', paddingHorizontal: 16, justifyContent: 'space-between' },
  headerText: { fontSize: 12, fontWeight: 'bold', color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 },
  activityList: { flex: 1 },
  activityRow: { backgroundColor: '#FFFFFF', borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  selectedActivityRow: { backgroundColor: '#EBF4FF', borderLeftWidth: 3, borderLeftColor: '#3B82F6' },
  activityTouchable: { padding: 12 },
  activityInfo: { marginBottom: 8 },
  activityNameContainer: { marginBottom: 6 },
  wbsContainer: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  activityWBS: { fontSize: 11, fontWeight: 'bold', color: '#6B7280', backgroundColor: '#F3F4F6', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, marginRight: 6 },
  criticalIndicator: { width: 16, height: 16, borderRadius: 8, backgroundColor: '#FEE2E2', justifyContent: 'center', alignItems: 'center' },
  activityName: { fontSize: 14, fontWeight: '600', color: '#111827', lineHeight: 18 },
  tagsContainer: { flexDirection: 'row', marginTop: 4 },
  tag: { backgroundColor: '#EBF4FF', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, marginRight: 4 },
  tagText: { fontSize: 10, fontWeight: '600', color: '#3B82F6' },
  durationContainer: { alignItems: 'center', minWidth: 60 },
  activityDuration: { fontSize: 12, fontWeight: 'bold', color: '#374151' },
  dateRange: { fontSize: 10, color: '#6B7280', marginTop: 2 },
  progressContainer: { alignItems: 'center', minWidth: 80 },
  progressBar: { width: 60, height: 6, backgroundColor: '#E5E7EB', borderRadius: 3, overflow: 'hidden', marginBottom: 4 },
  progressFill: { height: '100%', borderRadius: 3 },
  progressText: { fontSize: 11, fontWeight: '600', color: '#374151' },
  activityMeta: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 },
  statusText: { fontSize: 10, fontWeight: 'bold', color: '#FFFFFF' },
  priorityBadge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8, borderWidth: 1 },
  priorityText: { fontSize: 10, fontWeight: 'bold' },
  resourcesContainer: { flexDirection: 'row', marginLeft: 4 },
  resourceAvatar: { width: 20, height: 20, borderRadius: 10, backgroundColor: '#3B82F6', justifyContent: 'center', alignItems: 'center', marginLeft: -4, borderWidth: 1, borderColor: '#FFFFFF' },
  moreResources: { backgroundColor: '#6B7280' },
  resourceInitials: { fontSize: 8, fontWeight: 'bold', color: '#FFFFFF' },
  ganttChartContainer: { flex: 1 },
  timelineHeader: { height: 60, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  timelineScrollView: { flex: 1 },
  timelineHeaderContent: { flexDirection: 'row', height: '100%' },
  timelinePeriod: { justifyContent: 'center', alignItems: 'center', borderRightWidth: 1, borderRightColor: '#E5E7EB', paddingVertical: 8 },
  timelineText: { fontSize: 12, fontWeight: 'bold', color: '#374151' },
  timelineSubtext: { fontSize: 10, color: '#6B7280', marginTop: 2 },
  ganttBarsContainer: { flex: 1 },
  ganttScrollView: { flex: 1 },
  ganttBarsContent: { position: 'relative', backgroundColor: '#FAFAFA' },
  gridLine: { position: 'absolute', top: 0, bottom: 0, width: 1, backgroundColor: '#E5E7EB' },
  ganttBarRow: { position: 'absolute', height: 100, width: '100%', justifyContent: 'center' },
  ganttBar: { position: 'absolute', height: 32, borderRadius: 6, overflow: 'hidden', elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.2, shadowRadius: 2 },
  ganttBarGradient: { flex: 1, flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, position: 'relative' },
  ganttBarProgress: { position: 'absolute', top: 0, left: 0, bottom: 0, borderRadius: 6 },
  ganttBarContent: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 },
  ganttBarText: { fontSize: 11, fontWeight: '600', color: '#FFFFFF', flex: 1 },
  criticalMarker: { position: 'absolute', top: 2, right: 2, width: 16, height: 16, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.3)', justifyContent: 'center', alignItems: 'center' },
  milestoneMarker: { position: 'absolute', top: 8, width: 24, height: 24, borderRadius: 12, backgroundColor: '#FFFFFF', justifyContent: 'center', alignItems: 'center', elevation: 3, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.3, shadowRadius: 3 },
  todayLine: { position: 'absolute', top: 0, bottom: 0, width: 2, zIndex: 100 },
  todayLineGradient: { flex: 1, width: 2 },
  todayMarker: { position: 'absolute', top: -20, left: -20, backgroundColor: '#EF4444', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
  todayText: { fontSize: 8, fontWeight: 'bold', color: '#FFFFFF' },
  modalContainer: { flex: 1, backgroundColor: '#F8FAFC' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 16, paddingTop: Platform.OS === 'ios' ? 50 : 16 },
  modalCloseButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  modalTitle: { flex: 1, fontSize: 18, fontWeight: 'bold', color: '#FFFFFF', textAlign: 'center' },
  modalSaveButton: { backgroundColor: 'rgba(255,255,255,0.2)', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20 },
  modalSaveText: { fontSize: 14, fontWeight: 'bold', color: '#FFFFFF' },
  modalContent: { flex: 1, padding: 16 },
  formSection: { backgroundColor: '#FFFFFF', borderRadius: 12, padding: 16, marginBottom: 16, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', color: '#111827', marginBottom: 16 },
  formGroup: { marginBottom: 16 },
  formGroupHalf: { flex: 1 },
  formRow: { flexDirection: 'row', gap: 12 },
  formLabel: { fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 },
  formInput: { borderWidth: 1, borderColor: '#D1D5DB', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: '#111827', backgroundColor: '#FFFFFF' },
  formTextArea: { height: 80, textAlignVertical: 'top' },
  dateButton: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#D1D5DB', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, backgroundColor: '#FFFFFF' },
  dateButtonText: { flex: 1, fontSize: 14, color: '#111827', marginLeft: 8 },
  pickerButton: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#D1D5DB', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, backgroundColor: '#FFFFFF' },
  pickerButtonText: { flex: 1, fontSize: 14, color: '#111827' },
  statusIndicator: { width: 12, height: 12, borderRadius: 6, marginRight: 8 },
  switchContainer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  switchInfo: { flex: 1 },
  switchLabel: { fontSize: 14, fontWeight: '600', color: '#374151' },
  switchDescription: { fontSize: 12, color: '#6B7280', marginTop: 2 },
  activityDetails: { position: 'absolute', bottom: 0, left: 0, right: 0, maxHeight: screenHeight * 0.4 },
  activityDetailsBlur: { flex: 1 },
  activityDetailsContent: { backgroundColor: 'rgba(255,255,255,0.95)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20 },
  activityDetailsHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  activityDetailsTitle: { flex: 1, fontSize: 18, fontWeight: 'bold', color: '#111827' },
  activityDetailsClose: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#F3F4F6', justifyContent: 'center', alignItems: 'center' },
  activityDetailsBody: { gap: 12 },
  detailRow: { flexDirection: 'row', alignItems: 'center' },
  detailLabel: { fontSize: 14, fontWeight: '600', color: '#6B7280', width: 80 },
  detailValue: { fontSize: 14, color: '#111827', flex: 1 },
  detailDescription: { fontSize: 14, color: '#111827', flex: 1, lineHeight: 20 },
  placeholderView: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 },
  placeholderText: { fontSize: 18, fontWeight: 'bold', color: '#6B7280', marginTop: 16, marginBottom: 8 },
  placeholderSubtext: { fontSize: 14, color: '#9CA3AF', textAlign: 'center' },
  wbsContainer: { flex: 1, backgroundColor: '#FFFFFF', padding: 12 },
  wbsItem: { paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  wbsItemContent: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  wbsLeft: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  wbsChevron: { marginRight: 4 },
  wbsCode: { fontSize: 12, fontWeight: 'bold', color: '#6B7280', backgroundColor: '#F3F4F6', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, marginRight: 8 },
  wbsName: { fontSize: 14, color: '#111827', flex: 1 },
  wbsRight: { marginLeft: 8 },
  resourcesList: { padding: 12 },
  resourceCard: { flexDirection: 'row', backgroundColor: '#FFFFFF', borderRadius: 12, padding: 12, marginBottom: 8, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3 },
  resourceAvatarLarge: { width: 60, height: 60, borderRadius: 30, marginRight: 12 },
  resourceInfo: { flex: 1 },
  resourceName: { fontSize: 16, fontWeight: '600', color: '#111827' },
  resourceRole: { fontSize: 14, color: '#6B7280', marginTop: 2 },
  resourceDetails: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 },
  resourceRate: { fontSize: 14, fontWeight: '600', color: '#10B981' },
  resourceUtilization: { fontSize: 14, color: '#6B7280' },
  skillsContainer: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 6 },
  skillTag: { backgroundColor: '#EBF4FF', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2, marginRight: 6, marginBottom: 4 },
  skillText: { fontSize: 10, color: '#3B82F6', fontWeight: '500' },
});

export default ProjectPlanningScreen;